/* ════════════════════════════════════════════════════════════
   AMSX-Analytix — FilterPanel cluster
   Extracted from index.jsx — Tier 2 split

   Default export:  FilterPanel
   Named exports:   MultiSelect, FilterSection, FilterFeatRow
   ──────────────────────────────────────────────────────────── */

import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import {
  ALL_MEASURES, FEAT_LBL, EMPTY_SEL,
  SECTOR_TYPES, DIMENSION_KEYS
} from "../constants";
import { uniq } from "../utils/period";
import { filterRows } from "../utils/filterRows";
import { api } from "../utils/api";

/* Max features (pivot levels) selectable in one query — mirrors MAX_PIVOT_DIMS in app.jsx */
var MAX_FEATURES = 8;

/* ═══ Sales Tracker "Soft Shell" theme (teal) — SALES TRACKER STUDY ONLY ═══
   __PT is null for every other study and each themed style falls back to the
   exact pre-existing literal, so all other studies stay pixel-identical.
   FilterPanel assigns __PT at the very top of its render; MultiSelect /
   FilterSection / FilterFeatRow render as its children afterwards, so they
   can read the module-level value safely (renders are synchronous). */
var PT_PULSE = {
  shellBg: "#0A2F42",                    /* sidebar / shell base */
  shellBr: "rgba(255,255,255,.14)",      /* shell hairline borders */
  card: "#123D55",                       /* raised card on the shell */
  cardBr: "rgba(255,255,255,.16)",
  selBg: "#1A4B66",                      /* selected control surface */
  head: "#F0F6FA",                       /* primary shell text */
  sub: "rgba(240,246,250,.55)",          /* secondary shell text */
  mut: "#C7D9E8",                        /* muted shell text / off states */
  label: "#8FC9E8",                      /* accent section labels */
  acc: "#8FC9E8",                        /* pale-aqua accent */
  accTx: "#0A2F42",                      /* dark ink on aqua */
  accDim: "rgba(143,201,232,.5)",
  accBgOn: "rgba(143,201,232,.12)",      /* selected-row tint */
  accBgOpen: "rgba(143,201,232,.07)",
  icOff: "#8FA9BF",                      /* inactive icons */
  chkOff: "rgba(255,255,255,.35)",       /* unchecked box border */
  reset: "#FCA5A5",                      /* destructive actions on dark */
  runOffBg: "#235877",
  shadow: "0 3px 10px rgba(0,0,0,.3)"
};
var __PT = null;

/* ── Runtime registration of the Sales Tracker "Flow" dimension ──
   constants.js still lists the pre-Flow DIMENSION_KEYS / FEAT_LBL. ES-module singletons
   are shared, so registering here patches every importer (app.jsx, Dashboard_index)
   without touching constants.js. Move into constants.js when it is next edited. */
try {
  if (DIMENSION_KEYS.indexOf("flow") < 0) DIMENSION_KEYS.push("flow");
  if (!FEAT_LBL.flow) FEAT_LBL.flow = "Flow";
  if (DIMENSION_KEYS.indexOf("pharmacyClass") < 0) DIMENSION_KEYS.push("pharmacyClass");
  if (!FEAT_LBL.pharmacyClass) FEAT_LBL.pharmacyClass = "Pharmacy Class";
  /* Medical & Pharma Intelligence (jordan_msa): Pharma/Rx discriminator of the merged view */
  if (DIMENSION_KEYS.indexOf("source") < 0) DIMENSION_KEYS.push("source");
  if (!FEAT_LBL.source) FEAT_LBL.source = "Dataset";
  /* msa combined region concepts */
  if (DIMENSION_KEYS.indexOf("docSalesRegion") < 0) DIMENSION_KEYS.push("docSalesRegion");
  if (!FEAT_LBL.docSalesRegion) FEAT_LBL.docSalesRegion = "Doctor\u2013Sales Region";
  if (DIMENSION_KEYS.indexOf("phSalesRegion") < 0) DIMENSION_KEYS.push("phSalesRegion");
  if (!FEAT_LBL.phSalesRegion) FEAT_LBL.phSalesRegion = "Pharmacy\u2013Sales Region";
  /* # Pharmacies + Gross Selling measure keys — ALL_MEASURES drives the measure
     checkbox permission lists (admin grants UI included). */
  if (ALL_MEASURES.indexOf("Pharmacies") < 0) ALL_MEASURES.push("Pharmacies");
  if (ALL_MEASURES.indexOf("Gross") < 0) ALL_MEASURES.push("Gross");
  if (ALL_MEASURES.indexOf("Stock") < 0) ALL_MEASURES.push("Stock");
  /* r121: Rx per Doctor (medical) — prescriptions ÷ prescribing doctors of the speciality */
  if (ALL_MEASURES.indexOf("RxPerDoctor") < 0) ALL_MEASURES.push("RxPerDoctor");
  if (ALL_MEASURES.indexOf("Doctors") < 0) ALL_MEASURES.push("Doctors");               /* r122 */
  if (ALL_MEASURES.indexOf("DoctorsTotal") < 0) ALL_MEASURES.push("DoctorsTotal");
  /* GPD (Jordan tender market): tender-detail dimensions + the IU measure
     families (UNITS IU / UNIT PRICE IU read the *_ui columns of GpdData). */
  [["institution", "Institution"], ["tender", "Tender Name"], ["tenderNo", "Tender No"],
   ["bidder", "Bidder"], ["registerStatus", "Register Status"], ["reasonForAward", "Reason for Award"],
   ["shelfLife", "Shelf Life"], ["tenderState", "Tender Status"], ["orderNo", "Order No"]
  ].forEach(function (gd) {
    if (DIMENSION_KEYS.indexOf(gd[0]) < 0) DIMENSION_KEYS.push(gd[0]);
    if (!FEAT_LBL[gd[0]]) FEAT_LBL[gd[0]] = gd[1];
  });
  if (ALL_MEASURES.indexOf("UnitIU") < 0) ALL_MEASURES.push("UnitIU");
  if (ALL_MEASURES.indexOf("PriceIU") < 0) ALL_MEASURES.push("PriceIU");
  /* Molecule report advanced setup toggle (admin-grantable feature key) */
  if (!FEAT_LBL.molAdvanced) FEAT_LBL.molAdvanced = "Molecule Report \u2014 Manufacturer & Class selectors";
  /* r88: full-report permission (otherwise reports open in SAMPLE mode) */
  if (!FEAT_LBL.fullReports) FEAT_LBL.fullReports = "FULL reports \u2014 generate & export the complete report decks (otherwise SAMPLE only)";
} catch (eFlowReg) { /* frozen constants — labels fall back to the raw key */ }

/* ════════════════════════════════════════════════════════════
   TOP-N VALUE INPUTS — uncontrolled-style number inputs

   The plain `<input type=number value={tn.n} onChange={parseInt||10}>`
   has a usability bug: while the user is mid-edit, an empty string
   reverts to 10, the field loses focus / cursor jumps. These helpers
   keep their own draft string and only commit a clamped integer
   value on blur, Enter, or a debounce timeout — so typing is smooth.
════════════════════════════════════════════════════════════ */
export function TopNValueInput({ value, onCommit }) {
  const [draft, setDraft] = useState(String(value || 10));
  const commitRef = useRef(null);
  /* If the parent changes value externally (e.g. saved query loaded),
     sync the draft. We only do this when the input is NOT focused so
     we don't yank values out from under a typing user. */
  useEffect(function() {
    if (document.activeElement !== commitRef.current) {
      setDraft(String(value || 10));
    }
  }, [value]);
  var commit = function() {
    var n = parseInt(draft, 10);
    if (isNaN(n) || n < 1) n = 1;
    if (n > 1000) n = 1000;
    if (n !== value) onCommit(n);
    setDraft(String(n));
  };
  return (
    <input type="text" inputMode="numeric" pattern="[0-9]*" ref={commitRef}
      value={draft}
      onChange={function(e){ setDraft(e.target.value.replace(/[^0-9]/g, "")); }}
      onBlur={commit}
      onKeyDown={function(e){ if(e.key === "Enter"){ commit(); e.currentTarget.blur(); } }}
      style={{width:56,padding:"4px 7px",border:"1.5px solid #e2e8f0",borderRadius:5,fontSize:11,
              fontFamily:"inherit",outline:"none",textAlign:"center",color:"#0f172a",background:"#fff"}} />
  );
}

/* Per-dimension N override in selective mode. Empty = inherit the default. */
export function TopNDimInput({ value, placeholder, onCommit }) {
  const [draft, setDraft] = useState(value === "" || value == null ? "" : String(value));
  const ref = useRef(null);
  useEffect(function() {
    if (document.activeElement !== ref.current) {
      setDraft(value === "" || value == null ? "" : String(value));
    }
  }, [value]);
  var commit = function() {
    if (!draft.trim()) { onCommit(null); return; } /* empty → fall back to default */
    var n = parseInt(draft, 10);
    if (isNaN(n) || n < 1) n = 1;
    if (n > 1000) n = 1000;
    onCommit(n);
    setDraft(String(n));
  };
  return (
    <input type="text" inputMode="numeric" pattern="[0-9]*" ref={ref}
      value={draft}
      placeholder={placeholder}
      onChange={function(e){ setDraft(e.target.value.replace(/[^0-9]/g, "")); }}
      onBlur={commit}
      onKeyDown={function(e){ if(e.key === "Enter"){ commit(); e.currentTarget.blur(); } }}
      onClick={function(e){ e.stopPropagation(); /* prevent row-click toggle */ }}
      style={{width:38,padding:"2px 5px",border:"1.5px solid #f9a8d4",borderRadius:4,fontSize:10,
              fontFamily:"inherit",outline:"none",textAlign:"center",color:"#be185d",background:"#fff",
              fontWeight:700}} />
  );
}

/* ════════════════════════════════════════════════════════════
   MULTI-SELECT DROPDOWN
════════════════════════════════════════════════════════════ */
export function MultiSelect({ label, options, value, onChange, accent = "#2563eb", bg = "#eff6ff", onSearch, valueMap, compact, labelMap, keepOrder }) {
  const [open, setOpen] = useState(false);
  const [q, setQ]       = useState("");
  const [serverOpts, setServerOpts] = useState(null);
  const [searching, setSearching] = useState(false);
  const ref = useRef(null);
  const triggerRef = useRef(null);   /* refers to the closed-state trigger box for positioning */
  const timerRef = useRef(null);
  /* Dropdown is rendered as position:fixed anchored to the trigger's viewport
     position. This lets it ESCAPE the filter panel's overflow:auto container
     completely, so users no longer have to scroll inside the panel to see the
     list. The position is computed on open and recomputed on scroll/resize. */
  const [dropPos, setDropPos] = useState({top:0, left:0, openUp:false});

  /* ── User-resizable dropdown width ──
     Default is 420px (Noura 2026-09-16 — wider menu) so long molecule/
     manufacturer names and their value bars show fully without clipping.
     Stored in localStorage per label so each dropdown remembers its preferred
     width across sessions. The resize handle in the lower-right corner lets the
     user drag wider (capped 700px to prevent screen-spanning, floored 220px so
     the search input stays usable). Widths saved before this change (old 320px
     default era) are respected only if the user actually dragged; the untouched
     default upgrades automatically. */
  var WIDTH_KEY = "msw:" + (label || "default");
  var _initWidth = function() {
    try {
      var saved = parseInt(window.localStorage.getItem(WIDTH_KEY), 10);
      if (saved && saved >= 220 && saved <= 700) return saved;
    } catch(e) {}
    return 420;
  };
  const [dropWidth, setDropWidth] = useState(_initWidth);
  /* Drag state for the resize handle */
  var dragStartRef = useRef({x:0, w:0, dragging:false});
  var _onResizeStart = function(e) {
    e.preventDefault();
    e.stopPropagation();
    dragStartRef.current = {x: e.clientX, w: dropWidth, dragging: true, latest: dropWidth};
    var onMove = function(ev){
      if (!dragStartRef.current.dragging) return;
      var delta = ev.clientX - dragStartRef.current.x;
      var next = Math.max(220, Math.min(700, dragStartRef.current.w + delta));
      dragStartRef.current.latest = next; /* persist latest for the onUp closure */
      setDropWidth(next);
    };
    var onUp = function(){
      dragStartRef.current.dragging = false;
      try { window.localStorage.setItem(WIDTH_KEY, String(dragStartRef.current.latest)); } catch(_e){}
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  /* Compact value formatter */
  var _vFmt = function(v){ if(!v) return ""; return v>=1e9?(v/1e9).toFixed(1)+"B":v>=1e6?(v/1e6).toFixed(1)+"M":v>=1e3?(v/1e3).toFixed(1)+"K":String(Math.round(v)); };

  /* Compute max value for proportional bars */
  var _maxVal = useMemo(function(){
    if (!valueMap) return 0;
    var mx = 0;
    for (var k in valueMap) { if (valueMap[k] > mx) mx = valueMap[k]; }
    return mx;
  }, [valueMap]);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  /* Compute dropdown position from the trigger's viewport rect. If there isn't
     room below (less than 240px until viewport bottom), open upward instead so
     the list isn't clipped. The 240 figure roughly matches the dropdown's own
     height (search box + maxHeight 180 list + a bit of chrome). */
  var _recomputePos = function() {
    var el = triggerRef.current;
    if (!el) return;
    var r = el.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var spaceBelow = vh - r.bottom;
    var spaceAbove = r.top;
    var openUp = spaceBelow < 240 && spaceAbove > spaceBelow;
    setDropPos({
      top: openUp ? Math.max(8, r.top - 4) : r.bottom + 2,
      left: r.left,
      openUp: openUp,
    });
  };

  /* Recompute when the dropdown opens. Also recompute on any scroll or resize
     while open, so the dropdown follows the trigger if the page or filter panel
     scrolls — without this, the dropdown would stay anchored to the wrong spot
     after the user scrolls. capture:true on scroll catches scroll on any
     ancestor, since the filter panel itself scrolls and so do other containers. */
  useEffect(function() {
    if (!open) return;
    _recomputePos();
    var onMove = function(){ _recomputePos(); };
    window.addEventListener("scroll", onMove, true); /* capture:true → catches inner scrollers */
    window.addEventListener("resize", onMove);
    return function() {
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open]);

  /* Debounced server search */
  var handleSearch = function(text) {
    setQ(text);
    if (!onSearch) return; /* no server search — use local filter */
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!text.trim()) { setServerOpts(null); return; }
    timerRef.current = setTimeout(function() {
      setSearching(true);
      onSearch(text).then(function(results) {
        setServerOpts(results || []);
        setSearching(false);
      }).catch(function() { setSearching(false); });
    }, 300);
  };

  /* ── Option list assembly ──────────────────────────────────────────────────
     Three sources, none complete on its own:
       • `options`    — the cascaded dimension list, capped server-side (TOP 1000 for
                        large dims like Molecule / SKU / Product), so it CAN miss values.
       • `serverOpts` — live-search hits: cover the whole column but only the first
                        `limit` matches come back.
       • `value`      — already-selected values, which must always stay visible+checked.
     The local text match over `options` now runs in EVERY case: it gives instant
     feedback while the 300ms server debounce is pending (the list used to show every
     option for that moment, reading as "my search did nothing") and it keeps locally
     known matches that fall outside the server's limit. Server hits union on top. */
  var _lblOf = function(o){ return (labelMap && labelMap[o] != null) ? String(labelMap[o]) : String(o); };
  var _ql = q.trim().toLowerCase();
  var filtered = (_ql ? options.filter(function(o){ return _lblOf(o).toLowerCase().indexOf(_ql) >= 0; }) : options).slice();
  if (onSearch && serverOpts && _ql) {
    serverOpts.forEach(function(o){ if (filtered.indexOf(o) < 0) filtered.push(o); });
  }
  /* With NO search text, selected values are force-shown even when the (capped or
     server-search) option list doesn't contain them — otherwise a selection could be
     invisible and un-tickable. While a search IS active they are deliberately NOT
     force-shown: the list must mean exactly "what matches what I typed", because that
     list is also what "All (N)" acts on. Force-showing them here made All remove
     non-matching selections too — the very bug this pass is fixing. The trigger box
     keeps showing the true total ("N selected"). */
  if (!_ql) value.forEach(function(v) { if (filtered.indexOf(v) < 0) filtered.push(v); });
  /* Sort: selected items first, then by value descending.
     keepOrder (periods, ordered lists) skips this and shows the natural order. */
  if (!keepOrder) {
    filtered = filtered.slice().sort(function(a, b) {
      var aOn = value.includes(a) ? 1 : 0;
      var bOn = value.includes(b) ? 1 : 0;
      if (aOn !== bOn) return bOn - aOn; /* selected first */
      if (valueMap) return (valueMap[b] || 0) - (valueMap[a] || 0);
      return 0;
    });
  }
  /* Limit display to 50 unless searching (keepOrder lists are bounded — show all) */
  var displayList = q.trim() ? filtered : (keepOrder ? filtered : filtered.slice(0, 50));
  var hasMore = !q.trim() && !keepOrder && filtered.length > 50;
  var allOn    = filtered.length > 0 && filtered.every(o => value.includes(o));
  var _tOn = value.length > 0;
  var _tBr = __PT ? (_tOn ? __PT.acc : __PT.cardBr) : (_tOn ? accent : "#e2e8f0");
  var _tBg = __PT ? (_tOn ? __PT.selBg : __PT.card) : (_tOn ? bg : "#f8fafc");
  var _tTx = __PT ? (_tOn ? __PT.head : __PT.mut) : (_tOn ? accent : "#64748b");
  var display  = value.length === 0 ? `${label}\u2026` : `${value.length} selected`;

  if (compact) {
    /* Soft-Shell inline pill trigger — "All ▾" / "N selected ▾" on the row's right,
       exactly like the B2 Soft Shell mock. Popup behaviour is unchanged. */
    return (
      <div ref={ref} style={{position:"relative", flexShrink:0}}>
        <div ref={triggerRef}
          onClick={() => { setOpen(p => !p); setQ(""); setServerOpts(null); }}
          style={{display:"inline-flex", alignItems:"center", justifyContent:"center", gap:5, padding:"6px 11px", borderRadius:10,
                  border:"1.5px solid "+(_tOn?"#8FC9E8":"rgba(255,255,255,.25)"),
                  background:_tOn?"#1A4B66":"transparent", color:_tOn?"#F0F6FA":"#C7D9E8",
                  cursor:"pointer", fontSize:10.5, fontWeight:_tOn?800:600, userSelect:"none",
                  whiteSpace:"nowrap", maxWidth:132}}>
          <span style={{overflow:"hidden", textOverflow:"ellipsis"}}>{_tOn ? value.length + " selected" : "All"}</span>
          <span style={{fontSize:7.5, color:_tOn?"#8FC9E8":"rgba(255,255,255,.55)"}}>{"▼"}</span>
        </div>
        {open && (
          <div style={{
            position:"fixed",
            top: dropPos.openUp ? "auto" : dropPos.top,
            bottom: dropPos.openUp ? (window.innerHeight - dropPos.top) : "auto",
            left: dropPos.left,
            zIndex:9999, width: dropWidth,
            background:"#fff", border:"1.5px solid #e2e8f0", borderRadius:7,
            boxShadow:"0 6px 20px rgba(0,0,0,.14)", overflow:"visible",
          }}>
            <div style={{position:"relative"}}>
              <input
                autoFocus value={q} onChange={e => handleSearch(e.target.value)} placeholder={onSearch ? "Type to search server…" : "Search…"}
                style={{width:"100%", padding:"5px 9px", border:"none", borderBottom:"1px solid #f1f5f9",
                        fontSize:11, fontFamily:"inherit", outline:"none", color:"#1e293b", boxSizing:"border-box"}} />
              {searching && <div style={{position:"absolute",right:8,top:5,fontSize:9,color:"#94a3b8"}}>{"⏳"}</div>}
            </div>
            <div style={{maxHeight:180, overflowY:"auto", overflowX:"hidden"}}>
              {value.length > 0 && (
                <div onClick={() => onChange([])}
                  style={{padding:"5px 9px", cursor:"pointer", fontSize:10.5, color:"#dc2626", fontWeight:700, borderBottom:"1px solid #f8fafc", userSelect:"none"}}>
                  {"✕ Clear selection (" + value.length + ")"}
                </div>
              )}
              {displayList.length === 0 ? (
                <div style={{padding:10, fontSize:11, color:"#94a3b8", textAlign:"center", fontStyle:"italic"}}>{searching ? "Searching…" : q ? "No results" : "Type to search"}</div>
              ) : displayList.map(o => {
                const on = value.includes(o);
                return (
                  <div key={o}
                    onClick={() => onChange(on ? value.filter(x => x !== o) : [...value, o])}
                    style={{display:"flex", alignItems:"center", gap:6, padding:"6px 9px", cursor:"pointer", fontSize:11,
                            color: on ? accent : "#334155", background: on ? bg : "#fff",
                            userSelect:"none", fontWeight: on ? 600 : 400, borderBottom:"1px solid #f8fafc"}}>
                    <div style={{width:12, height:12, borderRadius:3, border:`1.5px solid ${on ? accent : "#cbd5e1"}`,
                                 background: on ? accent : "#fff", display:"flex", alignItems:"center", justifyContent:"center",
                                 fontSize:8, color:"#fff", flexShrink:0}}>{on ? "✓" : ""}</div>
                    <span style={{flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", fontSize:11}}>{_lblOf(o)}</span>
                  </div>
                );
              })}
              {hasMore && <div style={{padding:"6px 9px",fontSize:10,color:"#94a3b8",textAlign:"center",fontStyle:"italic",borderTop:"1px solid #f1f5f9"}}>{"Type to search "+(filtered.length-50)+" more…"}</div>}
            </div>
          </div>
        )}
      </div>
    );
  }
  return (
    <div ref={ref} style={{position:"relative", marginBottom:4}}>
      <div
        ref={triggerRef}
        onClick={() => { setOpen(p => !p); setQ(""); setServerOpts(null); }}
        style={{
          display:"flex", alignItems:"center", justifyContent:"space-between",
          padding:"5px 8px", borderRadius:6,
          border:"1.5px solid " + _tBr,
          background: _tBg,
          cursor:"pointer", fontSize:11,
          fontWeight: value.length > 0 ? 600 : 400,
          color: _tTx,
          userSelect:"none",
        }}
      >
        <span style={{flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{display}</span>
        <div style={{display:"flex", alignItems:"center", gap:4, flexShrink:0}}>
          {value.length > 0 && (
            <span
              onClick={e => { e.stopPropagation(); onChange([]); }}
              style={{width:14, height:14, borderRadius:"50%", background:__PT?"rgba(255,255,255,.18)":"#e2e8f0",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      fontSize:9, color:__PT?__PT.head:"#64748b", cursor:"pointer", flexShrink:0}}
            >{"✕"}</span>
          )}
          <span style={{fontSize:8, color:__PT?__PT.acc:"#94a3b8", display:"inline-block",
                        transition:"transform .15s", transform: open ? "rotate(180deg)" : "none"}}>{"▼"}</span>
        </div>
      </div>

      {open && (
        <div style={{
          position:"fixed",
          /* Fixed-position anchored to viewport: dropdown escapes any ancestor
             with overflow:hidden / overflow:auto (notably the filter panel
             scroll container). Coords come from _recomputePos() which reads
             the trigger's bounding rect. */
          top: dropPos.openUp ? "auto" : dropPos.top,
          bottom: dropPos.openUp ? (window.innerHeight - dropPos.top) : "auto",
          left: dropPos.left,
          zIndex:9999,
          width: dropWidth, /* user-resizable, persisted per label */
          background:"#fff", border:"1.5px solid #e2e8f0", borderRadius:7,
          boxShadow:"0 6px 20px rgba(0,0,0,.14)", overflow:"visible",
        }}>
          <div style={{position:"relative"}}>
            <input
              autoFocus value={q} onChange={e => handleSearch(e.target.value)} placeholder={onSearch ? "Type to search server\u2026" : "Search\u2026"}
              style={{width:"100%", padding:"5px 9px", border:"none", borderBottom:"1px solid #f1f5f9",
                      fontSize:11, fontFamily:"inherit", outline:"none", color:"#1e293b",
                      boxSizing:"border-box"}}
            />
            {searching && <div style={{position:"absolute",right:8,top:5,fontSize:9,color:"#94a3b8"}}>{"⏳"}</div>}
          </div>
          <div style={{maxHeight:180, overflowY:"auto", overflowX:"hidden"}}>
            {filtered.length > 1 && (
              <div
                /* "All (N)" acts on the values CURRENTLY IN VIEW only.
                   It used to call onChange(filtered) — a REPLACE — so under an active
                   search it silently dropped every already-selected value that didn't
                   match the search text: select 4, search again, click All, and the
                   first 4 were gone. You could never build up a selection across two
                   searches. Now it ADDS the view to the selection, and when the view is
                   already fully selected it removes ONLY the view. With no search text
                   the view IS the whole list, so behaviour there is unchanged. */
                onClick={() => {
                  if (allOn) {
                    var _rm = {};
                    filtered.forEach(function(o){ _rm[o] = 1; });
                    onChange(value.filter(function(v){ return !_rm[v]; }));
                  } else {
                    var _next = value.slice();
                    filtered.forEach(function(o){ if (_next.indexOf(o) < 0) _next.push(o); });
                    onChange(_next);
                  }
                }}
                style={{
                  display:"flex", alignItems:"center", gap:6, padding:"5px 9px", cursor:"pointer",
                  fontSize:11, borderBottom:"1px solid #f8fafc",
                  color: allOn ? accent : "#64748b",
                  background: allOn ? bg : "#fafafa",
                  fontStyle:"italic", userSelect:"none",
                }}
              >
                <div style={{
                  width:12, height:12, borderRadius:3,
                  border:`1.5px solid ${allOn ? accent : "#cbd5e1"}`,
                  background: allOn ? accent : "#fff",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:8, color:"#fff", flexShrink:0,
                }}>{allOn ? "✓" : ""}</div>
                All ({filtered.length})
              </div>
            )}
            {displayList.length === 0 ? (
              <div style={{padding:10, fontSize:11, color:"#94a3b8", textAlign:"center", fontStyle:"italic"}}>{searching ? "Searching\u2026" : q ? "No results" : "Type to search"}</div>
            ) : displayList.map(o => {
              const on = value.includes(o);
              var barW = (valueMap && _maxVal > 0 && valueMap[o]) ? Math.max(2, (valueMap[o] / _maxVal) * 100) : 0;
              var valStr = (valueMap && valueMap[o]) ? _vFmt(valueMap[o]) : "";
              return (
                <div
                  key={o}
                  onClick={() => onChange(on ? value.filter(x => x !== o) : [...value, o])}
                  style={{
                    display:"flex", alignItems:"center", gap:6, padding:"6px 9px",
                    cursor:"pointer", fontSize:11,
                    color: on ? accent : "#334155",
                    background: on ? bg : "#fff",
                    userSelect:"none", fontWeight: on ? 600 : 400,
                    borderBottom:"1px solid #f8fafc",
                  }}
                >
                  <div style={{
                    width:12, height:12, borderRadius:3,
                    border:`1.5px solid ${on ? accent : "#cbd5e1"}`,
                    background: on ? accent : "#fff",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    fontSize:8, color:"#fff", flexShrink:0,
                  }}>{on ? "✓" : ""}</div>
                  <span style={{flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", fontSize:11}}>{_lblOf(o)}</span>
                  {barW > 0 && <div style={{width:Math.min(barW,60)+"%",height:6,borderRadius:3,background:on?"#93c5fd":"#c7ddf0",flexShrink:0,minWidth:4,maxWidth:80,transition:"width .3s"}} />}
                  {valStr && <span style={{fontSize:9, color:"#64748b", fontWeight:600, flexShrink:0, fontFamily:"monospace",minWidth:40,textAlign:"right"}}>{valStr}</span>}
                </div>
              );
            })}
            {hasMore && <div style={{padding:"6px 9px",fontSize:10,color:"#94a3b8",textAlign:"center",fontStyle:"italic",borderTop:"1px solid #f1f5f9"}}>{"Type to search "+(filtered.length-50)+" more\u2026"}</div>}
          </div>
          {/* Resize handle — drag horizontally to widen the dropdown. Tiny corner
              affordance with diagonal hatch lines (CSS ::after won't work in
              inline styles, so we use unicode "⋮" rotated 90deg via transform).
              On hover the cursor becomes ew-resize so it's discoverable. */}
          <div
            onMouseDown={_onResizeStart}
            title="Drag to resize"
            style={{
              position:"absolute", right:0, bottom:0, width:14, height:14,
              cursor:"ew-resize", display:"flex", alignItems:"center", justifyContent:"center",
              color:"#94a3b8", fontSize:10, fontWeight:700, userSelect:"none",
              background:"linear-gradient(135deg, transparent 50%, #e2e8f0 50%)",
              borderBottomRightRadius:7,
            }}
          >
            <span style={{transform:"rotate(90deg)", display:"inline-block", marginRight:1, marginBottom:1}}>⋮⋮</span>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Section wrapper (defined outside FilterPanel to avoid re-mount on re-render) ── */
/* ── Filter section SVG icons (monochrome, matches dashboard sidebar style) ── */
function _filterIcon(key, sz, cl) {
  sz = sz || 16; cl = cl || "currentColor";
  var SVG = function(kids){ return React.createElement("svg",{width:sz,height:sz,viewBox:"0 0 24 24",fill:"none",stroke:cl,strokeWidth:2,strokeLinecap:"round",strokeLinejoin:"round"}, kids); };
  switch (key) {
    case "Market":        /* Tag icon */
      return SVG([React.createElement("path",{key:1, d:"M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"}), React.createElement("circle",{key:2, cx:7, cy:7, r:1, fill:cl})]);
    case "ATC":           /* DNA / molecule */
      return SVG([React.createElement("path",{key:1, d:"M4 4c4 4 12 12 16 16"}), React.createElement("path",{key:2, d:"M20 4c-4 4-12 12-16 16"}), React.createElement("path",{key:3, d:"M8 4h8M8 20h8"})]);
    case "NFC":           /* Pill */
      return SVG([React.createElement("path",{key:1, d:"M10.5 20.5a4.95 4.95 0 0 1-7-7l9-9a4.95 4.95 0 0 1 7 7l-9 9z"}), React.createElement("path",{key:2, d:"M8.5 8.5l7 7"})]);
    case "Doctor":        /* Stethoscope */
      return SVG([React.createElement("path",{key:1, d:"M4 3v6a4 4 0 0 0 4 4h0a4 4 0 0 0 4-4V3"}), React.createElement("path",{key:2, d:"M8 13v3a4 4 0 0 0 4 4h0a4 4 0 0 0 4-4v-1"}), React.createElement("circle",{key:3, cx:18, cy:11, r:2})]);
    case "Pharmacy":      /* Capsule */
      return SVG([React.createElement("path",{key:1, d:"M10.5 20.5a4.95 4.95 0 0 1-7-7l9-9a4.95 4.95 0 0 1 7 7l-9 9z"}), React.createElement("path",{key:2, d:"M8.5 8.5l7 7"})]);
    case "Patient":       /* Person */
      return SVG([React.createElement("circle",{key:1, cx:12, cy:8, r:4}), React.createElement("path",{key:2, d:"M4 21v-2a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v2"})]);
    case "Indication":    /* Clipboard */
      return SVG([React.createElement("rect",{key:1, x:6, y:4, width:12, height:18, rx:2}), React.createElement("rect",{key:2, x:9, y:2, width:6, height:4, rx:1}), React.createElement("line",{key:3, x1:9, y1:11, x2:15, y2:11}), React.createElement("line",{key:4, x1:9, y1:15, x2:15, y2:15})]);
    case "MfrOrigin":     /* Globe */
      return SVG([React.createElement("circle",{key:1, cx:12, cy:12, r:9}), React.createElement("path",{key:2, d:"M3 12h18"}), React.createElement("path",{key:3, d:"M12 3a13.5 13.5 0 0 1 0 18 13.5 13.5 0 0 1 0-18z"})]);
    case "Geo":           /* Pin / location */
      return SVG([React.createElement("path",{key:1, d:"M12 22s-7-7.5-7-13a7 7 0 0 1 14 0c0 5.5-7 13-7 13z"}), React.createElement("circle",{key:2, cx:12, cy:9, r:2.5})]);
    case "Period":        /* Calendar */
      return SVG([React.createElement("rect",{key:1, x:3, y:5, width:18, height:16, rx:2}), React.createElement("line",{key:2, x1:3, y1:10, x2:21, y2:10}), React.createElement("line",{key:3, x1:8, y1:3, x2:8, y2:7}), React.createElement("line",{key:4, x1:16, y1:3, x2:16, y2:7})]);
    case "Measures":      /* Bar chart */
      return SVG([React.createElement("rect",{key:1, x:4, y:13, width:3, height:7}), React.createElement("rect",{key:2, x:10, y:8, width:3, height:12}), React.createElement("rect",{key:3, x:16, y:4, width:3, height:16})]);
    case "Statistics":    /* Trend line */
      return SVG([React.createElement("polyline",{key:1, points:"3,17 8,12 13,15 21,5"}), React.createElement("polyline",{key:2, points:"15,5 21,5 21,11"})]);
    case "TopN":          /* Funnel */
      return SVG([React.createElement("path",{key:1, d:"M3 4h18l-7 9v6l-4 2v-8L3 4z"})]);
    case "Saved":         /* Bookmark */
    case "SavedQueries":  /* Bookmark (alias) */
      return SVG([React.createElement("path",{key:1, d:"M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"})]);
    default:              /* Generic dot */
      return SVG([React.createElement("circle",{key:1, cx:12, cy:12, r:3, fill:cl})]);
  }
}

export function FilterSection({ id, color, title, cnt, isOpen, onToggle, children }) {
  var hasSel = (typeof cnt === "number" && cnt > 0);
  /* Style matches DashboardsPanel sidebar:
     - Font size 11.5, mixed-case (not all-caps)
     - Inactive color #3b5a80, active #1d4ed8
     - Monochrome SVG icon left, no emoji prefix
     - Left border accent on hover/active (3px solid #2563eb)
     - Soft blue background tint when section is open or has selections */
  var _fsEdge = hasSel ? (__PT?__PT.acc:"#2563eb") : (isOpen ? (__PT?__PT.accDim:"#93c5fd") : "transparent");
  var _fsBg = hasSel ? (__PT?__PT.accBgOn:"rgba(37,99,235,.08)") : (isOpen ? (__PT?__PT.accBgOpen:"rgba(37,99,235,.05)") : "transparent");
  var _fsIc = (hasSel || isOpen) ? (__PT?__PT.acc:"#2563eb") : (__PT?__PT.icOff:"#6b8db5");
  var _fsTx = (hasSel || isOpen) ? (__PT?__PT.acc:"#1d4ed8") : (__PT?__PT.mut:"#3b5a80");
  if (__PT) {
    /* Soft-Shell flat section — always open, uppercase muted label, count right */
    return (
      <div style={{borderBottom:"1px solid rgba(255,255,255,.10)", padding:"11px 14px 13px"}}>
        <div style={{display:"flex", alignItems:"center", marginBottom:8}}>
          <span style={{fontSize:10.5, fontWeight:800, letterSpacing:".09em", textTransform:"uppercase", color:"#9FBFC5", flex:1}}>{title}</span>
          {hasSel && <span style={{fontSize:10.5, fontWeight:800, color:__PT.acc}}>{cnt}</span>}
        </div>
        {children}
      </div>
    );
  }
  return (
    <div style={{borderBottom:"1px solid "+(__PT?__PT.shellBr:"#e2e8f0")}}>
      <div
        onClick={() => onToggle(id)}
        style={{
          display:"flex", alignItems:"center", gap:10, padding:"10px 14px",
          cursor:"pointer", userSelect:"none",
          borderLeft:"3px solid " + _fsEdge,
          background: _fsBg,
          transition:"all .15s",
        }}
      >
        <span style={{flexShrink:0, width:20, display:"flex", alignItems:"center", justifyContent:"center", color: _fsIc}}>
          {_filterIcon(id, 16, _fsIc)}
        </span>
        <span style={{
          fontSize:11.5,
          fontWeight: hasSel || isOpen ? 700 : 500,
          color: _fsTx,
          flex:1
        }}>{title}</span>
        {hasSel && (
          <span style={{
            fontSize:9, fontWeight:800, color:__PT?__PT.accTx:"#fff",
            background:__PT?__PT.acc:"#2563eb", borderRadius:10, padding:"2px 7px",
            minWidth:18, textAlign:"center",
            boxShadow:__PT?"0 1px 3px rgba(0,0,0,.3)":"0 1px 3px rgba(37,99,235,.3)"
          }}>{cnt}</span>
        )}
        <span style={{
          fontSize:9, color: isOpen ? (__PT?__PT.acc:"#2563eb") : (__PT?__PT.icOff:"#7a9bbf"),
          transition:"transform .2s", display:"inline-block",
          transform: isOpen ? "rotate(90deg)" : "none"
        }}>▶</span>
      </div>
      {isOpen && <div style={{padding:"4px 14px 10px"}}>{children}</div>}
    </div>
  );
}

/* ── Feature row (defined outside FilterPanel to avoid re-mount on re-render) ── */
export function FilterFeatRow({ fkey, label, opts, val, setVal, accent, bg, mp, dimAllowed, showCols, parentDim, childDim, dims, togShow, reorderShow, onSearch, valueMaps }) {
  if (!mp[fkey]) return null;
  if (!dimAllowed(fkey)) return null;
  const chk      = showCols.includes(fkey);
  const dimOrder  = chk ? showCols.indexOf(fkey) + 1 : 0;
  const cnt      = opts ? opts.length : 0;
  var _vm = valueMaps && valueMaps[fkey] ? valueMaps[fkey] : null;
  var _selBlue = __PT ? __PT.acc : "#2563eb";

  /* Drag-to-reorder state — only meaningful when this row is checked-in (chk=true)
     and has companions in showCols. Drop indicator: top half = before, bottom half = after. */
  var [dropZone, setDropZone] = useState(""); /* "" | "top" | "bottom" */
  var draggable = !!(chk && reorderShow && showCols.length >= 2);

  function _onDragStart(e) {
    if (!draggable) return;
    try {
      e.dataTransfer.setData("application/x-amsx-feat", fkey);
      e.dataTransfer.setData("text/plain", fkey); /* fallback */
      e.dataTransfer.effectAllowed = "move";
    } catch (err) { /* some browsers throw on certain MIME types */ }
  }
  function _onDragOver(e) {
    if (!chk || !reorderShow) return;
    var dragKey = "";
    try { dragKey = e.dataTransfer.getData("application/x-amsx-feat") || e.dataTransfer.getData("text/plain") || ""; } catch (err) {}
    /* dataTransfer.getData isn't reliable during dragover in some browsers, so we
       just visually indicate any drop is acceptable (the actual key check happens on drop). */
    e.preventDefault(); /* allow drop */
    e.dataTransfer.dropEffect = "move";
    var rect = e.currentTarget.getBoundingClientRect();
    var midY = rect.top + rect.height / 2;
    setDropZone(e.clientY < midY ? "top" : "bottom");
  }
  function _onDragLeave() { setDropZone(""); }
  function _onDrop(e) {
    if (!chk || !reorderShow) return;
    e.preventDefault();
    var dragKey = "";
    try { dragKey = e.dataTransfer.getData("application/x-amsx-feat") || e.dataTransfer.getData("text/plain") || ""; } catch (err) {}
    var before = dropZone === "top";
    setDropZone("");
    if (dragKey && dragKey !== fkey) reorderShow(dragKey, fkey, before);
  }

  /* Top/bottom drop indicator bar */
  var indicator = dropZone ? (
    <div style={{position:"absolute", left:0, right:0, [dropZone==="top"?"top":"bottom"]:-2, height:3, background:_selBlue, borderRadius:2, pointerEvents:"none"}} />
  ) : null;

  if (__PT) {
    /* Soft-Shell row — [pale-blue checkbox] [label] [inline "All ▾" pill], one line */
    return (
      <div style={{position:"relative"}}
           onDragOver={_onDragOver} onDragLeave={_onDragLeave} onDrop={_onDrop}>
        {indicator}
        <div style={{display:"flex", alignItems:"center", gap:9, padding:"6px 0"}}>
          {chk && reorderShow ? (
            <span draggable="true" onDragStart={_onDragStart}
                  title="Drag to reorder pivot dimensions"
                  style={{cursor:"grab", color:"rgba(255,255,255,.4)", fontSize:11, lineHeight:1, userSelect:"none"}}>{"⋮⋮"}</span>
          ) : (
            <span style={{width:8, display:"inline-block"}} />
          )}
          <div onClick={() => togShow(fkey)} style={{display:"flex", alignItems:"center", gap:9, flex:1, cursor:"pointer", minWidth:0, userSelect:"none"}}>
            <div style={{width:19, height:19, borderRadius:6, flexShrink:0,
                         border:"1.5px solid "+(chk?__PT.acc:"rgba(255,255,255,.3)"),
                         background:chk?__PT.acc:"transparent",
                         display:"flex", alignItems:"center", justifyContent:"center",
                         fontSize:10, fontWeight:800, color:__PT.accTx}}>{chk ? dimOrder : ""}</div>
            <span style={{fontSize:12.5, fontWeight:chk?700:500, color:"#E9F4F5", flex:1,
                          overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{label}</span>
          </div>
          <MultiSelect compact label={"Filter " + label} options={opts} value={val} onChange={setVal} accent={accent} bg={bg} onSearch={onSearch} valueMap={_vm} />
        </div>
      </div>
    );
  }
  return (
    <div style={{marginBottom:8, position:"relative"}}
         onDragOver={_onDragOver} onDragLeave={_onDragLeave} onDrop={_onDrop}>
      {indicator}
      <div
        style={{display:"flex", alignItems:"center", gap:6, padding:"3px 0", userSelect:"none", marginBottom:3}}
      >
        {/* Drag grip — only shown when row is in pivot. Click anywhere else still toggles. */}
        {chk && reorderShow ? (
          <span draggable="true" onDragStart={_onDragStart}
                title="Drag to reorder pivot dimensions"
                style={{cursor:"grab", color:"#94a3b8", fontSize:11, lineHeight:1, padding:"0 1px", userSelect:"none"}}>⋮⋮</span>
        ) : (
          <span style={{width:8, display:"inline-block"}} />
        )}
        <div onClick={() => togShow(fkey)} style={{display:"flex", alignItems:"center", gap:6, flex:1, cursor:"pointer"}}>
          <div style={{
            width:14, height:14, borderRadius:3,
            border:"1.5px solid " + (chk ? _selBlue : (__PT?__PT.chkOff:"#cbd5e1")),
            background: chk ? _selBlue : (__PT?"transparent":"#fff"),
            display:"flex", alignItems:"center", justifyContent:"center",
            fontSize:8, color:__PT?__PT.accTx:"#fff", flexShrink:0,
          }}>{chk ? dimOrder : ""}</div>
          <span style={{fontSize:11, fontWeight:700, color: chk ? _selBlue : (__PT?__PT.mut:"#64748b"), letterSpacing:".04em", flex:1}}>{label}</span>
          {val && val.length > 0
            ? <span style={{fontSize:8, fontWeight:800, color:__PT?__PT.accTx:"#3b82f6", background:__PT?__PT.acc:"#eff6ff", borderRadius:8, padding:"1px 5px", minWidth:16, textAlign:"center"}}>{val.length}</span>
            : cnt > 0 && <span style={{fontSize:8, fontWeight:700, color:__PT?__PT.accTx:"#3b82f6", background:__PT?__PT.acc:"#eff6ff", borderRadius:8, padding:"1px 5px", minWidth:16, textAlign:"center"}}>{cnt}</span>
          }
        </div>
      </div>
      <MultiSelect label={"Filter " + label} options={opts} value={val} onChange={setVal} accent={accent} bg={bg} onSearch={onSearch} valueMap={_vm} />
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   FILTER PANEL  (left sidebar)
════════════════════════════════════════════════════════════ */
export default function FilterPanel({ rows, mp, sel, setSel, dimValues, setDimValues, onRun, loading, errors, pOptsExt, savedQueries, onSaveQuery, onDeleteQuery, onDeleteQueries, onLoadQuery, onShareQuery, editingQueryId, onStartEdit, onCancelEdit, onUpdateLoadedQuery, onRefreshQueries, user, clientPerms, userStudyTypes, activeStudyType, onStudyTypeChange, studyTypeConfig, collapsed, onToggleCollapse }) {
  /* Sales Tracker Soft Shell: AMS navy shell for the Sales Tracker study only.
     Assigned BEFORE any child renders so module-level readers see it. */
  __PT = (activeStudyType === "sales_tracker") ? PT_PULSE : null;
  /* Medical & Pharma Intelligence: the merged market carries THREE region concepts —
     the sales Region/Governorate (pharma dataset), the prescriber's Doctor Region and
     the dispensing Pharmacy Region (Rx dataset). Label them explicitly here so the
     filter panel reads unambiguously. */
  var _isMsaP = activeStudyType === "jordan_msa";
  /* Belt-and-braces: the structural msa dims must never disappear because a
     mapping was built by an older load path (offline fallback, stale cache). */
  if (_isMsaP && mp) {
    if (!mp.source) mp.source = "src";
    if (!mp.docSalesRegion) mp.docSalesRegion = "dsr";
    if (!mp.phSalesRegion) mp.phSalesRegion = "psr";
  }
  const [openSecs, setOpenSecs] = useState({Country:false, Sector:false, Market:false, MfrOrigin:false, ATC:false, Geo:false, Period:false, Measures:false, Statistics:false, TopN:false, SavedQueries:false});
  /* Checked saved queries for bulk delete — map of queryId → true */
  const [selQ, setSelQ] = useState({});
  const [queryName, setQueryName] = useState("");
  const [cascadeLoading, setCascadeLoading] = useState(false);
  /* Name being edited when a saved query is in edit mode. Auto-synced from the
     active query when edit mode starts (via useEffect below). When the user types
     in the row's name input, this state holds the in-flight value until they hit
     Save (which sends it to the server) or Cancel (which discards). */
  const [editName, setEditName] = useState("");
  useEffect(function() {
    if (!editingQueryId) { setEditName(""); return; }
    var sq = (savedQueries || []).find(function(x){ return x.id === editingQueryId; });
    setEditName(sq ? (sq.name || "") : "");
  }, [editingQueryId, savedQueries]);

  /* ── Store a snapshot of the initial (unfiltered) dimValues ── */
  var _initDimsRef = useRef(null);
  useEffect(function() {
    if (dimValues && !_initDimsRef.current) {
      _initDimsRef.current = Object.assign({}, dimValues);
    }
  }, [dimValues]);

  /* ── Global Definition Market: when the applied group changes, the
     "unfiltered" snapshots are STALE — they were captured under the previous
     scope, so clicking or clearing any filter restored the PRE-MARKET full
     lists ("the market resets to default"). On the group-change event
     (fired by the 🌐 header control): drop both snapshots so the freshly
     scoped dimensions (re-fetched by app.jsx on the same event) become the
     new baseline, re-fetch the value bars under the new scope, and re-run
     any active cascade. (The refs below are var-hoisted; the handler only
     runs after the first full render, when they all exist.) ── */
  var _gdmS = useState(0), _gdmVerF = _gdmS[0], _setGdmVerF = _gdmS[1];
  useEffect(function() {
    var h = function() {
      _initDimsRef.current = null;
      _initValMapsRef.current = null;
      if (activeStudyType) {
        _dimValFetched.current = true;
        api.getDimValues(activeStudyType).then(function(result) {
          if (result && typeof result === "object") {
            _initValMapsRef.current = result;
            if (!_hasAnyFilterRef.current) _setServerValMaps(result);
          }
        }).catch(function() {});
      }
      _setGdmVerF(function(v) { return v + 1; });
    };
    window.addEventListener("amsx-gdm-market", h);
    return function() { window.removeEventListener("amsx-gdm-market", h); };
  }, [activeStudyType]);

  /* ── Cascading filter: re-fetch dimensions when ANY selection changes ── */
  /* Build filters object and check if any are active */
  var _cascadeFilters = useMemo(function() {
    /* Collect every filterable dimension key from the sel state dynamically.
       This guarantees that ANY filter (core pharma dims + medical dims + study-type-specific
       features + continent/origin/mfr-type/nfc/etc.) cascades to ALL other filter dropdowns. */
    var out = {
      atcFilters: sel.atcFilters || {},
      nfcFilters: sel.nfcFilters || {}
    };
    /* Enumerate DIMENSION_KEYS to grab every known dimension from sel */
    var allDimKeys = ["flow","country","sector","manufacturer","subManufacturer","medicine","subProduct","productSource","sku","agent",
      "atc1","atc2","atc3","atc4","molecule",
      "nfc","nfc2","nfc3",
      "region","governorate",
      "continent","subContinent","countryOfOrigin","manufacturerType","localImported",
      "orderNo","tenderStatus","LaunchDate","pharmacyClass",
      "form","pack","strength","aloneCombined",
      "docRegion","docSpec","docCity","phRegion","phCity",
      "patientAge","patientGender","diagnosis","source","docSalesRegion","phSalesRegion"];
    allDimKeys.forEach(function(k){ out[k] = sel[k] || []; });
    return out;
  }, [sel]);

  var _hasAnyFilter = useMemo(function() {
    var f = _cascadeFilters;
    var keys = ["flow","country","sector","manufacturer","subManufacturer","medicine","subProduct","productSource","sku","agent",
      "atc1","atc2","atc3","atc4","molecule",
      "nfc","nfc2","nfc3",
      "region","governorate",
      "continent","subContinent","countryOfOrigin","manufacturerType","localImported",
      "orderNo","tenderStatus","LaunchDate","pharmacyClass",
      "form","pack","strength","aloneCombined",
      "docRegion","docSpec","docCity","phRegion","phCity",
      "patientAge","patientGender","diagnosis","source","docSalesRegion","phSalesRegion"];
    for (var i = 0; i < keys.length; i++) { if (f[keys[i]] && f[keys[i]].length) return true; }
    var atc = f.atcFilters || {};
    for (var ak in atc) { if (atc[ak] && atc[ak].length) return true; }
    var nfc = f.nfcFilters || {};
    for (var nk in nfc) { if (nfc[nk] && nfc[nk].length) return true; }
    return false;
  }, [_cascadeFilters]);

  /* Version counter — increments on every filter change to guarantee effect fires */
  var _cascadeVersion = useRef(0);
  var _cascadeVersionVal = useMemo(function() { return ++_cascadeVersion.current; }, [_cascadeFilters]);

  useEffect(function() {
    /* Only cascade for private studies with server-loaded dims (no client rows) */
    if (!dimValues || !setDimValues) return;
    if (!_initDimsRef.current && rows && rows.length > 0) return; /* client-side only (CSV upload) */

    /* If no filters active, immediately restore initial (full) dimension lists */
    if (!_hasAnyFilter && _initDimsRef.current) {
      setDimValues(Object.assign({}, _initDimsRef.current));
      setCascadeLoading(false);
      return;
    }

    var cancelled = false;
    setCascadeLoading(true);
    var timer = setTimeout(function() {
      api.getCascadedDims(activeStudyType || "iraq", _cascadeFilters)
        .then(function(newDims) {
          if (cancelled) return;
          setDimValues(function(prev) {
            if (!prev) return prev;
            var merged = Object.assign({}, prev);
            Object.keys(newDims).forEach(function(k) { merged[k] = newDims[k]; });
            return merged;
          });
        })
        .catch(function(e) { console.warn("[cascade] Failed:", e.message); })
        .finally(function() { if (!cancelled) setCascadeLoading(false); });
    }, 400);

    return function() { cancelled = true; clearTimeout(timer); };
  }, [_cascadeVersionVal, _gdmVerF]);

  /* ── Permission helpers ── */
  /* ── Resolve effective dimensions: tree-scoped per active study type ── */
  /* DIMENSION_KEYS (constants.js) predates LaunchDate — append it so the
     "no restriction" fallback (admins / legacy users without a permissions
     tree) can see the dimension. Tree-scoped users still need the grant. */
  var _companyDims = (clientPerms && clientPerms.allowedDimensions) ? clientPerms.allowedDimensions : DIMENSION_KEYS.concat(["LaunchDate"]);
  var _studyPerms = null;
  var _hasSATree = !!(user && Array.isArray(user.studyAccess) && user.studyAccess.length);
  if (_hasSATree && activeStudyType) {
    for (var _spi = 0; _spi < user.studyAccess.length; _spi++) {
      if (user.studyAccess[_spi].studyTypeCode === activeStudyType) { _studyPerms = user.studyAccess[_spi]; break; }
    }
  }
  /* FAIL-CLOSED (security review): when a per-study permissions tree exists, dimensions come
     ONLY from the matched study entry — a missing entry or missing list grants nothing.
     Company-level dims remain the fallback only for record-less legacy users. Admin unaffected. */
  var _isAdminFP = user && user.role === "admin";
  var _effectiveDims;
  if (_isAdminFP) _effectiveDims = (_studyPerms && Array.isArray(_studyPerms.effectiveFeatures) && _studyPerms.effectiveFeatures.length) ? _studyPerms.effectiveFeatures : _companyDims;
  else if (_hasSATree) _effectiveDims = (_studyPerms && Array.isArray(_studyPerms.effectiveFeatures)) ? _studyPerms.effectiveFeatures : [];
  else _effectiveDims = _companyDims;
  const allowedDims = _effectiveDims;
  const allowedSectors = (clientPerms && clientPerms.allowedSectors) ? clientPerms.allowedSectors : null;
  /* Check if all SECTOR_TYPES are allowed → treat as no restriction */
  const sectorRestricted = useMemo(function() {
    if (!allowedSectors) return false;
    var allAllowed = SECTOR_TYPES.every(function(st) { return allowedSectors.indexOf(st) >= 0; });
    return !allAllowed;
  }, [allowedSectors]);
  const dimAllowed = function(k) {
    /* msa merged market: Source and the two combined region dims are the STRUCTURE
       of the study (like Flow on Sales Tracker), not grantable content — permission
       trees saved before they existed would otherwise hide them forever. */
    if (_isMsaP && (k === "source" || k === "docSalesRegion" || k === "phSalesRegion")) return true;
    return allowedDims.indexOf(k) >= 0;
  };
  /* Measure & Statistics permissions */
  var _allMeasureKeys = ALL_MEASURES; /* ["Unit","USD","LC","Price","Patients","Prescriptions"] */
  var _allStatKeys = ["Market Share","Rank","Growth","CAGR","Evolution Index","FOC %","Coverage %","Sell-Through %","Profit"];
  /* FAIL-CLOSED (security review): same rule as dims — with a permissions tree present,
     measures/statistics come only from the matched entry; missing entry = none granted. */
  var allowedMeasures, allowedStatistics;
  if (_hasSATree) {
    allowedMeasures = (_studyPerms && Array.isArray(_studyPerms.effectiveMeasures)) ? _studyPerms.effectiveMeasures : [];
    allowedStatistics = (_studyPerms && Array.isArray(_studyPerms.effectiveStatistics)) ? _studyPerms.effectiveStatistics : [];
  } else {
    allowedMeasures = _allMeasureKeys;
    allowedStatistics = _allStatKeys;
  }
  /* Only a FULL admin bypasses the permission tree; limited/team admins (dataScoped)
     follow their linked client's grants exactly like a client user. */
  var isAdmin = user && user.role === "admin" && !user.dataScoped;
  if (isAdmin) { allowedMeasures = _allMeasureKeys; allowedStatistics = _allStatKeys; }

  /* Medical studies (jordan_medical, etc.) used to be hard-restricted to
     ["Unit","Patients","Prescriptions"] regardless of admin permissions — that was
     a holdover from when those columns weren't tracked in the permissions UI.
     Now that the admin form supports per-study measure/statistic permissions
     including USD/LC/Price/Patients/Prescriptions, respect what the admin granted.
     Fallback: if no permissions are configured for a medical study, default to
     the medical-friendly set so the user isn't left with zero measures. */
  var _isMedical = activeStudyType === "jordan_medical" || (studyTypeConfig && studyTypeConfig.code === "jordan_medical");
  if (_isMedical && !isAdmin) {
    var hasMedPerms = _studyPerms && _studyPerms.effectiveMeasures && _studyPerms.effectiveMeasures.length > 0;
    if (!hasMedPerms) {
      /* Legacy default for unconfigured medical clients */
      allowedMeasures = ["Unit", "Patients", "Prescriptions", "RxPerDoctor", "Doctors", "DoctorsTotal"];   /* r121 / r122 */
      allowedStatistics = ["Market Share", "Rank"];
    }
    /* If hasMedPerms, allowedMeasures was already set from _studyPerms above — honor it. */
  }

  /* Cascade-filtered row sets for option lists */
  /* When dimValues is available (server-loaded dimensions), always use those for filter dropdowns.
     dimValues contains ALL dimension values from the cascade endpoint — not just the queried ones.
     rows from runQuery only contain GROUP BY columns, so they can't populate all dropdowns. */
  var useDims = !!dimValues;

  const rCo = useMemo(() => useDims ? [] : filterRows(rows, mp, {...EMPTY_SEL, country:sel.country}),                   [rows,mp,sel.country,useDims]);
  const rS  = useMemo(() => useDims ? [] : filterRows(rows, mp, {...EMPTY_SEL, country:sel.country, sector:sel.sector}),                    [rows,mp,sel.country,sel.sector,useDims]);
  const rM  = useMemo(() => useDims ? [] : filterRows(rows, mp, {...EMPTY_SEL, country:sel.country, sector:sel.sector, manufacturer:sel.manufacturer}), [rows,mp,sel.country,sel.sector,sel.manufacturer,useDims]);
  const rMd = useMemo(() => useDims ? [] : filterRows(rows, mp, {...EMPTY_SEL, country:sel.country, sector:sel.sector, manufacturer:sel.manufacturer, medicine:sel.medicine}), [rows,mp,sel.country,sel.sector,sel.manufacturer,sel.medicine,useDims]);
  const rSk = useMemo(() => useDims ? [] : filterRows(rows, mp, {...EMPTY_SEL, country:sel.country, sector:sel.sector, manufacturer:sel.manufacturer, medicine:sel.medicine, sku:sel.sku}), [rows,mp,sel.country,sel.sector,sel.manufacturer,sel.medicine,sel.sku,useDims]);
  const rAg = useMemo(() => useDims ? [] : filterRows(rows, mp, {...EMPTY_SEL, country:sel.country, sector:sel.sector, manufacturer:sel.manufacturer, medicine:sel.medicine, sku:sel.sku, agent:sel.agent}), [rows,mp,sel.country,sel.sector,sel.manufacturer,sel.medicine,sel.sku,sel.agent,useDims]);
  const rReg = useMemo(() => useDims ? [] : filterRows(rows, mp, {...EMPTY_SEL, country:sel.country, sector:sel.sector, manufacturer:sel.manufacturer, medicine:sel.medicine, sku:sel.sku, agent:sel.agent, region:sel.region}), [rows,mp,sel.country,sel.sector,sel.manufacturer,sel.medicine,sel.sku,sel.agent,sel.region,useDims]);

  const ctrOpts = useMemo(() => useDims ? (dimValues.country || []) : uniq(rows, mp.country),      [rows, mp.country, useDims, dimValues]);
  const sOptsRaw = useMemo(() => useDims ? (dimValues.sector || []) : uniq(rCo, mp.sector),       [rCo, mp.sector, useDims, dimValues]);
  const sOpts    = useMemo(function() {
    if (!sectorRestricted || !allowedSectors) return sOptsRaw;
    var allowedLower = allowedSectors.map(function(s){ return s.toLowerCase(); });
    return sOptsRaw.filter(function(s){ return allowedLower.indexOf(s.toLowerCase()) >= 0; });
  }, [sOptsRaw, allowedSectors, sectorRestricted]);
  const mfrOpts = useMemo(() => useDims ? (dimValues.manufacturer || []) : uniq(rS,   mp.manufacturer), [rS,   mp.manufacturer, useDims, dimValues]);
  const medOpts = useMemo(() => useDims ? (dimValues.medicine || []) : uniq(rM,   mp.medicine),     [rM,   mp.medicine, useDims, dimValues]);
  const skuOpts = useMemo(() => useDims ? (dimValues.sku || []) : uniq(rMd,  mp.sku),          [rMd,  mp.sku, useDims, dimValues]);
  const agtOpts = useMemo(() => useDims ? (dimValues.agent || []) : uniq(rSk,  mp.agent),        [rSk,  mp.agent, useDims, dimValues]);
  const orderNoOpts = useMemo(() => useDims ? (dimValues.orderNo || []) : uniq(rAg, mp.orderNo),  [rAg, mp.orderNo, useDims, dimValues]);
  const tenderStatusOpts = useMemo(() => useDims ? (dimValues.tenderStatus || []) : uniq(rAg, mp.tenderStatus), [rAg, mp.tenderStatus, useDims, dimValues]);
  const launchDateOpts = useMemo(() => useDims ? (dimValues.LaunchDate || []) : uniq(rAg, mp.LaunchDate), [rAg, mp.LaunchDate, useDims, dimValues]);
  const regOpts = useMemo(() => useDims ? (dimValues.region || []) : uniq(rAg,  mp.region),       [rAg,  mp.region, useDims, dimValues]);
  const govOpts = useMemo(() => useDims ? (dimValues.governorate || []) : uniq(rReg, mp.governorate),  [rReg, mp.governorate, useDims, dimValues]);
  const cntOpts  = useMemo(() => useDims ? (dimValues.continent || []) : [], [useDims, dimValues]);
  const scntOpts = useMemo(() => useDims ? (dimValues.subContinent || dimValues.subcontinent || []) : [], [useDims, dimValues]);
  const cooOpts  = useMemo(() => useDims ? (dimValues.countryOfOrigin || dimValues.countryoforigin || []) : [], [useDims, dimValues]);
  const goOpts   = useMemo(() => useDims ? (dimValues.manufacturerType || dimValues.manufacturertype || []) : [], [useDims, dimValues]);
  const liOpts   = useMemo(() => useDims ? (dimValues.localImported || dimValues.local_imported || []) : [], [useDims, dimValues]);
  /* Libya-specific: SubManufacturer, SubProduct, ProductSource */
  const subMfrOpts  = useMemo(() => useDims ? (dimValues.subManufacturer || dimValues.submanufacturer || []) : [], [useDims, dimValues]);
  const subProdOpts = useMemo(() => useDims ? (dimValues.subProduct || dimValues.subproduct || []) : [], [useDims, dimValues]);
  const prodSrcOpts = useMemo(() => useDims ? (dimValues.productSource || dimValues.productsource || []) : [], [useDims, dimValues]);
  /* Sales Tracker: Sell-In / Sell-Out / FOC flows */
  const flowOpts     = useMemo(() => useDims ? (dimValues.flow || []) : [], [useDims, dimValues]);
  /* Medical & Pharma Intelligence: Pharma / Rx dataset split */
  const sourceOpts   = useMemo(() => useDims ? (dimValues.source || []) : [], [useDims, dimValues]);
  const dsrOpts      = useMemo(() => useDims ? (dimValues.docSalesRegion || dimValues.docsalesregion || []) : [], [useDims, dimValues]);
  const psrOpts      = useMemo(() => useDims ? (dimValues.phSalesRegion || dimValues.phsalesregion || []) : [], [useDims, dimValues]);
  const phcOpts      = useMemo(() => useDims ? (dimValues.pharmacyClass || []).slice().sort() : [], [useDims, dimValues]);
  /* Medical-specific dimension opts */
  const formOpts     = useMemo(() => useDims ? (dimValues.form || []) : [], [useDims, dimValues]);
  const packOpts     = useMemo(() => useDims ? (dimValues.pack || []) : [], [useDims, dimValues]);
  const strengthOpts = useMemo(() => useDims ? (dimValues.strength || []) : [], [useDims, dimValues]);
  const aloneOpts    = useMemo(() => useDims ? (dimValues.aloneCombined || dimValues.alonecombined || []) : [], [useDims, dimValues]);
  const docRegOpts   = useMemo(() => useDims ? (dimValues.docRegion || dimValues.docregion || []) : [], [useDims, dimValues]);
  const docSpecOpts  = useMemo(() => useDims ? (dimValues.docSpec || dimValues.docspec || []) : [], [useDims, dimValues]);
  const docCityOpts  = useMemo(() => useDims ? (dimValues.docCity || dimValues.doccity || []) : [], [useDims, dimValues]);
  const phRegOpts    = useMemo(() => useDims ? (dimValues.phRegion || dimValues.phregion || []) : [], [useDims, dimValues]);
  const phCityOpts   = useMemo(() => useDims ? (dimValues.phCity || dimValues.phcity || []) : [], [useDims, dimValues]);
  const patAgeOpts   = useMemo(() => useDims ? (dimValues.patientAge || dimValues.patientage || []) : [], [useDims, dimValues]);
  const patGenOpts   = useMemo(() => {
    var raw = useDims ? (dimValues.patientGender || dimValues.patientgender || dimValues.sex || []) : [];
    /* Translate Sex column values: -1 → Female, 0 → Male */
    var GENDER_MAP = {"-1":"Female","0":"Male","1":"Male","-1.0":"Female","0.0":"Male","1.0":"Male"};
    return raw.map(function(v) { return GENDER_MAP[String(v)] || String(v); }).filter(function(v,i,a){ return a.indexOf(v)===i; });
  }, [useDims, dimValues]);
  /* Gender value translation for pivot table display */
  var _GENDER_TRANSLATE = {"-1":"Female","0":"Male","1":"Male","-1.0":"Female","0.0":"Male","1.0":"Male"};
  const diagOpts     = useMemo(() => useDims ? (dimValues.diagnosis || []) : [], [useDims, dimValues]);
  const pOpts   = pOptsExt;

  const atcCols = {ATC1:mp.atc1, ATC2:mp.atc2, ATC3:mp.atc3, ATC4:mp.atc4, MOLECULE:mp.molecule};
  /* ATC options use only shared filters (country/sector) — NOT product-branch filters.
     ATC classification is independent of product selection. */
  const rAtcBase = useMemo(() => useDims ? [] : filterRows(rows, mp, {...EMPTY_SEL, country:sel.country, sector:sel.sector}), [rows,mp,sel.country,sel.sector,useDims]);
  const atcOpts = useMemo(function() {
    if (useDims) return {ATC1: dimValues.atc1||[], ATC2: dimValues.atc2||[], ATC3: dimValues.atc3||[], ATC4: dimValues.atc4||[], MOLECULE: dimValues.molecule||[]};
    return Object.fromEntries(Object.entries(atcCols).map(([k,c]) => [k, c ? uniq(rAtcBase, c) : []]));
  }, [rAtcBase, mp.atc1, mp.atc2, mp.atc3, mp.atc4, mp.molecule, useDims, dimValues]);

  const nfcCols = {NFC:mp.nfc, NFC2:mp.nfc2, NFC3:mp.nfc3};
  const nfcOpts = useMemo(function() {
    if (useDims) return {NFC: dimValues.nfc||[], NFC2: dimValues.nfc2||[], NFC3: dimValues.nfc3||[]};
    return Object.fromEntries(Object.entries(nfcCols).map(([k,c]) => [k, c ? uniq(rAg, c) : []]));
  }, [rAg, mp.nfc, mp.nfc2, mp.nfc3, useDims, dimValues]);

  /* ── Value maps: aggregate USD per option for visual bars in dropdowns ── */
  var _valCol = mp.valUSD || mp.valLC || mp.units || null;
  var _svmSt = useState(null), _serverValMaps = _svmSt[0], _setServerValMaps = _svmSt[1];
  var _initValMapsRef = useRef(null);      /* unfiltered snapshot — restored when the last filter clears */
  var _hasAnyFilterRef = useRef(false);
  _hasAnyFilterRef.current = _hasAnyFilter;
  var _dimValFetched = useRef(false);
  useEffect(function() {
    if (_dimValFetched.current || !activeStudyType) return;
    _dimValFetched.current = true;
    api.getDimValues(activeStudyType).then(function(result) {
      if (result && typeof result === "object") {
        _initValMapsRef.current = result;
        /* Don't clobber a filtered map that resolved first (saved query on load) */
        if (!_hasAnyFilterRef.current) _setServerValMaps(result);
      }
    }).catch(function() {});
  }, [activeStudyType]);

  /* ── Cascade the VALUE BARS too: with filters active, per-option aggregates are
     re-fetched WITHIN the current selection (same params + self-skip as the option
     cascade), so the bars and the value ordering reflect the filtered market.
     No filters → restore the unfiltered snapshot. CSV mode keeps client maps. ── */
  useEffect(function() {
    if (!activeStudyType) return;
    if (!_initDimsRef.current && rows && rows.length > 0) return; /* client-side only (CSV upload) */
    if (!_hasAnyFilter) {
      if (_initValMapsRef.current) _setServerValMaps(Object.assign({}, _initValMapsRef.current));
      return;
    }
    var cancelled = false;
    var timer = setTimeout(function() {
      api.getDimValues(activeStudyType, _cascadeFilters)
        .then(function(result) {
          if (!cancelled && result && typeof result === "object" && Object.keys(result).length > 0) _setServerValMaps(result);
        })
        .catch(function() {});
    }, 450);
    return function() { cancelled = true; clearTimeout(timer); };
  }, [_cascadeVersionVal, activeStudyType, _gdmVerF]);

  var valueMaps = useMemo(function() {
    /* Prefer server-fetched values */
    if (_serverValMaps && Object.keys(_serverValMaps).length > 0) return _serverValMaps;
    /* Fallback: compute from client rows (CSV uploads) */
    if (!rows || !rows.length || !_valCol) return {};
    var maxYr = 0;
    if (mp.yearCol) { for (var yi = 0; yi < rows.length; yi++) { var yv = Number(rows[yi][mp.yearCol]) || 0; if (yv > maxYr) maxYr = yv; } }
    var filtered = maxYr > 0 ? rows.filter(function(r) { return Number(r[mp.yearCol]) === maxYr; }) : rows;
    var dimKeys = ["country","sector","manufacturer","medicine","sku","agent","region","governorate",
                   "atc1","atc2","atc3","atc4","molecule","nfc","nfc2","nfc3",
                   "subManufacturer","subProduct","productSource","countryOfOrigin","manufacturerType","localImported"];
    var maps = {};
    for (var di = 0; di < dimKeys.length; di++) {
      var dk = dimKeys[di]; var col = mp[dk]; if (!col) continue;
      var m = {};
      for (var ri = 0; ri < filtered.length; ri++) {
        var name = String(filtered[ri][col] || ""); if (!name || name === "undefined") continue;
        m[name] = (m[name] || 0) + (Number(filtered[ri][_valCol]) || 0);
      }
      if (Object.keys(m).length > 0) maps[dk] = m;
    }
    /* ── Inject gender value translation: -1 → Female, 0 → Male ── */
    if (maps.patientGender) {
      var gm = maps.patientGender;
      var translated = {};
      var GT = {"-1":"Female","0":"Male","1":"Male","-1.0":"Female","0.0":"Male","1.0":"Male"};
      Object.keys(gm).forEach(function(k) {
        var label = GT[String(k)] || k;
        translated[label] = (translated[label] || 0) + gm[k];
      });
      maps.patientGender = translated;
    }
    return maps;
  }, [rows, _valCol, mp, _serverValMaps]);

  const S       = (k, v) => setSel(p => ({...p, [k]: v}));
  const setCtr  = v => setSel(p => ({...EMPTY_SEL, showCols:p.showCols, measures:p.measures, country:v}));
  const setSect = v => setSel(p => ({...EMPTY_SEL, showCols:p.showCols, measures:p.measures, country:p.country, sector:v}));
  const setMfr  = v => setSel(p => ({...p, manufacturer:v, medicine:[], sku:[], agent:[], region:[], governorate:[]}));
  const setMed  = v => setSel(p => ({...p, medicine:v,  sku:[], agent:[], region:[], governorate:[]}));
  const setSku  = v => setSel(p => ({...p, sku:v,  agent:[], region:[], governorate:[]}));
  const setAgt  = v => setSel(p => ({...p, agent:v, region:[], governorate:[]}));
  const setOrderNo = v => setSel(p => ({...p, orderNo:v}));
  const setLaunchDate = v => setSel(p => ({...p, LaunchDate:v}));
  const setTenderStatus = v => setSel(p => ({...p, tenderStatus:v}));
  const setReg  = v => setSel(p => ({...p, region:v, governorate:[]}));
  const setGov  = v => setSel(p => ({...p, governorate:v}));
  const setSubMfr  = v => setSel(p => ({...p, subManufacturer:v}));
  const setSubProd = v => setSel(p => ({...p, subProduct:v}));
  const setProdSrc = v => setSel(p => ({...p, productSource:v}));
  const setCnt  = v => setSel(p => ({...p, continent:v}));
  const setScnt = v => setSel(p => ({...p, subContinent:v}));
  const setCoo  = v => setSel(p => ({...p, countryOfOrigin:v}));
  const setGo   = v => setSel(p => ({...p, manufacturerType:v}));
  const setLi   = v => setSel(p => ({...p, localImported:v}));
  const setPhc  = v => setSel(p => ({...p, pharmacyClass:v}));
  /* Medical dimension setters */
  const setFlow    = v => setSel(p => ({...p, flow:v}));
  const setSource  = v => setSel(p => ({...p, source:v}));
  const setDsr     = v => setSel(p => ({...p, docSalesRegion:v}));
  const setPsr     = v => setSel(p => ({...p, phSalesRegion:v}));
  const setForm    = v => setSel(p => ({...p, form:v}));
  const setPack    = v => setSel(p => ({...p, pack:v}));
  const setStrength= v => setSel(p => ({...p, strength:v}));
  const setAlone   = v => setSel(p => ({...p, aloneCombined:v}));
  const setDocReg  = v => setSel(p => ({...p, docRegion:v}));
  const setDocSpec = v => setSel(p => ({...p, docSpec:v}));
  const setDocCity = v => setSel(p => ({...p, docCity:v}));
  const setPhReg   = v => setSel(p => ({...p, phRegion:v}));
  const setPhCity  = v => setSel(p => ({...p, phCity:v}));
  const setPatAge  = v => setSel(p => ({...p, patientAge:v}));
  const setPatGen  = v => setSel(p => ({...p, patientGender:v}));
  const setDiag    = v => setSel(p => ({...p, diagnosis:v}));
  /* When a statistic is enabled, set its measure scope to the FIRST selected measure
     (whichever the user picked first — Unit / USD / LC / Price / etc., NOT hardcoded to Unit).
     If the stat was previously enabled with a scope that's no longer valid (e.g. user loaded
     a saved query whose measures differed, or removed the stale measure from the Measures
     section), the stale entries are dropped first. If nothing valid remains after cleanup,
     default to the user's current first measure.
     statMeasures shape: { "Market Share": ["Unit"], "Rank": ["USD","LC"] }. */
  const togStatInd = v => setSel(p => {
    const st = p.stats || {indicators:[], growthMode:"prev"};
    const enabling = !st.indicators.includes(v);
    const inds = enabling ? [...st.indicators, v] : st.indicators.filter(x=>x!==v);
    const sm = Object.assign({}, st.statMeasures || {});
    if (enabling) {
      /* Drop any measures from the saved scope that are no longer in p.measures
         (defensive against stale Unit baked into older saved queries). */
      var prevValid = (sm[v] || []).filter(function(mk){
        return (p.measures || []).indexOf(mk) >= 0;
      });
      if (prevValid.length > 0) {
        sm[v] = prevValid;
      } else if (p.measures && p.measures.length) {
        /* No valid prior scope → default to the FIRST currently selected measure.
           EXCEPTION (Noura 2026-09-23): Sell-Through % / Profit default to ALL
           selected measures — a first-measure default of e.g. Unit left Profit
           (USD/LC only) with nothing to attach to, so the pill lit but no
           columns appeared. */
        sm[v] = (v === "Sell-Through %" || v === "Profit") ? p.measures.slice() : [p.measures[0]];
      }
      /* If user has no measures at all, sm[v] stays undefined — UI shows
         the "Select a measure in the Measures section above first" hint. */
    }
    return {...p, stats:{...st, indicators:inds, statMeasures:sm}};
  });
  /* Toggle a specific measure within a statistic's scope. */
  const togStatMeasure = (statId, measureKey) => setSel(p => {
    const st = p.stats || {indicators:[], growthMode:"prev"};
    const sm = Object.assign({}, st.statMeasures || {});
    const cur = (sm[statId] || []).slice();
    const idx = cur.indexOf(measureKey);
    if (idx >= 0) cur.splice(idx, 1); else cur.push(measureKey);
    sm[statId] = cur;
    return {...p, stats:{...st, statMeasures:sm}};
  });
  const setGrowthMode = m => setSel(p => {
    const st = p.stats || {indicators:[], growthMode:"prev"};
    return {...p, stats:{...st, growthMode:m}};
  });
  /* Evolution Index comparison mode — its own base-period choice, like Growth:
     "prev" (vs previous period), "yoy" (vs similar period last year), "both". */
  const setEvoMode = m => setSel(p => {
    const st = p.stats || {indicators:[], growthMode:"prev"};
    return {...p, stats:{...st, evoMode:m}};
  });
  const setAtcFilter = (lvl, vals) => setSel(p => ({...p, atcFilters:{...(p.atcFilters||{}), [lvl]:vals}}));
  const setNfcFilter = (lvl, vals) => setSel(p => ({...p, nfcFilters:{...(p.nfcFilters||{}), [lvl]:vals}}));
  /* Period mode click handler — MULTI-PERIOD-TYPE support.
     - Clicking a mode only switches which type panel is OPEN (accordion); it
       never clears selections. Keys from several types can now coexist in one
       query (each period key is self-typed by its format — 2026 / 2026-Q1 /
       2026-01 / MAT-Q1-2026 / YTD-2026), and every type's growth compares
       against ITS OWN previous / similar period. */
  const setMode = m => setSel(p => p.periodMode === m ? p : ({...p, periodMode: m}));
  const togKey  = k => setSel(p => ({...p, periodKeys: p.periodKeys.includes(k) ? p.periodKeys.filter(x => x !== k) : [...p.periodKeys, k]}));
  /* Select All / Clear scoped to ONE type's key list — keys of other types are preserved. */
  const togAllK = ks => setSel(p => {
    const allOn = ks.every(k => p.periodKeys.includes(k));
    const rest = p.periodKeys.filter(k => ks.indexOf(k) < 0);
    return {...p, periodKeys: allOn ? rest : rest.concat(ks)};
  });
  /* Up to MAX_FEATURES features (pivot levels) per query. Adding a 9th is refused
     with a short notice under the sections instead of silently dropping it later. */
  var _capS = useState(false), capHit = _capS[0], setCapHit = _capS[1];
  const togShow = k => setSel(p => {
    if (p.showCols.includes(k)) return {...p, showCols: p.showCols.filter(x => x !== k)};
    if (p.showCols.length >= MAX_FEATURES) { setCapHit(true); setTimeout(function(){ setCapHit(false); }, 3500); return p; }
    return {...p, showCols: [...p.showCols, k]};
  });

  /* Reorder a feature within sel.showCols by dragging. Both keys must already be in showCols.
     `dragKey` is the feature being moved; `targetKey` is the row it was dropped on; `before`
     decides whether to insert above or below the drop target. Pivot dimension order in the
     resulting tree follows showCols order, so this directly controls grouping. */
  const reorderShow = useCallback(function(dragKey, targetKey, before) {
    setSel(function(p){
      var sc = (p.showCols || []).slice();
      var di = sc.indexOf(dragKey);
      if (di < 0) return p;
      sc.splice(di, 1); /* remove from old slot */
      var ti = sc.indexOf(targetKey);
      if (ti < 0) { sc.push(dragKey); return Object.assign({}, p, {showCols: sc}); }
      var insertAt = before ? ti : ti + 1;
      sc.splice(insertAt, 0, dragKey);
      return Object.assign({}, p, {showCols: sc});
    });
  }, [setSel]);

  const dims      = sel.showCols.filter(k => mp[k]);
  const childDim  = dims.length > 0 ? dims[dims.length - 1] : null;
  const parentDim = dims.length >= 2 ? dims[0] : null;

  const togSec = useCallback(id => setOpenSecs(p => ({...p, [id]: !p[id]})), []);

  const mktSel = sel.manufacturer.length + sel.medicine.length + sel.sku.length + sel.agent.length + (sel.form||[]).length + (sel.pack||[]).length + (sel.strength||[]).length + (sel.aloneCombined||[]).length;
  const geoSel = sel.region.length + sel.governorate.length + (sel.docSalesRegion||[]).length + (sel.phSalesRegion||[]).length;
  const mfrOriginSel = sel.continent.length + sel.subContinent.length + sel.countryOfOrigin.length + sel.manufacturerType.length + sel.localImported.length;

  /* Feature-active counts: count features added to the pivot tree (showCols) per section.
     These tell the user "this section has features in your pivot" — distinct from filter-value
     counts which only count selected dropdown values. The displayed cnt = sum of both so a
     section lights up whether you've added features OR selected filter values. */
  var _scSet = (sel.showCols || []);
  var _has = function(k) { return _scSet.indexOf(k) >= 0 ? 1 : 0; };
  const mktFeat   = _has("manufacturer") + _has("subManufacturer") + _has("medicine") + _has("subProduct") + _has("productSource") + _has("sku") + _has("agent") + _has("form") + _has("pack") + _has("strength") + _has("aloneCombined");
  const geoFeat   = _has("region") + _has("governorate");
  const mfrOFeat  = _has("continent") + _has("subContinent") + _has("countryOfOrigin") + _has("manufacturerType") + _has("localImported");
  const atcFeat   = _has("atc1") + _has("atc2") + _has("atc3") + _has("atc4") + _has("molecule");
  const nfcFeat   = _has("nfc") + _has("nfc2") + _has("nfc3");
  const docFeat   = _has("docRegion") + _has("docSpec") + _has("docCity");
  const phFeat    = _has("phRegion") + _has("phCity");
  const patFeat   = _has("patientAge") + _has("patientGender");
  const indFeat   = _has("diagnosis");
  const atcTagVals = Object.values(sel.atcFilters || {}).flat();
  const nfcTagVals = Object.values(sel.nfcFilters || {}).flat();
  var _tnMLabel = "";
  if (sel.topN && sel.topN.enabled) {
    var _tnM = sel.topN.measure || "Unit";
    var _tnAvail = [];
    if (sel.measures.includes("Unit") && mp.units) _tnAvail.push("Unit");
    if (sel.measures.includes("USD") && mp.valUSD) _tnAvail.push("USD");
    if (sel.measures.includes("LC") && mp.valLC) _tnAvail.push("LC");
    if (_tnAvail.length > 0 && _tnAvail.indexOf(_tnM) < 0) _tnM = _tnAvail[0];
    _tnMLabel = _tnM;
  }
  const tnTag = (sel.topN && sel.topN.enabled) ? ["Top "+sel.topN.n+" by "+_tnMLabel+(sel.topN.showOthers?" +Others":"")] : [];
  /* ── Active filters, grouped by dimension ────────────────────────────────
     Rendered as one chip per dimension ("Product: 3"), each individually
     clearable, plus a Reset for the lot. The previous flat value list was never
     rendered anywhere and silently omitted productSource, subProduct and
     subManufacturer — every data dimension is now accounted for. */
  const activeGroups = (function () {
    var g = [];
    var add = function (key, label, vals) { if (vals && vals.length) g.push({ key: key, label: label, vals: vals }); };
    add("country", "Country", sel.country);
    add("sector", "Sector", sel.sector);
    add("manufacturer", "Manufacturer", sel.manufacturer);
    add("subManufacturer", "Sub-manufacturer", sel.subManufacturer);
    add("medicine", "Product", sel.medicine);
    add("productSource", "Product Source", sel.productSource);
    add("subProduct", "SubProduct", sel.subProduct);
    add("sku", "SKU", sel.sku);
    add("agent", "Agent", sel.agent);
    if (atcTagVals.length) g.push({ key: "atcFilters", label: "ATC", vals: atcTagVals });
    if (nfcTagVals.length) g.push({ key: "nfcFilters", label: "NFC", vals: nfcTagVals });
    add("region", "Region", sel.region);
    add("governorate", "Governorate", sel.governorate);
    add("continent", "Continent", sel.continent);
    add("subContinent", "Sub-continent", sel.subContinent);
    add("countryOfOrigin", "Country of origin", sel.countryOfOrigin);
    add("manufacturerType", "Manufacturer type", sel.manufacturerType);
    add("localImported", "Local / Imported", sel.localImported);
    return g;
  })();
  const activeTags = activeGroups.reduce(function (a, x) { return a.concat(x.vals); },
    [].concat(sel.periodMode ? [sel.periodMode] : [], sel.periodKeys || [], sel.measures || [], tnTag));
  const clearDim = function (key) {
    setSel(function (p) {
      var n = Object.assign({}, p);
      if (key === "atcFilters" || key === "nfcFilters") n[key] = {}; else n[key] = [];
      return n;
    });
  };
  const resetDataFilters = function () {
    setSel(function (p) {
      var n = Object.assign({}, p);
      for (var i = 0; i < activeGroups.length; i++) {
        var k = activeGroups[i].key;
        if (k === "atcFilters" || k === "nfcFilters") n[k] = {}; else n[k] = [];
      }
      return n;
    });
  };

  /* ── Live search callback: queries server for matching values when user types in dropdown ── */
  var makeSearch = useCallback(function(dimKey) {
    return function(query) {
      /* Search within the CURRENT cascade: results only include values that
         co-exist with every other active filter. */
      return api.searchDim(activeStudyType || "", dimKey, query, 50, _cascadeFilters)
        .catch(function() { return []; });
    };
  }, [activeStudyType, _cascadeFilters]);

  return (
    <aside style={{width:collapsed?36:302, minWidth:collapsed?36:302,
                   background:__PT?__PT.shellBg:"linear-gradient(180deg,#eef4fb 0%,#dce8f5 100%)",
                   borderRight:"1.5px solid "+(__PT?__PT.shellBr:"#c9daea"),
                   display:"flex", flexDirection:"column", height:"100%", overflow:"hidden", flexShrink:0, transition:"width .2s"}}>

      {collapsed ? (
        /* Collapsed view: expand button + clickable icons for each section */
        <div style={{display:"flex", flexDirection:"column", height:"100%", background:__PT?__PT.shellBg:"linear-gradient(180deg,#eef4fb 0%,#dce8f5 100%)"}}>
          <div style={{padding:"10px 4px", borderBottom:"1px solid "+(__PT?__PT.shellBr:"#c9daea"), display:"flex", justifyContent:"center", flexShrink:0}}>
            <button onClick={onToggleCollapse}
              title="Show filters"
              style={{background:__PT?__PT.card:"#fff", border:"1px solid "+(__PT?__PT.cardBr:"#c9daea"), borderRadius:6, padding:"5px 7px", cursor:"pointer", color:__PT?__PT.acc:"#4b7bb5", fontSize:12, lineHeight:1, display:"flex", alignItems:"center", justifyContent:"center"}}>
              ▶
            </button>
          </div>
          <div style={{flex:1, overflowY:"auto", padding:"8px 0"}}>
            {(function(){
              /* Build ordered icon list matching visible sections */
              var iconList = [];
              /* Dynamic groups from studyTypeConfig */
              if (studyTypeConfig && studyTypeConfig.features && (studyTypeConfig.category === "public" || studyTypeConfig.code === "kimadia")) {
                var GRP_ICONS2 = {Product:"🏷️","Market Details":"🏷️",ATC:"🧬","ATC/Molecule":"🧬","Therapeutic Class":"🧬",Geo:"📍","Region / Governorate":"📍",Origin:"🌍","Manufacturers Origin":"🌍",MarketGroup:"🔗",Status:"📋"};
                var seen = {};
                studyTypeConfig.features.forEach(function(f){
                  if (f.key === "tenderStatus" || f.key === "TenderStatus") return;
                  var g = f.group || "Other";
                  if (seen[g]) return; seen[g] = 1;
                  iconList.push({id:"dyn_"+g, icon: GRP_ICONS2[g] || "📋", label: g});
                });
              } else {
                /* Private/default study types (iraq, jordan, libya, jordan_medical).
                   Use the SAME gating signals as the expanded panel below so the icon
                   list matches what's actually visible. Medical-only sections (Doctor,
                   Pharmacy, Patient, Indication) only show on jordan_medical because
                   only that study's mp.* mapping populates docRegion/phRegion/etc. */
                iconList.push({id:"Market", icon:"🏷️", label:"Market Features"});
                iconList.push({id:"ATC", icon:"🧬", label:"ATC / Molecule"});
                if (mp && (mp.nfc || mp.nfc2 || mp.nfc3)) iconList.push({id:"NFC", icon:"🔖", label:"NFC"});
                if (mp && (mp.docRegion || mp.docSpec || mp.docCity))
                  iconList.push({id:"Doctor", icon:"🩺", label:"Doctor Details"});
                if (mp && (mp.phRegion || mp.phCity))
                  iconList.push({id:"Pharmacy", icon:"💊", label:"Pharmacy Details"});
                if (mp && (mp.patientAge || mp.patientGender))
                  iconList.push({id:"Patient", icon:"👤", label:"Patient Details"});
                if (mp && mp.diagnosis)
                  iconList.push({id:"Indication", icon:"🔬", label:"Indication"});
                if (mp && (mp.region || mp.governorate))
                  iconList.push({id:"Geo", icon:"📍", label:"Region / Governorate"});
                iconList.push({id:"MfrOrigin", icon:"🌍", label:"Manufacturers Origin"});
              }
              iconList.push({id:"Period", icon:"📅", label:"Period"});
              iconList.push({id:"Measures", icon:"📊", label:"Measures"});
              iconList.push({id:"Statistics", icon:"📈", label:"Statistics"});
              iconList.push({id:"TopN", icon:"🔝", label:"Top N Filter"});
              iconList.push({id:"SavedQueries", icon:"💾", label:"Saved Queries"});

              return iconList.map(function(it){
                return React.createElement("button",{
                  key: it.id,
                  title: it.label,
                  onClick: function(){
                    /* Expand the panel and open this section */
                    if (onToggleCollapse) onToggleCollapse();
                    setOpenSecs(function(p){ var n = Object.assign({}, p); n[it.id] = true; return n; });
                  },
                  style:{
                    display:"flex", alignItems:"center", justifyContent:"center",
                    width:"100%", padding:"10px 0", border:"none",
                    background:"transparent", cursor:"pointer",
                    color:__PT?__PT.icOff:"#6b8db5", borderLeft:"3px solid transparent",
                    transition:"all .15s"
                  },
                  onMouseOver: function(e){
                    e.currentTarget.style.background = __PT?__PT.accBgOn:"rgba(37,99,235,.08)";
                    e.currentTarget.style.borderLeft = "3px solid "+(__PT?__PT.acc:"#2563eb");
                    /* Force-color the inner SVG on hover by recoloring the stroke via CSS */
                    var svg = e.currentTarget.querySelector("svg");
                    if (svg) svg.setAttribute("stroke", __PT?__PT.acc:"#2563eb");
                  },
                  onMouseOut:  function(e){
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.borderLeft = "3px solid transparent";
                    var svg = e.currentTarget.querySelector("svg");
                    if (svg) svg.setAttribute("stroke", __PT?__PT.icOff:"#6b8db5");
                  }
                }, _filterIcon(it.id, 18, __PT?__PT.icOff:"#6b8db5"));
              });
            })()}
          </div>
        </div>
      ) : (
      <React.Fragment>
      <div style={{padding:"12px 14px 8px", borderBottom:"1.5px solid "+(__PT?__PT.shellBr:"#f1f5f9"), flexShrink:0, display:"flex", alignItems:"center", justifyContent:"space-between", gap:8}}>
        {__PT ? (
          <div style={{flex:1, minWidth:0, position:"relative"}}>
            <select value={activeStudyType || ""}
              onChange={function(e){ if (e.target.value && onStudyTypeChange) onStudyTypeChange(e.target.value); }}
              style={{width:"100%", appearance:"none", WebkitAppearance:"none", MozAppearance:"none",
                      background:__PT.card, border:"1px solid rgba(255,255,255,.18)", borderRadius:10,
                      padding:"9px 30px 9px 13px", color:__PT.head, fontSize:12.5, fontWeight:800,
                      fontFamily:"inherit", outline:"none", cursor:"pointer"}}>
              {(userStudyTypes && userStudyTypes.length ? userStudyTypes : [{code:activeStudyType||"", name:"Sales Tracker"}]).map(function(st){
                return <option key={st.code} value={st.code}>{"AMS Analytix — " + (st.name || st.code)}</option>;
              })}
            </select>
            <span style={{position:"absolute", right:11, top:"50%", transform:"translateY(-50%)", fontSize:9, color:__PT.head, pointerEvents:"none"}}>{"▼"}</span>
          </div>
        ) : (
        <div style={{flex:1, minWidth:0}}>
          <div style={{fontSize:13, fontWeight:800, color:"#0f172a"}}>Filters</div>
          <div style={{fontSize:9, color:"#94a3b8", marginTop:2}}>✓ = market dim · multi-select = filter · 2 dims = tree</div>
        </div>
        )}
        {(!_isAdminFP && _hasSATree && allowedDims.length === 0) && (
          <div style={{margin:"10px 12px", padding:"10px 12px", background:"#FFF7E6", border:"1px solid #F0C36D", borderRadius:8, fontSize:10.5, color:"#7A5B00", lineHeight:1.5}}>
            ⚠️ Your permissions for this study don't grant any dimensions or features yet. Please contact your administrator to review your access.
          </div>
        )}
        {onToggleCollapse && <button onClick={onToggleCollapse}
          title="Hide filters"
          style={{background:__PT?__PT.card:"#fff", border:"1px solid "+(__PT?__PT.cardBr:"#c9daea"), borderRadius:6, padding:"4px 6px", cursor:"pointer", color:__PT?__PT.acc:"#4b7bb5", fontSize:12, lineHeight:1, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0}}>
          ◀
        </button>}
      </div>

      {clientPerms && (sectorRestricted || (clientPerms.periodAccess && clientPerms.periodAccess.mode !== "none")) && (
        <div style={{margin:"6px 12px 0", padding:"5px 9px", background:"#f0f9ff", borderRadius:5, border:"1px solid #bae6fd", fontSize:9, color:"#0369a1"}}>
          <strong>🔒 Access:</strong>{" "}
          {sectorRestricted && allowedSectors && <span>Sectors: {allowedSectors.join(", ")}</span>}
          {sectorRestricted && allowedSectors && clientPerms.periodAccess && clientPerms.periodAccess.mode !== "none" && <span> · </span>}
          {clientPerms.periodAccess && clientPerms.periodAccess.mode !== "none" && (
            <span>Period restricted</span>
          )}
        </div>
      )}

      {/* ── ACTIVE FILTERS ── one chip per dimension, each clearable, plus Reset.
             Sits above the sections so the reader always knows what the numbers
             they are about to query actually represent. */}
      {activeGroups.length > 0 && (
        <div style={{margin:"6px 12px 0", padding:"6px 8px", background:__PT?__PT.card:"#fff", border:"1px solid "+(__PT?__PT.cardBr:"#c9daea"), borderRadius:8}}>
          <div style={{display:"flex", alignItems:"center", gap:6, marginBottom:5}}>
            <span style={{fontSize:8.5, fontWeight:800, color:__PT?"#9FBFC5":"#1A3F6E", textTransform:"uppercase", letterSpacing:__PT?".08em":".05em"}}>Active filters</span>
            <span style={{fontSize:8.5, fontWeight:700, color:__PT?__PT.sub:"#64748b"}}>{activeGroups.length + (activeGroups.length === 1 ? " dimension" : " dimensions")}</span>
            <span onClick={resetDataFilters} title="Clear every data filter (period, measures and Top N are kept)"
              style={{marginLeft:"auto", fontSize:8.5, fontWeight:800, color:__PT?__PT.acc:"#dc2626", cursor:"pointer", textTransform:__PT?"none":"uppercase", letterSpacing:".04em"}}>Reset</span>
          </div>
          <div style={{display:"flex", flexWrap:"wrap", flexDirection:__PT?"column":"row", gap:__PT?6:4}}>
            {activeGroups.map(function (g) {
              var one = g.vals.length === 1;
              return (
                <span key={g.key} title={g.label + ": " + g.vals.join(", ")}
                  style={{display:"inline-flex", alignItems:"center", gap:4, background:__PT?__PT.selBg:"#eef4fb", border:"1px solid "+(__PT?__PT.accDim:"#c9daea"),
                          borderRadius:__PT?9:7, padding:__PT?"7px 11px":"2px 6px", fontSize:__PT?10.5:8.5, fontWeight:700, color:__PT?__PT.head:"#1A3F6E", maxWidth:__PT?"100%":172}}>
                  <span style={{overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                    {g.label + ": " + (one ? String(g.vals[0]) : g.vals.length)}
                  </span>
                  <span onClick={function(){ clearDim(g.key); }} title={"Clear " + g.label}
                    style={{cursor:"pointer", color:__PT?__PT.acc:"#64748b", fontWeight:800, flexShrink:0}}>×</span>
                </span>
              );
            })}
          </div>
        </div>
      )}

      <div style={{flex:1, overflowY:"auto"}}>

        {/* ══════ DYNAMIC STUDY-TYPE-DRIVEN SECTIONS ══════ */}
        {studyTypeConfig && studyTypeConfig.features && (studyTypeConfig.category === "public" || studyTypeConfig.code === "kimadia") && (() => {
          /* Group features by FeatureGroup */
          var GRP_COLORS = {Product:"#9333ea", "Market Details":"#9333ea", ATC:"#7c3aed", "ATC/Molecule":"#7c3aed", "Therapeutic Class":"#7c3aed", Geo:"#b45309", "Region / Governorate":"#b45309", Origin:"#0e7490", "Manufacturers Origin":"#0e7490", MarketGroup:"#db2777", Status:"#dc2626", NFC:"#db2777", "Product Details":"#047857"};
          var GRP_ICONS  = {Product:"🏷️", "Market Details":"🏷️", ATC:"🧬", "ATC/Molecule":"🧬", "Therapeutic Class":"🧬", Geo:"📍", "Region / Governorate":"📍", Origin:"🌍", "Manufacturers Origin":"🌍", MarketGroup:"🔗", Status:"📋", NFC:"📋", "Product Details":"💊"};
          var GRP_BG     = {Product:"#f5f3ff", "Market Details":"#f5f3ff", ATC:"#ede9fe", "ATC/Molecule":"#ede9fe", "Therapeutic Class":"#ede9fe", Geo:"#fffbeb", "Region / Governorate":"#fffbeb", Origin:"#ecfeff", "Manufacturers Origin":"#ecfeff", MarketGroup:"#fdf2f8", Status:"#fef2f2", NFC:"#fdf2f8", "Product Details":"#ecfdf5"};
          var groups = {};
          studyTypeConfig.features.forEach(function(f) {
            /* Skip tenderStatus in feature list — it's handled by the Period section */
            if (f.key === "tenderStatus" || f.key === "TenderStatus") return;
            var g = f.group || "Other";
            if (!groups[g]) groups[g] = [];
            groups[g].push(f);
          });
          var groupKeys = Object.keys(groups);
          /* Generic setter */
          var setDynVal = function(fkey, val) { setSel(function(p) { var n = Object.assign({}, p); n[fkey] = val; n.periodKeys = []; return n; }); };

          return groupKeys.map(function(gk) {
            var feats = groups[gk];
            var color = GRP_COLORS[gk] || "#64748b";
            var icon  = GRP_ICONS[gk]  || "📋";
            var bg    = GRP_BG[gk]     || "#f8fafc";
            /* Count = filter values selected + features added to pivot (showCols).
               Mirrors the legacy private-study section highlighting so Kimadia/public
               studies get the same visual feedback when features are checked. */
            var _scSetK = (sel.showCols || []);
            var cnt   = feats.reduce(function(s, f) {
              var fvCnt = (sel[f.key] && sel[f.key].length) || 0;
              var atcCnt = (sel.atcFilters && (sel.atcFilters[f.key.toUpperCase()] || sel.atcFilters[f.label]) || []).length;
              var inPivot = _scSetK.indexOf(f.key) >= 0 ? 1 : 0;
              return s + fvCnt + atcCnt + inPivot;
            }, 0);
            var secId = "dyn_" + gk;
            /* Check if section is ATC-type (uses ATC multi-level layout) */
            var isATC = gk === "ATC" || gk === "ATC/Molecule" || gk === "Therapeutic Class";

            return (
              <FilterSection key={secId} id={secId} isOpen={openSecs[secId] === true} onToggle={togSec} color={color} title={gk} cnt={cnt}>
                {isATC ? (
                  /* ATC-style: checkbox + multiselect per level */
                  feats.map(function(f) {
                    var chk = sel.showCols.includes(f.key);
                    var dimOrder = chk ? sel.showCols.indexOf(f.key) + 1 : 0;
                    var opts = (dimValues && dimValues[f.key]) ? dimValues[f.key] : [];
                    
                    var selVals = (sel.atcFilters && sel.atcFilters[f.key.toUpperCase()]) ? sel.atcFilters[f.key.toUpperCase()] : ((sel.atcFilters && sel.atcFilters[f.label]) ? sel.atcFilters[f.label] : []);
                    return (
                      <div key={f.key} style={{marginBottom:10}}>
                        <div onClick={function(){togShow(f.key);}}
                          style={{display:"flex",alignItems:"center",gap:6,padding:"3px 0",cursor:"pointer",userSelect:"none",marginBottom:4}}>
                          <div style={{width:13,height:13,borderRadius:3,border:"1.5px solid "+(chk?color:"#cbd5e1"),
                                       background:chk?color:"#fff",display:"flex",alignItems:"center",
                                       justifyContent:"center",fontSize:8,color:"#fff",flexShrink:0}}>{chk?dimOrder:""}</div>
                          <span style={{fontSize:11,fontWeight:chk?700:500,color:chk?color:"#64748b",flex:1}}>{f.label}</span>
                          {selVals.length > 0
                            ? <span style={{fontSize:8,background:"#eff6ff",color:"#3b82f6",borderRadius:10,padding:"1px 6px",fontWeight:800}}>{selVals.length}</span>
                            : opts.length > 0 && <span style={{fontSize:8,fontWeight:700,color:"#3b82f6",background:"#eff6ff",borderRadius:8,padding:"1px 5px",minWidth:16,textAlign:"center"}}>{opts.length}</span>
                          }
                        </div>
                        {opts.length > 0 && (
                          <MultiSelect label={"Filter " + f.label} options={opts} value={selVals}
                            onChange={function(v){ setAtcFilter(f.key.toUpperCase(), v); }} accent={color} bg={bg} valueMap={valueMaps[f.key]} />
                        )}
                      </div>
                    );
                  })
                ) : (
                  /* Standard: FilterFeatRow per feature */
                  feats.map(function(f) {
                    var opts = (dimValues && dimValues[f.key]) ? dimValues[f.key] : [];
                    var val  = sel[f.key] || [];
                    return (
                      <FilterFeatRow key={f.key} mp={mp} dimAllowed={function(){return true;}} showCols={sel.showCols}
                        parentDim={parentDim} childDim={childDim} dims={dims} togShow={togShow} reorderShow={reorderShow}
                        fkey={f.key} label={f.label} opts={opts} val={val}
                        setVal={function(v){ setDynVal(f.key, v); }} accent={color} bg={bg} />
                    );
                  })
                )}
              </FilterSection>
            );
          });
        })()}

        {/* ══════ LEGACY HARDCODED SECTIONS (private studies — cascading filters) ══════ */}
        {(!studyTypeConfig || (studyTypeConfig.category !== "public" && studyTypeConfig.code !== "kimadia")) && (<>

        <FilterSection id="Market" isOpen={openSecs.Market} onToggle={togSec} color="#9333ea" title={"Market Features"} cnt={mktSel + mktFeat}>
          {/* Sales Tracker: the flow split (Sell-In / Sell-Out / FOC). Rendered only
              when the study's data carries the column (mp.flow), like every optional dim. */}
          {/* Flow (Sell-In / Sell-Out) intentionally NOT a filter row — it is
              selected in the Period section and rendered as header bands. */}
          {/* Medical & Pharma Intelligence: the Pharma/Rx dataset split of the merged
              market — a first-class dimension + filter, like Flow on Sales Tracker. */}
          {mp.source && <FilterFeatRow mp={mp} dimAllowed={dimAllowed} showCols={sel.showCols} parentDim={parentDim} childDim={childDim} dims={dims} togShow={togShow} reorderShow={reorderShow} valueMaps={valueMaps} fkey="source" label="Dataset (Pharma / Rx)" opts={sourceOpts} val={sel.source||[]} setVal={setSource} accent="#0F6E56" bg="#e4f7f0" />}
          <FilterFeatRow mp={mp} dimAllowed={dimAllowed} showCols={sel.showCols} parentDim={parentDim} childDim={childDim} dims={dims} togShow={togShow} reorderShow={reorderShow} valueMaps={valueMaps} fkey="manufacturer" label="Manufacturer" opts={mfrOpts} val={sel.manufacturer} setVal={setMfr} accent="#ea580c" bg="#fff7ed" onSearch={makeSearch("manufacturer")} />
          {mp.subManufacturer && <FilterFeatRow mp={mp} dimAllowed={dimAllowed} showCols={sel.showCols} parentDim={parentDim} childDim={childDim} dims={dims} togShow={togShow} reorderShow={reorderShow} valueMaps={valueMaps} fkey="subManufacturer" label="Sub-Manufacturer" opts={subMfrOpts} val={sel.subManufacturer||[]} setVal={setSubMfr} accent="#c2410c" bg="#fff7ed" onSearch={makeSearch("subManufacturer")} />}
          <FilterFeatRow mp={mp} dimAllowed={dimAllowed} showCols={sel.showCols} parentDim={parentDim} childDim={childDim} dims={dims} togShow={togShow} reorderShow={reorderShow} valueMaps={valueMaps} fkey="medicine"     label="Product Name"     opts={medOpts} val={sel.medicine}     setVal={setMed} accent="#16a34a" bg="#f0fdf4" onSearch={makeSearch("medicine")} />
          {mp.subProduct && <FilterFeatRow mp={mp} dimAllowed={dimAllowed} showCols={sel.showCols} parentDim={parentDim} childDim={childDim} dims={dims} togShow={togShow} reorderShow={reorderShow} valueMaps={valueMaps} fkey="subProduct" label="Sub-Product" opts={subProdOpts} val={sel.subProduct||[]} setVal={setSubProd} accent="#15803d" bg="#f0fdf4" onSearch={makeSearch("subProduct")} />}
          {mp.productSource && <FilterFeatRow mp={mp} dimAllowed={dimAllowed} showCols={sel.showCols} parentDim={parentDim} childDim={childDim} dims={dims} togShow={togShow} reorderShow={reorderShow} valueMaps={valueMaps} fkey="productSource" label="Product Source" opts={prodSrcOpts} val={sel.productSource||[]} setVal={setProdSrc} accent="#047857" bg="#f0fdf4" />}
          <FilterFeatRow mp={mp} dimAllowed={dimAllowed} showCols={sel.showCols} parentDim={parentDim} childDim={childDim} dims={dims} togShow={togShow} reorderShow={reorderShow} valueMaps={valueMaps} fkey="sku"          label="SKU"          opts={skuOpts} val={sel.sku}          setVal={setSku} accent="#0284c7" bg="#f0f9ff" onSearch={makeSearch("sku")} />
          <FilterFeatRow mp={mp} dimAllowed={dimAllowed} showCols={sel.showCols} parentDim={parentDim} childDim={childDim} dims={dims} togShow={togShow} reorderShow={reorderShow} valueMaps={valueMaps} fkey="agent"        label="Agent"        opts={agtOpts} val={sel.agent}        setVal={setAgt} accent="#ca8a04" bg="#fefce8" onSearch={makeSearch("agent")} />
          {mp.orderNo && <FilterFeatRow mp={mp} dimAllowed={dimAllowed} showCols={sel.showCols} parentDim={parentDim} childDim={childDim} dims={dims} togShow={togShow} reorderShow={reorderShow} valueMaps={valueMaps} fkey="orderNo" label="Order No" opts={orderNoOpts} val={sel.orderNo||[]} setVal={setOrderNo} accent="#7c3aed" bg="#f5f3ff" onSearch={makeSearch("orderNo")} />}
          {mp.LaunchDate && <FilterFeatRow mp={mp} dimAllowed={dimAllowed} showCols={sel.showCols} parentDim={parentDim} childDim={childDim} dims={dims} togShow={togShow} reorderShow={reorderShow} valueMaps={valueMaps} fkey="LaunchDate" label="Launch Date" opts={launchDateOpts} val={sel.LaunchDate||[]} setVal={setLaunchDate} accent="#0d9488" bg="#f0fdfa" onSearch={makeSearch("LaunchDate")} />}
          {/* Product Details (Form, Pack, Strength, Alone/Combined) — medical studies */}
          {mp.form && <FilterFeatRow mp={mp} dimAllowed={dimAllowed} showCols={sel.showCols} parentDim={parentDim} childDim={childDim} dims={dims} togShow={togShow} reorderShow={reorderShow} valueMaps={valueMaps} fkey="form" label="Form" opts={formOpts} val={sel.form||[]} setVal={setForm} accent="#ea580c" bg="#fff7ed" onSearch={makeSearch("form")} />}
          {mp.pack && <FilterFeatRow mp={mp} dimAllowed={dimAllowed} showCols={sel.showCols} parentDim={parentDim} childDim={childDim} dims={dims} togShow={togShow} reorderShow={reorderShow} valueMaps={valueMaps} fkey="pack" label="Pack Size" opts={packOpts} val={sel.pack||[]} setVal={setPack} accent="#f97316" bg="#fff7ed" />}
          {mp.strength && <FilterFeatRow mp={mp} dimAllowed={dimAllowed} showCols={sel.showCols} parentDim={parentDim} childDim={childDim} dims={dims} togShow={togShow} reorderShow={reorderShow} valueMaps={valueMaps} fkey="strength" label="Strength" opts={strengthOpts} val={sel.strength||[]} setVal={setStrength} accent="#fb923c" bg="#fff7ed" />}
          {mp.aloneCombined && <FilterFeatRow mp={mp} dimAllowed={dimAllowed} showCols={sel.showCols} parentDim={parentDim} childDim={childDim} dims={dims} togShow={togShow} reorderShow={reorderShow} valueMaps={valueMaps} fkey="aloneCombined" label="Alone/Combined" opts={aloneOpts} val={sel.aloneCombined||[]} setVal={setAlone} accent="#c2410c" bg="#fff7ed" />}
          {!mp.manufacturer && !mp.medicine && !mp.sku && !mp.agent &&
            <p style={{fontSize:10, color:"#94a3b8", margin:0}}>No market columns mapped</p>}
          {mp.manufacturer && !dimAllowed("manufacturer") && mp.medicine && !dimAllowed("medicine") && mp.sku && !dimAllowed("sku") && mp.agent && !dimAllowed("agent") &&
            <p style={{fontSize:10, color:"#f59e0b", margin:0}}>🔒 Market dimensions restricted by permissions</p>}
        </FilterSection>

        {(mp.atc1 || mp.atc2 || mp.atc3 || mp.atc4 || mp.molecule) && (() => {
          const atcTotalSel = Object.values(sel.atcFilters || {}).reduce((s,v) => s + (v ? v.length : 0), 0);
          const ATC_LEVELS = [
            {key:"atc1",  lvl:"ATC1",     label:"ATC 1",    accent:"#7c3aed", bg:"#f5f3ff"},
            {key:"atc2",  lvl:"ATC2",     label:"ATC 2",    accent:"#6d28d9", bg:"#ede9fe"},
            {key:"atc3",  lvl:"ATC3",     label:"ATC 3",    accent:"#5b21b6", bg:"#ddd6fe"},
            {key:"atc4",  lvl:"ATC4",     label:"ATC 4",    accent:"#4c1d95", bg:"#c4b5fd"},
            {key:"molecule",lvl:"MOLECULE",label:"Molecule", accent:"#7e22ce", bg:"#f3e8ff"},
          ];
          var visibleATC = ATC_LEVELS.filter(a => mp[a.key] && dimAllowed(a.key));
          if (!visibleATC.length) return null;
          return (
            <FilterSection id="ATC" isOpen={openSecs.ATC} onToggle={togSec} color="#7c3aed" title={"ATC / Molecule"} cnt={atcTotalSel + atcFeat}>
              {visibleATC.map(a => {
                const chk      = sel.showCols.includes(a.key);
                const lvlIdx   = dims.indexOf(a.key);
                const lvlNum   = lvlIdx >= 0 ? lvlIdx + 1 : null;
                const LCOLORS  = ["#1a4068","#1e5082","#2d6a8f","#3d7a9c","#4d8aa8","#64748b"];
                const lvlColor = lvlNum ? (LCOLORS[lvlIdx] || "#64748b") : null;
                const opts     = atcOpts[a.lvl] || [];
                
                const selVals  = (sel.atcFilters && sel.atcFilters[a.lvl]) ? sel.atcFilters[a.lvl] : [];
                return (
                  <div key={a.key} style={__PT?{display:"flex", alignItems:"center", gap:9, padding:"5px 0"}:{marginBottom:10}}>
                    {/* Checkbox row — toggles pivot dimension */}
                    <div onClick={() => togShow(a.key)}
                      style={{display:"flex",alignItems:"center",gap:__PT?9:6,padding:"3px 0",cursor:"pointer",userSelect:"none",marginBottom:__PT?0:4,flex:__PT?1:"none",minWidth:0}}>
                      <div style={{width:__PT?19:13,height:__PT?19:13,borderRadius:__PT?6:3,border:"1.5px solid "+(chk?(__PT?__PT.acc:"#2563eb"):(__PT?"rgba(255,255,255,.3)":"#cbd5e1")),
                                   background:chk?(__PT?__PT.acc:"#2563eb"):(__PT?"transparent":"#fff"),display:"flex",alignItems:"center",
                                   justifyContent:"center",fontSize:__PT?10:8,fontWeight:__PT?800:400,color:__PT?__PT.accTx:"#fff",flexShrink:0}}>{chk?(sel.showCols.indexOf(a.key)+1):""}</div>
                      <span style={{fontSize:__PT?12.5:11,fontWeight:chk?700:500,color:__PT?"#E9F4F5":(chk?"#2563eb":"#64748b"),
                                    letterSpacing:__PT?"0":".04em",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{a.label}</span>
                      {!__PT && (selVals.length > 0
                        ? <span style={{fontSize:8,background:"#eff6ff",color:"#3b82f6",borderRadius:10,padding:"1px 6px",fontWeight:800}}>{selVals.length}</span>
                        : opts.length > 0 && <span style={{fontSize:8,fontWeight:700,color:"#3b82f6",background:"#eff6ff",borderRadius:8,padding:"1px 5px",minWidth:16,textAlign:"center"}}>{opts.length}</span>
                      )}
                    </div>
                    {/* Value filter MultiSelect */}
                    <MultiSelect compact={!!__PT}
                      label={"Filter " + a.label}
                      options={opts}
                      value={selVals}
                      onChange={v => setAtcFilter(a.lvl, v)}
                      accent={a.accent}
                      bg={a.bg}
                      valueMap={valueMaps[a.key]}
                      /* Server search — the ATC levels and Molecule were the only dims
                         WITHOUT it, so they could search no further than the cascaded
                         option list, which cascaded-dims caps at TOP 1000 for molecule.
                         Any molecule past that cap was simply unreachable. search-dim
                         already supports atc1-4 + molecule and self-skips the searched
                         dimension, so this needs no server change. */
                      onSearch={makeSearch(a.key)}
                    />
                  </div>
                );
              })}
            </FilterSection>
          );
        })()}

        {/* ── NFC section (same pattern as ATC) ── */}
        {(mp.nfc || mp.nfc2 || mp.nfc3) && (() => {
          const nfcTotalSel = Object.values(sel.nfcFilters || {}).reduce((s,v) => s + (v ? v.length : 0), 0);
          const NFC_LEVELS = [
            {key:"nfc",  lvl:"NFC",  label:"NFC",   accent:"#0891b2", bg:"#ecfeff"},
            {key:"nfc2", lvl:"NFC2", label:"NFC 2", accent:"#0e7490", bg:"#e0f7fa"},
            {key:"nfc3", lvl:"NFC3", label:"NFC 3", accent:"#155e75", bg:"#cffafe"},
          ];
          var visibleNFC = NFC_LEVELS.filter(n => mp[n.key] && dimAllowed(n.key));
          if (!visibleNFC.length) return null;
          return (
            <FilterSection id="NFC" isOpen={openSecs.NFC} onToggle={togSec} color="#0891b2" title={"NFC"} cnt={nfcTotalSel + nfcFeat}>
              {visibleNFC.map(n => {
                const chk      = sel.showCols.includes(n.key);
                const lvlIdx   = dims.indexOf(n.key);
                const lvlNum   = lvlIdx >= 0 ? lvlIdx + 1 : null;
                const LCOLORS  = ["#0891b2","#0e7490","#155e75"];
                const lvlColor = lvlNum ? (LCOLORS[lvlIdx] || "#64748b") : null;
                const opts     = nfcOpts[n.lvl] || [];
                
                const selVals  = (sel.nfcFilters && sel.nfcFilters[n.lvl]) ? sel.nfcFilters[n.lvl] : [];
                return (
                  <div key={n.key} style={__PT?{display:"flex", alignItems:"center", gap:9, padding:"5px 0"}:{marginBottom:10}}>
                    <div onClick={() => togShow(n.key)}
                      style={{display:"flex",alignItems:"center",gap:__PT?9:6,padding:"3px 0",cursor:"pointer",userSelect:"none",marginBottom:__PT?0:4,flex:__PT?1:"none",minWidth:0}}>
                      <div style={{width:__PT?19:13,height:__PT?19:13,borderRadius:__PT?6:3,border:"1.5px solid "+(chk?(__PT?__PT.acc:"#2563eb"):(__PT?"rgba(255,255,255,.3)":"#cbd5e1")),
                                   background:chk?(__PT?__PT.acc:"#2563eb"):(__PT?"transparent":"#fff"),display:"flex",alignItems:"center",
                                   justifyContent:"center",fontSize:__PT?10:8,fontWeight:__PT?800:400,color:__PT?__PT.accTx:"#fff",flexShrink:0}}>{chk?(sel.showCols.indexOf(n.key)+1):""}</div>
                      <span style={{fontSize:__PT?12.5:11,fontWeight:chk?700:500,color:__PT?"#E9F4F5":(chk?"#2563eb":"#64748b"),
                                    letterSpacing:__PT?"0":".04em",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{n.label}</span>
                      {!__PT && (selVals.length > 0
                        ? <span style={{fontSize:8,background:"#eff6ff",color:"#3b82f6",borderRadius:10,padding:"1px 6px",fontWeight:800}}>{selVals.length}</span>
                        : opts.length > 0 && <span style={{fontSize:8,fontWeight:700,color:"#3b82f6",background:"#eff6ff",borderRadius:8,padding:"1px 5px",minWidth:16,textAlign:"center"}}>{opts.length}</span>
                      )}
                    </div>
                    <MultiSelect compact={!!__PT}
                      label={"Filter " + n.label}
                      options={opts}
                      value={selVals}
                      onChange={v => setNfcFilter(n.lvl, v)}
                      accent={n.accent}
                      bg={n.bg}
                      valueMap={valueMaps[n.key]}
                      /* Same gap as the ATC levels: NFC rows had no server search, so
                         they could only reach the cascaded option list (TOP 500). */
                      onSearch={makeSearch(n.key)}
                    />
                  </div>
                );
              })}
            </FilterSection>
          );
        })()}

        {/* ── MEDICAL: Doctor Details ── */}
        {!_isMsaP && (mp.docRegion || mp.docSpec || mp.docCity) && (
        <FilterSection id="Doctor" isOpen={openSecs.Doctor} onToggle={togSec} color="#1d4ed8" title={_isMsaP ? "Doctor Details (Rx)" : "Doctor Details"} cnt={(sel.docRegion||[]).length + (sel.docSpec||[]).length + (sel.docCity||[]).length + docFeat}>
          {mp.docRegion && <FilterFeatRow mp={mp} dimAllowed={dimAllowed} showCols={sel.showCols} parentDim={parentDim} childDim={childDim} dims={dims} togShow={togShow} reorderShow={reorderShow} valueMaps={valueMaps} fkey="docRegion" label="Doctor Region" opts={docRegOpts} val={sel.docRegion||[]} setVal={setDocReg} accent="#1d4ed8" bg="#eff6ff" onSearch={makeSearch("docRegion")} />}
          {mp.docSpec && <FilterFeatRow mp={mp} dimAllowed={dimAllowed} showCols={sel.showCols} parentDim={parentDim} childDim={childDim} dims={dims} togShow={togShow} reorderShow={reorderShow} valueMaps={valueMaps} fkey="docSpec" label="Doctor Specialty" opts={docSpecOpts} val={sel.docSpec||[]} setVal={setDocSpec} accent="#2563eb" bg="#eff6ff" onSearch={makeSearch("docSpec")} />}
          {mp.docCity && <FilterFeatRow mp={mp} dimAllowed={dimAllowed} showCols={sel.showCols} parentDim={parentDim} childDim={childDim} dims={dims} togShow={togShow} reorderShow={reorderShow} valueMaps={valueMaps} fkey="docCity" label="Doc City" opts={docCityOpts} val={sel.docCity||[]} setVal={setDocCity} accent="#3b82f6" bg="#eff6ff" onSearch={makeSearch("docCity")} />}
        </FilterSection>
        )}

        {/* ── MEDICAL: Pharmacy Details ── */}
        {!_isMsaP && (mp.phRegion || mp.phCity) && (
        <FilterSection id="Pharmacy" isOpen={openSecs.Pharmacy} onToggle={togSec} color="#059669" title={_isMsaP ? "Pharmacy Details (Rx)" : "Pharmacy Details"} cnt={(sel.phRegion||[]).length + (sel.phCity||[]).length + phFeat}>
          {mp.phRegion && <FilterFeatRow mp={mp} dimAllowed={dimAllowed} showCols={sel.showCols} parentDim={parentDim} childDim={childDim} dims={dims} togShow={togShow} reorderShow={reorderShow} valueMaps={valueMaps} fkey="phRegion" label="Pharmacy Region" opts={phRegOpts} val={sel.phRegion||[]} setVal={setPhReg} accent="#059669" bg="#ecfdf5" onSearch={makeSearch("phRegion")} />}
          {mp.phCity && <FilterFeatRow mp={mp} dimAllowed={dimAllowed} showCols={sel.showCols} parentDim={parentDim} childDim={childDim} dims={dims} togShow={togShow} reorderShow={reorderShow} valueMaps={valueMaps} fkey="phCity" label="Ph. City" opts={phCityOpts} val={sel.phCity||[]} setVal={setPhCity} accent="#10b981" bg="#ecfdf5" onSearch={makeSearch("phCity")} />}
        </FilterSection>
        )}

        {/* ── MEDICAL: Patient Details ── */}
        {!_isMsaP && (mp.patientAge || mp.patientGender) && (
        <FilterSection id="Patient" isOpen={openSecs.Patient} onToggle={togSec} color="#7c3aed" title={"Patient Details"} cnt={(sel.patientAge||[]).length + (sel.patientGender||[]).length + patFeat}>
          {mp.patientAge && <FilterFeatRow mp={mp} dimAllowed={dimAllowed} showCols={sel.showCols} parentDim={parentDim} childDim={childDim} dims={dims} togShow={togShow} reorderShow={reorderShow} valueMaps={valueMaps} fkey="patientAge" label="Patient Age" opts={patAgeOpts} val={sel.patientAge||[]} setVal={setPatAge} accent="#7c3aed" bg="#f5f3ff" />}
          {mp.patientGender && <FilterFeatRow mp={mp} dimAllowed={dimAllowed} showCols={sel.showCols} parentDim={parentDim} childDim={childDim} dims={dims} togShow={togShow} reorderShow={reorderShow} valueMaps={valueMaps} fkey="patientGender" label="Patient Gender" opts={patGenOpts} val={sel.patientGender||[]} setVal={setPatGen} accent="#8b5cf6" bg="#f5f3ff" />}
        </FilterSection>
        )}

        {/* ── MEDICAL: Indication / Diagnosis ── */}
        {!_isMsaP && mp.diagnosis && (
        <FilterSection id="Indication" isOpen={openSecs.Indication} onToggle={togSec} color="#dc2626" title={"Indication"} cnt={(sel.diagnosis||[]).length + indFeat}>
          <FilterFeatRow mp={mp} dimAllowed={dimAllowed} showCols={sel.showCols} parentDim={parentDim} childDim={childDim} dims={dims} togShow={togShow} reorderShow={reorderShow} valueMaps={valueMaps} fkey="diagnosis" label="Diagnosis" opts={diagOpts} val={sel.diagnosis||[]} setVal={setDiag} accent="#dc2626" bg="#fef2f2" onSearch={makeSearch("diagnosis")} />
        </FilterSection>
        )}

        {(_isMsaP ? (mp.docSalesRegion || mp.phSalesRegion) : ((mp.region || mp.governorate) && (dimAllowed("region") || dimAllowed("governorate")))) && (
        <FilterSection id="Geo" isOpen={openSecs.Geo} onToggle={togSec} color="#b45309" title={_isMsaP ? "Region" : "Region / Governorate"} cnt={geoSel + geoFeat}>
          {/* msa merged market: TWO combined region concepts replace the separate
              sales/doctor/pharmacy region rows — each reads the Doctor (or Pharmacy)
              region on Rx rows and the sales Region on Pharma rows, so a region
              pivot compares prescribing / dispensing vs sales on the SAME rows. */}
          {_isMsaP && mp.docSalesRegion && <FilterFeatRow mp={mp} dimAllowed={dimAllowed} showCols={sel.showCols} parentDim={parentDim} childDim={childDim} dims={dims} togShow={togShow} reorderShow={reorderShow} valueMaps={valueMaps} fkey="docSalesRegion" label={"Doctor\u2013Sales Region"} opts={dsrOpts} val={sel.docSalesRegion||[]} setVal={setDsr} accent="#1d4ed8" bg="#eff6ff" onSearch={makeSearch("docSalesRegion")} />}
          {_isMsaP && mp.phSalesRegion && <FilterFeatRow mp={mp} dimAllowed={dimAllowed} showCols={sel.showCols} parentDim={parentDim} childDim={childDim} dims={dims} togShow={togShow} reorderShow={reorderShow} valueMaps={valueMaps} fkey="phSalesRegion" label={"Pharmacy\u2013Sales Region"} opts={psrOpts} val={sel.phSalesRegion||[]} setVal={setPsr} accent="#059669" bg="#ecfdf5" onSearch={makeSearch("phSalesRegion")} />}
          {!_isMsaP && <FilterFeatRow mp={mp} dimAllowed={dimAllowed} showCols={sel.showCols} parentDim={parentDim} childDim={childDim} dims={dims} togShow={togShow} reorderShow={reorderShow} valueMaps={valueMaps} fkey="region"      label="Region"      opts={regOpts} val={sel.region}      setVal={setReg} accent="#b45309" bg="#fffbeb" />}
          {!_isMsaP && <FilterFeatRow mp={mp} dimAllowed={dimAllowed} showCols={sel.showCols} parentDim={parentDim} childDim={childDim} dims={dims} togShow={togShow} reorderShow={reorderShow} valueMaps={valueMaps} fkey="governorate" label="Governorate" opts={govOpts} val={sel.governorate} setVal={setGov} accent="#92400e" bg="#fef3c7" />}
          {!mp.region && !mp.governorate &&
            <p style={{fontSize:10, color:"#94a3b8", margin:0}}>No region/governorate columns mapped</p>}
        </FilterSection>
        )}

        {/* ── SALES TRACKER: Pharmacy Panel (class A-D = panel weight) ── */}
        {mp.pharmacyClass && (
        <FilterSection id="PhPanel" isOpen={openSecs.PhPanel} onToggle={togSec} color="#0F6E56" title={"Pharmacy Panel"} cnt={(sel.pharmacyClass||[]).length}>
          <FilterFeatRow mp={mp} dimAllowed={dimAllowed} showCols={sel.showCols} parentDim={parentDim} childDim={childDim} dims={dims} togShow={togShow} reorderShow={reorderShow} valueMaps={valueMaps} fkey="pharmacyClass" label="Pharmacy Class" opts={phcOpts} val={sel.pharmacyClass||[]} setVal={setPhc} accent="#0F6E56" bg="#e4f7f0" />
          <p style={{fontSize:8.5, color:"#94a3b8", margin:"4px 0 0"}}>
            Panel classes (A{"–"}D). Add as a pivot dimension with the # Pharmacies measure to see the panel weight per class.
          </p>
        </FilterSection>
        )}

        {(mp.continent || mp.subContinent || mp.countryOfOrigin || mp.manufacturerType || mp.localImported) &&
         (dimAllowed("continent") || dimAllowed("subContinent") || dimAllowed("countryOfOrigin") || dimAllowed("manufacturerType") || dimAllowed("localImported")) && (
        <FilterSection id="MfrOrigin" isOpen={openSecs.MfrOrigin} onToggle={togSec} color="#0e7490" title={"Manufacturers Origin"} cnt={mfrOriginSel + mfrOFeat}>
          <FilterFeatRow mp={mp} dimAllowed={dimAllowed} showCols={sel.showCols} parentDim={parentDim} childDim={childDim} dims={dims} togShow={togShow} reorderShow={reorderShow} valueMaps={valueMaps} fkey="continent"       label="Continent"         opts={cntOpts}  val={sel.continent}       setVal={setCnt}  accent="#0e7490" bg="#ecfeff" />
          <FilterFeatRow mp={mp} dimAllowed={dimAllowed} showCols={sel.showCols} parentDim={parentDim} childDim={childDim} dims={dims} togShow={togShow} reorderShow={reorderShow} valueMaps={valueMaps} fkey="subContinent"    label="Sub-Continent"     opts={scntOpts} val={sel.subContinent}    setVal={setScnt} accent="#0891b2" bg="#ecfeff" />
          <FilterFeatRow mp={mp} dimAllowed={dimAllowed} showCols={sel.showCols} parentDim={parentDim} childDim={childDim} dims={dims} togShow={togShow} reorderShow={reorderShow} valueMaps={valueMaps} fkey="countryOfOrigin" label="Country of Origin" opts={cooOpts}  val={sel.countryOfOrigin} setVal={setCoo}  accent="#06b6d4" bg="#ecfeff" />
          <FilterFeatRow mp={mp} dimAllowed={dimAllowed} showCols={sel.showCols} parentDim={parentDim} childDim={childDim} dims={dims} togShow={togShow} reorderShow={reorderShow} valueMaps={valueMaps} fkey="manufacturerType"   label="Manufacturer Type" opts={goOpts}   val={sel.manufacturerType}   setVal={setGo}   accent="#155e75" bg="#ecfeff" />
          <FilterFeatRow mp={mp} dimAllowed={dimAllowed} showCols={sel.showCols} parentDim={parentDim} childDim={childDim} dims={dims} togShow={togShow} reorderShow={reorderShow} valueMaps={valueMaps} fkey="localImported"   label="Local / Imported"  opts={liOpts}   val={sel.localImported}   setVal={setLi}   accent="#164e63" bg="#ecfeff" />
        </FilterSection>
        )}

        </>)}
        {/* ══════ END LEGACY / DYNAMIC SECTIONS ══════ */}

        <FilterSection id="Period" isOpen={openSecs.Period} onToggle={togSec} color="#dc2626" title={"Period"} cnt={(sel.periodMode?1:0) + sel.periodKeys.length}>
          {["Yearly","Quarterly","Monthly"].indexOf(sel.periodMode) >= 0 && (sel.periodKeys || []).length > 1 && !mp.flow && (
            <div onClick={() => setSel(p => ({...p, periodTotal: !p.periodTotal}))}
              title="Adds one extra column band that sums the selected periods, with Share / Rank / Growth computed on the total (growth compares vs the same periods last year)"
              style={{display:"flex",alignItems:"center",gap:6,padding:"5px 9px",marginBottom:7,borderRadius:7,border:"1.5px solid "+(sel.periodTotal?"#fbbf24":"#e2e8f0"),background:sel.periodTotal?"#fffbeb":"#fff",cursor:"pointer",userSelect:"none"}}>
              <span style={{fontSize:11,color:sel.periodTotal?"#d97706":"#94a3b8"}}>{sel.periodTotal?"\u2611":"\u2610"}</span>
              <span style={{fontSize:10,fontWeight:700,color:sel.periodTotal?"#92400e":"#475569"}}>{"\u03A3 Total of selected periods"}</span>
              <span style={{fontSize:8.5,color:"#94a3b8"}}>{"+1 column band with statistics"}</span>
            </div>
          )}
          {!mp.yearCol ? (
            <p style={{fontSize:10, color:"#f87171", margin:0}}>⚠ Map a Year column first</p>
          ) : mp._periodMode === "tender" ? (
            /* ── Kimadia: Year labels + Status checkboxes ── */
            (() => {
              var tpOpts = (dimValues && dimValues.tenderPeriods) || [];
              var availYears = []; var availStatuses = [];
              var seenY = {}; var seenS = {};
              tpOpts.forEach(function(tp) {
                if (!seenY[tp.year]) { availYears.push(tp.year); seenY[tp.year] = 1; }
                if (!seenS[tp.status]) { availStatuses.push(tp.status); seenS[tp.status] = 1; }
              });
              if (!availYears.length && dimValues && dimValues.years) availYears = dimValues.years.slice();
              availYears.sort(function(a,b){ return b - a; });

              /* Parse selection */
              var selYears = []; var selStatuses = [];
              var _seenSY = {}; var _seenSS = {};
              (sel.periodKeys || []).forEach(function(pk) {
                var parts = pk.split("_"); if (parts.length < 2) return;
                var y = parts[0]; var s = parts.slice(1).join("_");
                if (!_seenSY[y]) { selYears.push(y); _seenSY[y] = 1; }
                if (!_seenSS[s]) { selStatuses.push(s); _seenSS[s] = 1; }
              });

              var rebuildKeys = function(years, statuses) {
                var keys = [];
                years.forEach(function(yr){ statuses.forEach(function(st){ keys.push(yr+"_"+st); }); });
                S("periodMode", "TenderStatus"); S("periodKeys", keys);
              };

              var toggleYear = function(y) {
                var ny = selYears.indexOf(y) >= 0 ? selYears.filter(function(x){return x!==y;}) : selYears.concat([y]);
                rebuildKeys(ny, selStatuses.length ? selStatuses : availStatuses);
              };
              var toggleStatus = function(s) {
                var ns = selStatuses.indexOf(s) >= 0 ? selStatuses.filter(function(x){return x!==s;}) : selStatuses.concat([s]);
                if (ns.length === 0) return; /* Must keep at least one status checked */
                rebuildKeys(selYears.length ? selYears : [], ns);
              };

              var noStatusSelected = !selStatuses.length;

              return (
                <div>
                  {/* STATUS — mandatory checkboxes */}
                  <div style={{marginBottom:10}}>
                    <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4}}>
                      <span style={{fontSize:9, fontWeight:800, color:"#64748b", textTransform:"uppercase", letterSpacing:".05em"}}>Status</span>
                      {noStatusSelected && <span style={{fontSize:8, color:"#dc2626", fontWeight:700}}>⚠ Select at least one</span>}
                    </div>
                    <div style={{display:"flex", gap:6, flexWrap:"wrap"}}>
                      {availStatuses.map(function(s) {
                        var on = selStatuses.indexOf(s) >= 0;
                        var stCol = s === "Awarded" ? {bg:"#dcfce7",co:"#15803d",bd:"#15803d"} : s === "Delivered" ? {bg:"#fce7f3",co:"#be185d",bd:"#be185d"} : {bg:"#f1f5f9",co:"#475569",bd:"#94a3b8"};
                        return (
                          <div key={s} onClick={function(){ toggleStatus(s); }}
                            style={{display:"flex", alignItems:"center", gap:5, padding:"4px 10px", borderRadius:6,
                              cursor:"pointer", fontSize:10.5, fontWeight:on?700:500, color:on?stCol.co:"#94a3b8",
                              background:on?stCol.bg:"#f8fafc", border:"1.5px solid "+(on?stCol.bd:"#e2e8f0"), userSelect:"none",
                              transition:"all .15s"}}>
                            <div style={{width:12, height:12, borderRadius:3, border:"1.5px solid "+(on?stCol.bd:"#cbd5e1"),
                              background:on?stCol.bd:"#fff", display:"flex", alignItems:"center", justifyContent:"center",
                              fontSize:8, color:"#fff", flexShrink:0}}>{on?"✓":""}</div>
                            <span>{s}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {/* YEARS — label list like other studies */}
                  <div>
                    <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4}}>
                      <span style={{fontSize:9, fontWeight:800, color:"#64748b", textTransform:"uppercase", letterSpacing:".05em"}}>Years</span>
                      <span onClick={function(){
                        var allY = availYears.map(String);
                        var newY = selYears.length === allY.length ? [] : allY;
                        rebuildKeys(newY, selStatuses.length ? selStatuses : availStatuses);
                      }} style={{fontSize:8.5, color:"#2563eb", cursor:"pointer", fontWeight:700}}>
                        {selYears.length === availYears.length ? "Clear All" : "Select All"}
                      </span>
                    </div>
                    {!availYears.length && <p style={{fontSize:10, color:"#f87171", margin:0}}>No yearly periods found in data</p>}
                    <div style={{display:"flex", flexDirection:"column", gap:2, maxHeight:180, overflowY:"auto"}}>
                      {availYears.map(function(y) {
                        var on = selYears.indexOf(String(y)) >= 0;
                        return (
                          <div key={y} onClick={function(){ toggleYear(String(y)); }}
                            style={{display:"flex", alignItems:"center", gap:6, padding:"4px 8px", borderRadius:5,
                              cursor:"pointer", fontSize:11, fontWeight:on?700:400, color:on?"#dc2626":"#64748b",
                              background:on?"#fef2f2":"transparent", userSelect:"none", transition:"all .15s"}}>
                            <div style={{width:13, height:13, borderRadius:3, border:"1.5px solid "+(on?"#dc2626":"#cbd5e1"),
                              background:on?"#dc2626":"#fff", display:"flex", alignItems:"center", justifyContent:"center",
                              fontSize:8, color:"#fff", flexShrink:0}}>{on?"✓":""}</div>
                            <span>{y}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {noStatusSelected && (
                    <p style={{fontSize:10, color:"#dc2626", margin:"8px 0 0", fontWeight:600}}>⚠ Select at least one status to run query</p>
                  )}
                </div>
              );
            })()
          ) : (
            <>
              {/* ── Sales Tracker: Sell-In / Sell-Out flow checkboxes ──
                  Combine with EVERY period type below: each selected period
                  (year / quarter / month / MAT / YTD) splits into one column
                  band per checked flow. At least one flow stays checked. */}
              {mp.flow && (() => {
                var availFlows = (flowOpts && flowOpts.length) ? flowOpts.slice() : ["Sell-In", "Sell-Out"];
                var selFlows = sel.periodFlows === undefined ? availFlows.slice() : sel.periodFlows;
                var toggleFlow = function(fl) {
                  var nf = selFlows.indexOf(fl) >= 0 ? selFlows.filter(function(x){ return x !== fl; }) : selFlows.concat([fl]);
                  if (nf.length === 0) return; /* keep at least one flow checked */
                  S("periodFlows", nf);
                };
                return (
                  <div style={{marginBottom:8}}>
                    <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4}}>
                      <span style={{fontSize:9, fontWeight:800, color:"#64748b", textTransform:"uppercase", letterSpacing:".05em"}}>Flow</span>
                      <span style={{fontSize:8, color:"#94a3b8"}}>splits every period below</span>
                    </div>
                    <div style={{display:"flex", gap:6, flexWrap:"wrap"}}>
                      {availFlows.map(function(fl) {
                        var on = selFlows.indexOf(fl) >= 0;
                        var flCol = fl === "Sell-In" ? {bg:"#dbeafe", co:"#1d4ed8", bd:"#1d4ed8"} : fl === "Sell-Out" ? {bg:"#e4f7f0", co:"#0F6E56", bd:"#0F6E56"} : {bg:"#f1f5f9", co:"#475569", bd:"#94a3b8"};
                if (__PT) flCol = on ? {bg:__PT.acc, co:__PT.accTx, bd:__PT.acc} : {bg:"transparent", co:__PT.mut, bd:"rgba(255,255,255,.25)"};
                        return (
                          <div key={fl} onClick={function(){ toggleFlow(fl); }}
                            style={{display:"flex", alignItems:"center", gap:5, padding:"4px 10px", borderRadius:6,
                              cursor:"pointer", fontSize:10.5, fontWeight:on?700:500, color:on?flCol.co:(__PT?__PT.mut:"#94a3b8"),
                              background:on?flCol.bg:(__PT?"transparent":"#f8fafc"), border:"1.5px solid "+(on?flCol.bd:(__PT?"rgba(255,255,255,.25)":"#e2e8f0")), userSelect:"none",
                              transition:"all .15s"}}>
                            {!__PT && <div style={{width:12, height:12, borderRadius:3, border:"1.5px solid "+(on?flCol.bd:"#cbd5e1"),
                              background:on?flCol.bd:"#fff", display:"flex", alignItems:"center", justifyContent:"center",
                              fontSize:8, color:"#fff", flexShrink:0}}>{on?"✓":""}</div>}
                            <span>{fl}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
              <div style={{fontSize:__PT?8.5:9, padding:__PT?"0 2px":"3px 6px", background:__PT?"transparent":"#dcfce7", borderRadius:4, color:__PT?"rgba(240,246,250,.45)":"#166534", marginBottom:6, fontWeight:600}}>
                Mix period types freely — each type gets its own column band, and growth always compares within its own type (Q1 2026 vs Q4 2025 / Q1 2025, MAT vs previous MAT, ...)
              </div>
              {(function(){
                var _pModesAll = [
                {mode:"Yearly",    opts:pOpts.years,       dis:false,                        hint:"", perm:"yearly"},
                {mode:"Quarterly", opts:pOpts.quarters,    dis:!pOpts.quarters||!pOpts.quarters.length, hint:"needs quarter data", perm:"quarterly"},
                {mode:"Monthly",   opts:pOpts.months,      dis:!pOpts.months||!pOpts.months.length,     hint:"needs month data", perm:"monthly"},
                {mode:"MAT",       opts:pOpts.mats||[],    dis:!(pOpts.mats&&pOpts.mats.length),        hint:"needs 4+ quarters", perm:"quarterly"},
                {mode:"MAT Monthly",opts:pOpts.matMonths||[],dis:!(pOpts.matMonths&&pOpts.matMonths.length), hint:"needs 12+ months", perm:"monthly"},
                {mode:"YTD",       opts:pOpts.ytds||[],    dis:!(pOpts.ytds&&pOpts.ytds.length),        hint:"needs YTD data", perm:"quarterly"},
                {mode:"YTD Monthly",opts:pOpts.ytdMonthly||[], dis:!(pOpts.ytdMonthly&&pOpts.ytdMonthly.length), hint:"needs monthly data", perm:"monthly"},
              ];
                var _pModes = _pModesAll.filter(function(item){
                if (item.dis) return false;
                /* Period permission: combine client setting + study type data availability */
                var ppClient = (clientPerms && clientPerms.periodPermission) || "monthly";
                /* Tree-scoped: check studyAccess for active study type */
                if (user && user.studyAccess && activeStudyType) {
                  for (var _ppi = 0; _ppi < user.studyAccess.length; _ppi++) {
                    if (user.studyAccess[_ppi].studyTypeCode === activeStudyType && user.studyAccess[_ppi].periodPermission) {
                      ppClient = user.studyAccess[_ppi].periodPermission; break;
                    }
                  }
                }
                /* If study type config exists, derive max from period config columns */
                if (studyTypeConfig && studyTypeConfig.period) {
                  var pc = studyTypeConfig.period;
                  var hasQr = pc.quarterColumn || pc.QuarterColumn;
                  if (!hasQr) ppClient = "yearly"; /* no quarter column = yearly only */
                  /* Don't cap monthly here — actual data availability (pOpts.months) determines it */
                }
                /* Check if data has period columns */
                if (!mp.quarterCol && !mp.monthCol) ppClient = "yearly";
                /* jordan_medical: no monthly periods */
                if (activeStudyType === "jordan_medical") ppClient = "quarterly";
                var allowed = {yearly:["yearly"], quarterly:["yearly","quarterly"], quarterly_open:["yearly","quarterly"], semesterly:["yearly","quarterly"], monthly:["yearly","quarterly","monthly"]};   /* r108: quarterly_open = quarters incl. the open one (Q3 2026*) */
                return (allowed[ppClient] || allowed.monthly).indexOf(item.perm) >= 0;
              });
                /* r108: legend for the starred (open) quarter — only when such a quarter is offered */
                var _oq9 = null; try { _oq9 = window.__amsxOpenQ9 || null; } catch (eOq9) {}
                var _oqShown9 = !!(_oq9 && (pOpts.quarters || []).some(function(q9){ return q9.key === _oq9.key; }));
                var _oqNote9 = _oqShown9 ? (
                  <div key="oq-note" style={{fontSize:__PT?8.5:9, padding:"3px 6px", background:__PT?"transparent":"#fff7ed", border:__PT?"none":"1px solid #fed7aa", borderRadius:4, color:__PT?"rgba(240,246,250,.55)":"#9a3412", marginBottom:6, fontWeight:600}}>
                    {"* Q" + _oq9.quarter + " " + _oq9.year + " is in progress \u2014 " + _oq9.months + " of 3 months loaded (to " + ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][(_oq9.lastMonth || 1) - 1] + " " + _oq9.year + "); not comparable with full quarters"}
                  </div>
                ) : null;
                var _rowsJsx = _pModes.map(({mode, opts, dis, hint}) => {
                const isSel = sel.periodMode === mode;
                const allKs = opts.map(o => o.key);
                const selCnt = allKs.filter(k => sel.periodKeys.includes(k)).length;
                const modeLabel = mode === "MAT" ? "MAT (Quarterly)" : mode === "MAT Monthly" ? "MAT (Monthly)" : mode === "YTD" ? "YTD (Quarterly)" : mode === "YTD Monthly" ? "YTD (Monthly)" : mode;
                return (
                  <div key={mode} style={{marginBottom:6}}>
                    <div
                      onClick={() => !dis && setMode(mode)}
                      style={{display:"flex", alignItems:"center", gap:7, padding:"4px 6px", borderRadius:5,
                              cursor:dis?"not-allowed":"pointer", opacity:dis?0.45:1, userSelect:"none",
                              background:isSel?(__PT?__PT.accBgOn:"#eff6ff"):"transparent"}}
                    >
                      <div style={{width:11, height:11, borderRadius:"50%", border:"1.5px solid "+(isSel?(__PT?__PT.acc:"#2563eb"):(__PT?__PT.chkOff:"#cbd5e1")),
                                   background:isSel?(__PT?__PT.acc:"#2563eb"):(__PT?"transparent":"#fff"), flexShrink:0}} />
                      <span style={{fontSize:11.5, flex:1, fontWeight:isSel?700:400, color:isSel?(__PT?__PT.acc:"#1d4ed8"):(__PT?__PT.mut:"#64748b")}}>{modeLabel}</span>
                      {dis && <span style={{fontSize:9, color:"#f87171"}}>{hint}</span>}
                      {selCnt > 0 && <span style={{fontSize:9, fontWeight:800, background:"#fee2e2", color:"#dc2626", borderRadius:10, padding:"1px 5px"}}>{selCnt}</span>}
                    </div>
                    {isSel && (
                      <div style={{paddingLeft:18, marginTop:3}}>
                        {opts.length === 0 ? (
                          <div style={{fontSize:9.5, padding:"5px 7px", background:"#fef2f2", borderRadius:4, color:"#dc2626", fontWeight:600}}>
                            No {mode} periods available — need {mode==="MAT"?"4 consecutive quarters":mode==="MAT Monthly"?"12 consecutive months":"data starting from Q1"} in the dataset
                          </div>
                        ) : (
                          <>
                            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:3}}>
                              <span style={{fontSize:9, fontWeight:700, color:__PT?__PT.mut:"#94a3b8", textTransform:"uppercase"}}>
                                {mode === "MAT" ? "MAT Periods" : mode === "YTD" ? "YTD Quarterly" : mode === "YTD Monthly" ? "YTD Monthly" : mode.replace("ly","").replace("hly","")+"s"}
                              </span>
                              <button onClick={() => togAllK(allKs)}
                                style={{fontSize:9, color:__PT?__PT.acc:"#2563eb", background:"none", border:"none", cursor:"pointer", fontFamily:"inherit"}}>
                                {selCnt === allKs.length ? "Clear" : "All"}
                              </button>
                            </div>
                            {(mode === "MAT" || mode === "YTD" || mode === "YTD Monthly") && (
                              <div style={{fontSize:8.5, padding:"3px 6px", background:mode==="MAT"?"#fce7f3":"#dbeafe", borderRadius:4, color:mode==="MAT"?"#9d174d":"#1d4ed8", marginBottom:4, fontWeight:600}}>
                                {mode === "MAT" ? "Last 4 quarters (rolling 12 months)" : "Year-to-date accumulation"}
                              </div>
                            )}
                            <MultiSelect keepOrder label={modeLabel}
                              options={allKs.slice().reverse()}
                              labelMap={(function(){ var _lm9 = {}; opts.forEach(function(o9){ _lm9[o9.key] = o9.label; }); return _lm9; })()}
                              value={sel.periodKeys.filter(k => allKs.includes(k))}
                              onChange={function(v9){ setSel(function(p){ var _rest9 = (p.periodKeys || []).filter(function(k9){ return allKs.indexOf(k9) < 0; }); return {...p, periodKeys: _rest9.concat(v9)}; }); }}
                              accent="#dc2626" bg="#fef2f2" />
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              });
                if (!__PT) return _oqNote9 ? [_oqNote9].concat(_rowsJsx) : _rowsJsx;   /* r108: legend first */
                /* ── Sales Tracker (AMS) period: mode pills + the selected mode's chips ── */
                var _pShort = {Yearly:"Year", Quarterly:"Quarter", Monthly:"Month", MAT:"MAT", "MAT Monthly":"MAT (M)", YTD:"YTD", "YTD Monthly":"YTD (M)"};
                var _pSel2 = null;
                for (var _pqi = 0; _pqi < _pModes.length; _pqi++) if (_pModes[_pqi].mode === sel.periodMode) _pSel2 = _pModes[_pqi];
                var _selKeys2 = sel.periodKeys || [];
                var _pillSt = function(on){ return {padding:"6px 13px", borderRadius:10, cursor:"pointer", fontSize:11.5, fontWeight:on?800:600,
                  background:on?__PT.acc:"transparent", color:on?__PT.accTx:__PT.mut,
                  border:"1.5px solid "+(on?__PT.acc:"rgba(255,255,255,.25)"), userSelect:"none", whiteSpace:"nowrap"}; };
                return (
                  <React.Fragment>
                    <div style={{display:"flex", flexWrap:"wrap", gap:6, marginBottom:8}}>
                      {_pModes.map(function(item){
                        var _on = sel.periodMode === item.mode;
                        return (
                          <div key={item.mode} onClick={function(){ setMode(item.mode); }} style={_pillSt(_on)}>
                            {_pShort[item.mode] || item.mode}
                          </div>
                        );
                      })}
                    </div>
                    {_pSel2 && _pSel2.opts && _pSel2.opts.length > 0 && (
                      <React.Fragment>
                        <div style={{display:"flex", justifyContent:"flex-end", marginBottom:4}}>
                          <button onClick={function(){ togAllK(_pSel2.opts.map(function(o){ return o.key; })); }}
                            style={{fontSize:9.5, color:__PT.acc, background:"none", border:"none", cursor:"pointer", fontFamily:"inherit", fontWeight:700, padding:0}}>
                            {(_pSel2.opts.filter(function(o){ return _selKeys2.indexOf(o.key) >= 0; }).length === _pSel2.opts.length) ? "Clear" : "All"}
                          </button>
                        </div>
                        <div style={{display:"flex", flexWrap:"wrap", gap:6}}>
                          {_pSel2.opts.map(function(o){
                            var _on = _selKeys2.indexOf(o.key) >= 0;
                            return (
                              <div key={o.key} onClick={function(){ togKey(o.key); }}
                                style={{padding:"5px 12px", borderRadius:10, cursor:"pointer", fontSize:11, fontWeight:_on?800:600,
                                        background:_on?__PT.acc:"transparent", color:_on?__PT.accTx:__PT.mut,
                                        border:"1.5px solid "+(_on?__PT.acc:"rgba(255,255,255,.25)"), userSelect:"none", whiteSpace:"nowrap"}}>
                                {o.label}
                              </div>
                            );
                          })}
                        </div>
                      </React.Fragment>
                    )}
                  </React.Fragment>
                );
              })()}
            </>
          )}
        </FilterSection>

        <FilterSection id="Measures" isOpen={openSecs.Measures} onToggle={togSec} color="#15803d" title={"Measures"} cnt={sel.measures.length}>
          {(() => {
            var msrs = sel.measures || [];
            var hasUSD = msrs.indexOf("USD") >= 0;
            var hasLC = msrs.indexOf("LC") >= 0;
            var hasUnit = msrs.indexOf("Unit") >= 0;
            var hasPrice = msrs.indexOf("Price") >= 0;
            var hasCurrency = hasUSD || hasLC;

            var toggleM = function(m) {
              var next = msrs.slice();
              var idx = next.indexOf(m);
              if (idx >= 0) {
                next.splice(idx, 1);
                /* If removing last currency, also remove Price */
                if ((m === "USD" || m === "LC") && next.indexOf("Price") >= 0) {
                  var hasOther = (m === "USD" ? next.indexOf("LC") : next.indexOf("USD")) >= 0;
                  if (!hasOther) next.splice(next.indexOf("Price"), 1);
                }
              } else {
                /* Price requires a currency */
                if (m === "Price" && !next.includes("USD") && !next.includes("LC")) return;
                next.push(m);
                /* Price requires SKU dimension — auto-add if not already checked */
                if (m === "Price" && mp.sku) {
                  setSel(function(p) {
                    var sc = p.showCols.slice();
                    if (sc.indexOf("sku") < 0) sc.push("sku");
                    return Object.assign({}, p, {measures: next, showCols: sc});
                  });
                  return; /* already set via setSel */
                }
              }
              S("measures", next);
            };

            /* Build measure items from study type config or fallback to mapping */
            var mItems = [];
            var _isKimadia = studyTypeConfig && studyTypeConfig.code === "kimadia";
            var _isGpdM = studyTypeConfig && studyTypeConfig.code === "gpd"; /* Jordan tender market */
            var _lcLabel = _isKimadia ? "IQD" : _isGpdM ? "JD" : "LC";
            if (studyTypeConfig && studyTypeConfig.measures && studyTypeConfig.measures.length > 0) {
              /* Group measures by MeasureGroup */
              var seen = {};
              var _isJordanMedical = studyTypeConfig && studyTypeConfig.code === "jordan_medical";
              var _isMsaM = studyTypeConfig && studyTypeConfig.code === "jordan_msa"; /* merged Pharma+Rx market */
              var _unitLbl = _isJordanMedical ? "PRODUCT" : "UNITS";
              studyTypeConfig.measures.forEach(function(m) {
                if (m.group === "Unit" && !seen.Unit)  {
                  mItems.push({key:"Unit",  label:_unitLbl});  seen.Unit = true;
                  /* Gross Selling directly under UNITS — it's a unit-family measure */
                  if (mp.grossUnit && !seen.Gross) { mItems.push({key:"Gross", label:"GROSS SELLING"}); seen.Gross = true; }
                  /* Stock: closing Sell-In − Sell-Out, trailing block in the pivot */
                  if (mp.stock && !seen.Stock) { mItems.push({key:"Stock", label:"STOCK"}); seen.Stock = true; }
                }
                if (m.group === "USD"  && !seen.USD)   { mItems.push({key:"USD",   label:"USD"});   seen.USD = true; }
                if (m.group === "LC"   && !seen.LC)    { mItems.push({key:"LC",    label:_lcLabel});    seen.LC = true; }
                /* GPD IU families — their own checkboxes, like the legacy tool */
                if (m.group === "UnitIU"  && !seen.UnitIU)  { mItems.push({key:"UnitIU",  label:"UNITS IU"});      seen.UnitIU = true; }
                if (m.group === "PriceIU" && !seen.PriceIU) { mItems.push({key:"PriceIU", label:"UNIT PRICE IU"}); seen.PriceIU = true; }
                /* FOCUnit: auto-shows in pivot, no checkbox needed */
                if (m.group === "Price" && !seen.Price) {
                  mItems.push({key:"Price", label:((_isKimadia || _isGpdM) ? "UNIT PRICE" : "Price") + (hasPrice ? (hasUSD && hasLC ? " (USD + " + _lcLabel + ")" : hasUSD ? " (USD)" : hasLC ? " (" + _lcLabel + ")" : "") : "")});
                  seen.Price = true;
                }
              });
              /* Medical bridge: StudyTypeMeasures (global config) historically lists only
                 Unit/Patients/Prescriptions for medical, even though the underlying MedicalData
                 table HAS ValueUSD/ValueLC/PriceUSD/PriceLC columns. When an admin grants
                 USD/LC/Price to a medical client via permissions, those checkboxes need to
                 appear. Add them from the column mapping if not already in seen{}. */
              if (_isJordanMedical) {
                if (!seen.USD && mp.valUSD) { mItems.push({key:"USD", label:"USD"}); seen.USD = true; }
                if (!seen.LC  && mp.valLC)  { mItems.push({key:"LC",  label:_lcLabel}); seen.LC = true; }
                if (!seen.Price && (mp.priceLC || mp.priceUSD)) {
                  mItems.push({key:"Price", label:"Price" + (hasPrice ? (hasUSD && hasLC ? " (USD + " + _lcLabel + ")" : hasUSD ? " (USD)" : hasLC ? " (" + _lcLabel + ")" : "") : "")});
                  seen.Price = true;
                }
              }
              /* Patients / Prescriptions: COUNT(DISTINCT …) — medical study, and the
                 merged Medical & Pharma market (its view carries PatientID/intOrder) */
              if (_isJordanMedical || (_isMsaM && mp.patients)) { mItems.push({key:"Patients", label:"# PATIENTS"}); if (mp.prescriptions) { mItems.push({key:"Prescriptions", label:"# PRESCRIPTIONS"}); mItems.push({key:"RxPerDoctor", label:"RX PER DOCTOR"}); mItems.push({key:"Doctors", label:"# DOCTORS"}); mItems.push({key:"DoctorsTotal", label:"# DOCTORS (TOTAL)"}); } }
              /* Pharmacies: Sales Tracker COUNT(DISTINCT PharmacyID) — panel weight */
              if (mp.pharmacies) mItems.push({key:"Pharmacies", label:"# PHARMACIES"});
            } else {
              var _unitLblF = (activeStudyType === "jordan_medical") ? "PRODUCT" : "UNITS";
              if (mp.units) mItems.push({key:"Unit", label:_unitLblF});
              /* Gross Selling directly under UNITS — it's a unit-family measure */
              if (mp.grossUnit) mItems.push({key:"Gross", label:"GROSS SELLING"});
              /* Stock: closing Sell-In − Sell-Out, trailing block in the pivot */
              if (mp.stock) mItems.push({key:"Stock", label:"STOCK"});
              if (mp.valUSD) mItems.push({key:"USD", label:"USD"});
              if (mp.valLC) mItems.push({key:"LC", label:_lcLabel});
              if (mp.unitsIU) mItems.push({key:"UnitIU", label:"UNITS IU"});
              if (mp.priceUSDIU || mp.priceLCIU) mItems.push({key:"PriceIU", label:"UNIT PRICE IU"});
              if (mp.priceLC || mp.priceUSD) mItems.push({key:"Price", label:"Price" + (hasPrice ? (hasUSD && hasLC ? " (USD + " + _lcLabel + ")" : hasUSD ? " (USD)" : hasLC ? " (" + _lcLabel + ")" : "") : "")});
              /* Patients / Prescriptions: medical study + merged Medical & Pharma market */
              if (activeStudyType === "jordan_medical" || (activeStudyType === "jordan_msa" && mp.patients)) { mItems.push({key:"Patients", label:"# PATIENTS"}); if (mp.prescriptions) { mItems.push({key:"Prescriptions", label:"# PRESCRIPTIONS"}); mItems.push({key:"RxPerDoctor", label:"RX PER DOCTOR"}); mItems.push({key:"Doctors", label:"# DOCTORS"}); mItems.push({key:"DoctorsTotal", label:"# DOCTORS (TOTAL)"}); } }
              /* Pharmacies: Sales Tracker COUNT(DISTINCT PharmacyID) — panel weight */
              if (mp.pharmacies) mItems.push({key:"Pharmacies", label:"# PHARMACIES"});
            }

            /* Medical & Pharma Intelligence: group the checkboxes — Pharma measures
               (sales dataset) first, then the Rx measures (prescription dataset) —
               mirroring how the merged view splits by Source. Header rows are
               render-only (mi.hdr) and never toggle. */
            var _msaGrp = (activeStudyType === "jordan_msa") && !__PT;
            if (_msaGrp && mItems.length) {
              var _rxKeys = {Patients:1, Prescriptions:1};
              var _phItems = mItems.filter(function(mi){ return !_rxKeys[mi.key]; });
              var _rxItems = mItems.filter(function(mi){ return _rxKeys[mi.key]; });
              mItems = [];
              if (_phItems.length) { mItems.push({hdr:true, key:"__hph", label:"\ud83d\udc8a PHARMA \u2014 sales dataset"}); mItems = mItems.concat(_phItems); }
              if (_rxItems.length) { mItems.push({hdr:true, key:"__hrx", label:"\ud83e\ude7a RX \u2014 prescription dataset"}); mItems = mItems.concat(_rxItems); }
            }

            /* No auto-select on load — user explicitly picks measures.
               (Was: auto-tick "Unit"/"PRODUCT" for medical studies, but per request the
               user wants to choose manually so the selection isn't pre-imposed.) */

            if (__PT) {
              /* ── Sales Tracker (AMS) measures: pill row ── */
              var _pMLbl = { Unit:"Units", USD:"USD", LC:_lcLabel, Gross:"Gross", Stock:"Stock",
                             Pharmacies:"# Pharmacies", Patients:"# Patients", Prescriptions:"# Rx", Price:"Price",
};
              return (
                <div style={{display:"flex", flexWrap:"wrap", gap:6}}>
                  {mItems.filter(function(mi){ return allowedMeasures.indexOf(mi.key) >= 0; }).map(function(mi){
                    var _on = msrs.indexOf(mi.key) >= 0;
                    var _dis = mi.key === "Price" && !hasCurrency;
                    return (
                      <div key={mi.key} onClick={function(){ if (!_dis) toggleM(mi.key); }}
                        style={{padding:"6px 13px", borderRadius:10, cursor:_dis?"not-allowed":"pointer", fontSize:11.5, fontWeight:_on?800:600,
                                opacity:_dis?0.4:1,
                                background:_on?__PT.acc:"transparent", color:_on?__PT.accTx:__PT.mut,
                                border:"1.5px solid "+(_on?__PT.acc:"rgba(255,255,255,.25)"), userSelect:"none", whiteSpace:"nowrap"}}>
                        {_pMLbl[mi.key] || mi.label}
                      </div>
                    );
                  })}
                  {(mp.priceLC || mp.priceUSD) && !hasCurrency && (
                    <div style={{width:"100%", fontSize:8.5, color:"#F0C36D", fontWeight:600}}>{"Select USD or " + _lcLabel + " to enable Price"}</div>
                  )}
                </div>
              );
            }
            return (
              <div style={{display:"flex", flexDirection:"column", gap:2}}>
                {mItems.filter(function(mi){ return mi.hdr || allowedMeasures.indexOf(mi.key) >= 0; }).map(function(mi) {
                  if (mi.hdr) return (
                    <div key={mi.key} style={{fontSize:8.5, fontWeight:800, color:"#5a7da6", textTransform:"uppercase", letterSpacing:".06em", padding:"6px 8px 2px"}}>{mi.label}</div>
                  );
                  var on = msrs.indexOf(mi.key) >= 0;
                  var disabled = mi.key === "Price" && !hasCurrency;
                  return (
                    <div key={mi.key} onClick={function(){ if (!disabled) toggleM(mi.key); }}
                      style={{display:"flex", alignItems:"center", gap:7, padding:"4px 8px", borderRadius:5,
                        cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.4 : 1,
                        background: on ? (__PT?__PT.accBgOn:"#f0fdf4") : "transparent",
                        transition:"background .12s"}}>
                      <div style={{width:14, height:14, borderRadius:3, border:"1.5px solid " + (on ? (__PT?__PT.acc:"#15803d") : (__PT?__PT.chkOff:"#cbd5e1")),
                        background: on ? (__PT?__PT.acc:"#15803d") : (__PT?"transparent":"#fff"), display:"flex", alignItems:"center", justifyContent:"center",
                        fontSize:9, color:__PT?__PT.accTx:"#fff", fontWeight:800, flexShrink:0}}>
                        {on ? "✓" : ""}
                      </div>
                      <span style={{fontSize:10.5, fontWeight: on ? 700 : 400, color: on ? (__PT?__PT.acc:"#15803d") : (__PT?__PT.mut:"#334155")}}>{mi.label}</span>
                    </div>
                  );
                })}
                {(mp.priceLC || mp.priceUSD) && !hasCurrency && (
                  <div style={{fontSize:8.5, color:"#f59e0b", marginLeft:8, fontWeight:600}}>⚠ Select USD or {_lcLabel} to enable Price</div>
                )}
              </div>
            );
          })()}
        </FilterSection>

        {(() => {
          const st = sel.stats || {indicators:[], growthMode:"prev"};
          const STAT_DEFS = [
            {id:"Market Share", label:"Market Share", icon:"\u00ab%\u00bb", desc:"% of grand total per period"},
            {id:"Rank",         label:"Rank",         icon:"#",             desc:"Within each group"},
            {id:"Growth",       label:"Growth",       icon:"\u25B2\u25BC",   desc:"Period-over-period"},
            {id:"CAGR",         label:"CAGR",         icon:"\u03BC",        desc:"Compound Annual Growth Rate"},
            {id:"Evolution Index", label:"Evolution Index", icon:"EI",     desc:"growth vs market (100 = with market)"},
          ];
          /* FOC % \u2014 Sales Tracker only (needs FOC + Gross columns). Its own
             derived column (FOC \u00F7 Gross per cell), no per-measure scoping. */
          if (mp.focUnit && mp.grossUnit) STAT_DEFS.push({id:"FOC %", label:"FOC %", icon:"%", desc:"FOC as % of gross selling", noScope:true, note:"Adds a FOC % column under each period (requires UNITS)"});
          /* Coverage % — Sales Tracker only (needs pharmacy distinct counts):
             pharmacies carrying the selection ÷ total panel pharmacies, per
             governorate when Governorate is a row dimension. */
          if (mp.pharmacies) STAT_DEFS.push({id:"Coverage %", label:"Coverage %", icon:"◔", desc:"pharmacies carrying ÷ total panel", noScope:true, note:"Adds a COVERAGE % column under each period — pharmacies carrying the row's selection ÷ total panel pharmacies (per governorate when Governorate is a row dimension)"});
          /* Sell-Through % — Sales Tracker only (flow bands): sell-out ÷ sell-in of
             each SCOPED measure (units, LC, USD). Its column appears ONLY under
             Sell-Out bands (Noura 2026-09-23). */
          if (mp.flow) STAT_DEFS.push({id:"Sell-Through %", label:"Sell-Through %", icon:"⇄", desc:"sell-out ÷ sell-in per period", note:"Adds a SELL-THROUGH column under each Sell-Out band — sell-out ÷ sell-in of the measures you scope it to (units, LC, USD)"});
          /* Profit — Sales Tracker only: sell-out value − the same units at the
             sell-in price. Scoped to the VALUE measures: USD → ProfitUSD, LC →
             ProfitLC. Sell-Out bands only. */
          if (mp.flow && (mp.profitUSD || mp.profitLC)) STAT_DEFS.push({id:"Profit", label:"Profit", icon:"$", desc:"sell-out value − sell-in value", note:"Adds SELL-OUT VALUE (at the retail price) + PROFIT columns under each Sell-Out band — the Value column itself is at the sell-in price, so Value + Profit = Sell-Out Value. Scoped value measure(s): USD and/or LC"});
          const GROWTH_OPTS = [
            {mode:"prev", label:"vs Previous Period"},
            {mode:"yoy",  label:"By Similar (same period last year)"},
            {mode:"both", label:"Both Comparisons"},
          ];
          return (
            <FilterSection id="Statistics" isOpen={openSecs.Statistics} onToggle={togSec} color="#0891b2" title={"Statistics"} cnt={st.indicators.length}>
              {__PT ? (function(){
                /* ── Sales Tracker (AMS) statistics: pill row; enabling a stat scopes it to
                   EVERY selected measure (the per-measure scoping UI is a light-theme
                   power feature — pulse keeps the pills clean like the mock). ── */
                var _pStats = STAT_DEFS.filter(function(s2){ return allowedStatistics.indexOf(s2.id) >= 0; });
                var _pSLbl = {"Market Share":"Share", "Evolution Index":"EI"};
                var _pTogStat = function(id){
                  setSel(function(p){
                    var st2 = p.stats || {indicators:[], growthMode:"prev"};
                    var enabling = (st2.indicators || []).indexOf(id) < 0;
                    var inds = enabling ? (st2.indicators || []).concat([id]) : st2.indicators.filter(function(x){ return x !== id; });
                    var sm = Object.assign({}, st2.statMeasures || {});
                    if (enabling) sm[id] = (p.measures || []).slice();
                    return Object.assign({}, p, { stats: Object.assign({}, st2, { indicators: inds, statMeasures: sm }) });
                  });
                };
                var _gOn = st.indicators.indexOf("Growth") >= 0 || st.indicators.indexOf("Evolution Index") >= 0;
                return (
                  <React.Fragment>
                    <div style={{display:"flex", flexWrap:"wrap", gap:6}}>
                      {_pStats.map(function(stat){
                        var _on = st.indicators.indexOf(stat.id) >= 0;
                        return (
                          <div key={stat.id} onClick={function(){ _pTogStat(stat.id); }}
                            style={{padding:"6px 13px", borderRadius:10, cursor:"pointer", fontSize:11.5, fontWeight:_on?800:600,
                                    background:_on?__PT.acc:"transparent", color:_on?__PT.accTx:__PT.mut,
                                    border:"1.5px solid "+(_on?__PT.acc:"rgba(255,255,255,.25)"), userSelect:"none", whiteSpace:"nowrap"}}>
                            {_pSLbl[stat.id] || stat.label}
                          </div>
                        );
                      })}
                    </div>
                    {st.indicators.length > 0 && (sel.measures || []).length === 0 && (
                      <div style={{fontSize:8.5, color:"#F0C36D", fontWeight:600, marginTop:6}}>Pick a measure above first</div>
                    )}
                    {_gOn && (
                      <div style={{display:"flex", alignItems:"center", gap:5, marginTop:9, flexWrap:"wrap"}}>
                        <span style={{fontSize:8.5, fontWeight:800, color:"#9FBFC5", textTransform:"uppercase", letterSpacing:".07em"}}>Growth basis</span>
                        {GROWTH_OPTS.map(function(opt){
                          var _on = st.growthMode === opt.mode;
                          return (
                            <div key={opt.mode} onClick={function(){ setGrowthMode(opt.mode); }}
                              style={{padding:"3px 9px", borderRadius:8, cursor:"pointer", fontSize:9.5, fontWeight:_on?800:600,
                                      background:_on?__PT.acc:"transparent", color:_on?__PT.accTx:__PT.mut,
                                      border:"1px solid "+(_on?__PT.acc:"rgba(255,255,255,.25)"), userSelect:"none", whiteSpace:"nowrap"}}>
                              {opt.mode === "prev" ? "vs Previous" : opt.mode === "yoy" ? "vs Similar" : "Both"}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </React.Fragment>
                );
              })() : (<React.Fragment>
              <div style={{fontSize:9.5,padding:"4px 7px",background:"#e0f2fe",borderRadius:5,color:"#0369a1",marginBottom:10,fontWeight:600}}>
                Choose which measures get each statistic
              </div>
              {STAT_DEFS.filter(function(stat){ return allowedStatistics.indexOf(stat.id) >= 0; }).map(function(stat){
                var on = st.indicators.indexOf(stat.id) >= 0;
                /* For each ENABLED statistic, show inline chip-toggles for the user's
                   currently-selected measures. Click a chip to add/remove that measure
                   from this statistic's scope. */
                /* Per-statistic measure scope. The chips show ONLY measures the user has
                   actually CHECKED in the Measures section above AND which are configured
                   for the current study (allowed per admin permissions). This way:
                   - If user checks USD but it's not allowed for this study → no chip
                   - If user has Unit allowed but unchecked → no chip
                   - Only measures truly available + selected get chips
                   We also strip any "orphan" entries in statMeasures (e.g., user enabled
                   Rank for USD, then later unchecked USD in Measures — that USD scope
                   entry is invisible but kept; silently ignored here).
                   Note: mItems is defined inside the Measures section's IIFE (different
                   scope) — we use the top-level `allowedMeasures` permission list instead. */
                var userMeasures = (sel.measures || []).filter(function(mk){
                  return allowedMeasures.indexOf(mk) >= 0;
                });
                var rawStatMeasures = (st.statMeasures && st.statMeasures[stat.id]) || [];
                var statMeasures = rawStatMeasures.filter(function(mk){
                  return userMeasures.indexOf(mk) >= 0;
                });
                /* Resolve measure key → friendly display label. Maps the internal keys
                   (Unit / USD / LC / Price / Patients / Prescriptions) to user-facing
                   labels matching what shows in the Measures section. */
                var measureLabel = function(mKey) {
                  if (mKey === "Unit") return _isMedical ? "PRODUCT" : "UNITS";
                  if (mKey === "USD") return "USD";
                  if (mKey === "LC") return (studyTypeConfig && studyTypeConfig.code === "kimadia") ? "IQD" : (studyTypeConfig && studyTypeConfig.code === "gpd") ? "JD" : "LC";
                  if (mKey === "Price") return (studyTypeConfig && studyTypeConfig.code === "kimadia" || studyTypeConfig && studyTypeConfig.code === "gpd") ? "UNIT PRICE" : "Price";
                  if (mKey === "UnitIU") return "UNITS IU";
                  if (mKey === "PriceIU") return "UNIT PRICE IU";
                  if (mKey === "Patients") return "# PATIENTS";
                  if (mKey === "Prescriptions") return "# PRESCRIPTIONS";
                  return mKey;
                };
                return (
                  <div key={stat.id} style={{marginBottom:8}}>
                    <div onClick={function(){ togStatInd(stat.id); }}
                      style={{display:"flex",alignItems:"center",gap:7,padding:"6px 8px",borderRadius:6,
                              cursor:"pointer",userSelect:"none",
                              background:on?"#e0f2fe":"#f8fafc",
                              border:"1.5px solid "+(on?"#7dd3fc":"#e2e8f0")}}>
                      <div style={{width:14,height:14,borderRadius:3,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",
                                   border:"1.5px solid "+(on?"#0891b2":"#cbd5e1"),background:on?"#0891b2":"#fff",fontSize:9,color:"#fff"}}>
                        {on?"✓":""}
                      </div>
                      <span style={{fontWeight:700,fontSize:10,letterSpacing:".04em",color:on?"#0369a1":"#334155",flex:1}}>
                        {stat.label}
                      </span>
                      <span style={{fontSize:9,color:on?"#38bdf8":"#94a3b8",fontWeight:600}}>{stat.desc}</span>
                    </div>
                    {/* Measure-scope chips — only shown when the statistic is enabled.
                        Each chip is one of the user's currently-selected measures; clicking
                        toggles its membership in this statistic's scope. */}
                    {on && stat.noScope && (
                      <div style={{marginTop:5,marginLeft:14,borderLeft:"2px solid #7dd3fc",paddingLeft:10,padding:"4px 0 4px 10px"}}>
                        <div style={{fontSize:9,color:"#0369a1",fontWeight:600,fontStyle:"italic"}}>
                          {stat.note || "Adds a derived column under each period"}
                        </div>
                      </div>
                    )}
                    {on && !stat.noScope && userMeasures.length === 0 && (
                      <div style={{marginTop:5,marginLeft:14,borderLeft:"2px solid #fcd34d",paddingLeft:10,padding:"4px 0 4px 10px"}}>
                        <div style={{fontSize:9,color:"#92400e",fontWeight:600,fontStyle:"italic"}}>
                          ⓘ Select a measure in the Measures section above first
                        </div>
                      </div>
                    )}
                    {on && !stat.noScope && userMeasures.length > 0 && (
                      <div style={{marginTop:5,marginLeft:14,borderLeft:"2px solid #7dd3fc",paddingLeft:10}}>
                        <div style={{fontSize:9,color:"#0369a1",fontWeight:600,marginBottom:4}}>Apply to:</div>
                        <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                          {userMeasures.map(function(mKey){
                            var picked = statMeasures.indexOf(mKey) >= 0;
                            return (
                              <div key={mKey}
                                onClick={function(m){ return function(e){ e.stopPropagation(); togStatMeasure(stat.id, m); }; }(mKey)}
                                style={{padding:"3px 8px",borderRadius:10,fontSize:9.5,fontWeight:700,cursor:"pointer",userSelect:"none",
                                        background:picked?"#0891b2":"#fff",color:picked?"#fff":"#475569",
                                        border:"1px solid "+(picked?"#0891b2":"#cbd5e1"),
                                        transition:"all .15s"}}>
                                {picked?"✓ ":""}{measureLabel(mKey)}
                              </div>
                            );
                          })}
                        </div>
                        {statMeasures.length === 0 && (
                          <div style={{fontSize:9,color:"#dc2626",fontWeight:600,marginTop:4,fontStyle:"italic"}}>
                            ⚠ No measures selected — this statistic won't appear in the pivot
                          </div>
                        )}
                      </div>
                    )}
                    {stat.id === "Growth" && on && (
                      <div style={{marginTop:5,marginLeft:14,borderLeft:"2px solid #7dd3fc",paddingLeft:10,display:"flex",flexDirection:"column",gap:3}}>
                        {GROWTH_OPTS.map(function(opt){
                          var sel2 = st.growthMode === opt.mode;
                          return (
                            <div key={opt.mode}
                              onClick={function(m){ return function(){ setGrowthMode(m); }; }(opt.mode)}
                              style={{display:"flex",alignItems:"center",gap:6,padding:"3px 4px",cursor:"pointer",userSelect:"none",borderRadius:4,
                                      background:sel2?"#e0f2fe":"transparent"}}>
                              <div style={{width:10,height:10,borderRadius:"50%",flexShrink:0,
                                           border:"1.5px solid "+(sel2?"#0891b2":"#cbd5e1"),
                                           background:sel2?"#0891b2":"#fff"}}/>
                              <span style={{fontSize:10,color:sel2?"#0369a1":(__PT?__PT.mut:"#64748b"),fontWeight:sel2?700:400}}>{opt.label}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {stat.id === "Evolution Index" && on && (
                      /* EI's own comparison-period choice — same options as Growth.
                         Back-compat default mirrors the old behavior (followed the
                         Growth mode) for queries saved before evoMode existed. */
                      <div style={{marginTop:5,marginLeft:14,borderLeft:"2px solid #7dd3fc",paddingLeft:10,display:"flex",flexDirection:"column",gap:3}}>
                        {GROWTH_OPTS.map(function(opt){
                          var evoCur = st.evoMode || (st.growthMode === "yoy" ? "yoy" : "prev");
                          var sel3 = evoCur === opt.mode;
                          return (
                            <div key={"evo_"+opt.mode}
                              onClick={function(m){ return function(){ setEvoMode(m); }; }(opt.mode)}
                              style={{display:"flex",alignItems:"center",gap:6,padding:"3px 4px",cursor:"pointer",userSelect:"none",borderRadius:4,
                                      background:sel3?"#e0f2fe":"transparent"}}>
                              <div style={{width:10,height:10,borderRadius:"50%",flexShrink:0,
                                           border:"1.5px solid "+(sel3?"#0891b2":"#cbd5e1"),
                                           background:sel3?"#0891b2":"#fff"}}/>
                              <span style={{fontSize:10,color:sel3?"#0369a1":(__PT?__PT.mut:"#64748b"),fontWeight:sel3?700:400}}>{opt.label}</span>
                            </div>
                          );
                        })}
                        <div style={{fontSize:9,color:"#0369a1",fontWeight:600,fontStyle:"italic",padding:"2px 0"}}>
                          {"100 = grew with the market · >100 gained share · <100 lost share"}
                        </div>
                      </div>
                    )}
                    {stat.id === "CAGR" && on && (
                      <div style={{marginTop:4,marginLeft:14,borderLeft:"2px solid #fde68a",paddingLeft:10}}>
                        <div style={{fontSize:9,color:"#92400e",fontWeight:600,padding:"3px 0"}}>
                          Applies to <strong>Year</strong> and <strong>MAT</strong> period modes only. Minimum 3 periods required.
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              </React.Fragment>)}
            </FilterSection>
          );
        })()}

        {/* ═══ TOP N FILTER ═══ */}
        {(() => {
          const tn = sel.topN || {enabled:false, n:10, measure:"Unit", applyTo:"top", mode:"global", dims:[]};
          const setTN = (k,v) => setSel(p => ({...p, topN:{...(p.topN||tn), [k]:v}}));
          const togTNDim = (d) => setSel(p => {
            var cur = (p.topN||tn).dims||[];
            var next = cur.indexOf(d) >= 0 ? cur.filter(x=>x!==d) : cur.concat([d]);
            return {...p, topN:{...(p.topN||tn), dims:next}};
          });
          /* Only show measures that are BOTH mapped AND selected by user */
          const _hasP = sel.measures.includes("Price");
          const _hasD = sel.measures.includes("USD");
          const _hasL = sel.measures.includes("LC");
          var _isK = studyTypeConfig && studyTypeConfig.code === "kimadia";
          var _lcL = _isK ? "IQD" : "LC";
          var availM = [];
          if (sel.measures.includes("Unit") && mp.units) availM.push("Unit");
          if (sel.measures.includes("Unit") && mp.focUnit) availM.push("FOC Unit");
          if (_hasD && mp.valUSD) availM.push("USD");
          if (_hasL && mp.valLC) availM.push(_lcL);
          if (_hasP && _hasD && mp.priceUSD) availM.push("Price USD");
          if (_hasP && _hasD && mp.netPriceUSD) availM.push("Net Price USD");
          if (_hasP && _hasL && mp.priceLC) availM.push("Price " + _lcL);
          if (_hasP && _hasL && mp.netPriceLC) availM.push("Net Price " + _lcL);
          /* Medical-only distinct counts: Patients (PatientID), Prescriptions (intOrder).
             Selectable here so Top N can rank by either of them. */
          if (sel.measures.includes("Patients") && mp.patients) availM.push("Patients");
          if (sel.measures.includes("Prescriptions") && mp.prescriptions) availM.push("Prescriptions");
          /* Auto-correct measure if current one isn't valid — to the FIRST SELECTED measure
             (r126: selection order, so a medical query that picked Prescriptions first ranks by
             Prescriptions), then the first available. */
          var _tnByUser = { Unit: "Unit", USD: "USD", LC: _lcL, Patients: "Patients", Prescriptions: "Prescriptions" };
          var _tnFirst = null;
          for (var _ti = 0; _ti < sel.measures.length && !_tnFirst; _ti++) { var _tc9 = _tnByUser[sel.measures[_ti]]; if (_tc9 && availM.includes(_tc9)) _tnFirst = _tc9; }
          const effectiveMeasure = availM.length > 0 && !availM.includes(tn.measure) ? (_tnFirst || availM[0]) : tn.measure;
          const selDims = sel.showCols.filter(k => mp[k]);
          return (
            <FilterSection id="TopN" isOpen={openSecs.TopN} onToggle={togSec} color="#be185d" title={"Top N Filter"} cnt={tn.enabled?1:0}>
              <div style={{marginBottom:8}}>
                <div onClick={() => setTN("enabled", !tn.enabled)}
                  style={{display:"flex",alignItems:"center",gap:7,padding:"6px 8px",borderRadius:6,
                          cursor:"pointer",userSelect:"none",
                          background:tn.enabled?"#fdf2f8":"#f8fafc",
                          border:"1.5px solid "+(tn.enabled?"#f9a8d4":"#e2e8f0")}}>
                  <div style={{width:14,height:14,borderRadius:3,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",
                               border:"1.5px solid "+(tn.enabled?"#be185d":"#cbd5e1"),background:tn.enabled?"#be185d":"#fff",fontSize:9,color:"#fff"}}>
                    {tn.enabled?"✓":""}
                  </div>
                  <span style={{fontWeight:700,fontSize:10,letterSpacing:".04em",color:tn.enabled?"#be185d":"#334155",flex:1}}>
                    Enable Top N
                  </span>
                  <span style={{fontSize:9,color:tn.enabled?"#ec4899":"#94a3b8",fontWeight:600}}>Rank-based filtering</span>
                </div>
              </div>
              {tn.enabled && (
                <div style={{padding:"6px 0",display:"flex",flexDirection:"column",gap:8}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <label style={{fontSize:10,fontWeight:700,color:__PT?__PT.mut:"#64748b",minWidth:50}}>
                      {(tn.mode||"global") === "selective" ? "Default" : "Show top"}
                    </label>
                    {/* Uncontrolled-style input via local draft state — the previous version
                        rewrote tn.n on every keystroke with parseInt(...)||10, which made
                        partial values like empty (between deleting and retyping) snap back
                        to 10 and stole the cursor. Now the user types freely; we commit a
                        clamped integer on blur, Enter, or 400ms of idle. */}
                    <TopNValueInput value={tn.n} onCommit={function(n){ setTN("n", n); }} />
                    <span style={{fontSize:10,color:__PT?__PT.mut:"#64748b"}}>items</span>
                    {(tn.mode||"global") === "selective" && (tn.dims||[]).length > 0 && (
                      <span style={{fontSize:9,color:"#94a3b8",fontStyle:"italic"}}>
                        — used when no per-feature N is set
                      </span>
                    )}
                  </div>
                  <div>
                    <label style={{fontSize:10,fontWeight:700,color:__PT?__PT.mut:"#64748b",display:"block",marginBottom:3}}>Ranked by</label>
                    {availM.length === 0 ? (
                      <div style={{fontSize:9,color:"#f87171",padding:"4px 6px",background:"#fef2f2",borderRadius:4}}>
                        ⚠ Select at least one measure first (in Measures section)
                      </div>
                    ) : (
                    <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                      {availM.map(m => {
                        const on = effectiveMeasure === m;
                        return (
                          <div key={m} onClick={() => setTN("measure",m)}
                            style={{padding:"3px 10px",borderRadius:20,cursor:"pointer",fontSize:10,
                                    border:"1.5px solid "+(on?"#be185d":"#e2e8f0"),background:on?"#fdf2f8":"#fff",
                                    color:on?"#be185d":"#64748b",fontWeight:on?700:400,userSelect:"none"}}>{m}</div>
                        );
                      })}
                    </div>
                    )}
                  </div>
                  <div>
                    <label style={{fontSize:10,fontWeight:700,color:__PT?__PT.mut:"#64748b",display:"block",marginBottom:3}}>Direction</label>
                    <div style={{display:"flex",gap:4}}>
                      {[{k:"top",lb:"Top (highest)"},{k:"bottom",lb:"Bottom (lowest)"}].map(d => {
                        const on = tn.applyTo === d.k;
                        return (
                          <div key={d.k} onClick={() => setTN("applyTo",d.k)}
                            style={{padding:"3px 10px",borderRadius:20,cursor:"pointer",fontSize:10,
                                    border:"1.5px solid "+(on?"#be185d":"#e2e8f0"),background:on?"#fdf2f8":"#fff",
                                    color:on?"#be185d":"#64748b",fontWeight:on?700:400,userSelect:"none"}}>{d.lb}</div>
                        );
                      })}
                    </div>
                  </div>

                  {/* ── Show Others ── */}
                  <div>
                    <div onClick={() => setTN("showOthers", !tn.showOthers)}
                      style={{display:"flex",alignItems:"center",gap:7,padding:"5px 8px",borderRadius:6,
                              cursor:"pointer",userSelect:"none",
                              background:tn.showOthers?"#fdf2f8":"#f8fafc",
                              border:"1.5px solid "+(tn.showOthers?"#f9a8d4":"#e2e8f0")}}>
                      <div style={{width:13,height:13,borderRadius:3,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",
                                   border:"1.5px solid "+(tn.showOthers?"#be185d":"#cbd5e1"),background:tn.showOthers?"#be185d":"#fff",fontSize:8,color:"#fff"}}>
                        {tn.showOthers?"✓":""}
                      </div>
                      <span style={{fontSize:10,fontWeight:tn.showOthers?700:500,color:tn.showOthers?"#be185d":"#64748b"}}>
                        Show Others
                      </span>
                      <span style={{fontSize:9,color:"#94a3b8",fontWeight:400}}>— group remaining items</span>
                    </div>
                  </div>

                  {/* ── Mode: Global vs Selective ── */}
                  <div>
                    <label style={{fontSize:10,fontWeight:700,color:__PT?__PT.mut:"#64748b",display:"block",marginBottom:3}}>Apply Mode</label>
                    <div style={{display:"flex",gap:4}}>
                      {[{k:"global",lb:"Global (all levels)"},{k:"selective",lb:"Selective (per feature)"}].map(md => {
                        const on = (tn.mode||"global") === md.k;
                        return (
                          <div key={md.k} onClick={() => setTN("mode",md.k)}
                            style={{padding:"3px 10px",borderRadius:20,cursor:"pointer",fontSize:10,
                                    border:"1.5px solid "+(on?"#be185d":"#e2e8f0"),background:on?"#fdf2f8":"#fff",
                                    color:on?"#be185d":"#64748b",fontWeight:on?700:400,userSelect:"none"}}>{md.lb}</div>
                        );
                      })}
                    </div>
                  </div>

                  {/* ── Selective: choose dimensions ── */}
                  {(tn.mode||"global") === "selective" && selDims.length > 0 && (() => {
                    var hasSelection = (tn.dims || []).length > 0;
                    return (
                    <div style={{
                      padding:"7px 8px",
                      borderRadius:7,
                      border:"1.5px solid "+(hasSelection ? "#f9a8d4" : "#e2e8f0"),
                      background:hasSelection ? "linear-gradient(180deg,#fdf2f8 0%,#fce7f3 100%)" : "#fafafa",
                      transition:"all .2s"
                    }}>
                      <label style={{
                        fontSize:10,fontWeight:700,
                        color:hasSelection?"#be185d":"#64748b",
                        display:"flex",alignItems:"center",gap:5,marginBottom:5,
                        transition:"color .2s"
                      }}>
                        <span style={{
                          width:6,height:6,borderRadius:"50%",
                          background:hasSelection?"#be185d":"#cbd5e1",
                          transition:"background .2s"
                        }} />
                        Apply to features
                        {hasSelection && (
                          <span style={{fontSize:9,fontWeight:700,color:"#be185d",marginLeft:"auto",
                                        background:"#fff",padding:"1px 6px",borderRadius:8,border:"1px solid #f9a8d4"}}>
                            {(tn.dims||[]).length} selected
                          </span>
                        )}
                      </label>
                      <div style={{display:"flex",flexDirection:"column",gap:3}}>
                        {selDims.map(function(dk) {
                          var on = (tn.dims||[]).indexOf(dk) >= 0;
                          var lbl = FEAT_LBL[dk]||dk;
                          /* Per-dim N: falls back to tn.n if not set for this dim. Only
                             shown when the dim is checked — clicking the row toggles
                             selection; the N input lives to the right with stopPropagation
                             so clicking inside it doesn't toggle the row off. */
                          var perN = (tn.dimN && tn.dimN[dk] != null) ? tn.dimN[dk] : "";
                          return (
                            <div key={dk} onClick={function(){togTNDim(dk);}}
                              style={{display:"flex",alignItems:"center",gap:6,padding:"3px 8px",borderRadius:5,
                                      cursor:"pointer",userSelect:"none",
                                      border:"1px solid "+(on?"#f9a8d4":"#e2e8f0"),background:on?"#fff":"#fff",
                                      transition:"all .15s"}}>
                              <div style={{width:12,height:12,borderRadius:3,
                                           border:"1.5px solid "+(on?"#be185d":"#cbd5e1"),background:on?"#be185d":"#fff",
                                           display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,color:"#fff",flexShrink:0}}>
                                {on?"✓":""}
                              </div>
                              <span style={{fontSize:10,fontWeight:on?700:400,color:on?"#be185d":"#64748b",flex:1}}>{lbl}</span>
                              {on && (
                                <span onClick={function(e){ e.stopPropagation(); }}
                                  style={{display:"flex",alignItems:"center",gap:3}}>
                                  <span style={{fontSize:8.5,color:"#94a3b8",fontWeight:600}}>Top</span>
                                  <TopNDimInput
                                    value={perN}
                                    placeholder={String(tn.n || 10)}
                                    onCommit={function(n){
                                      setSel(function(p){
                                        var cur = Object.assign({}, (p.topN && p.topN.dimN) || {});
                                        if (n === null || n === "" || isNaN(n)) {
                                          delete cur[dk]; /* clear override → fall back to default */
                                        } else {
                                          cur[dk] = Math.max(1, Math.min(1000, parseInt(n,10)));
                                        }
                                        return {...p, topN:{...(p.topN||tn), dimN:cur}};
                                      });
                                    }}
                                  />
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {!hasSelection && (
                        <div style={{fontSize:9,color:"#f87171",marginTop:5,display:"flex",alignItems:"center",gap:4}}>
                          <span style={{fontSize:11}}>↑</span> Select at least one feature
                        </div>
                      )}
                    </div>
                    );
                  })()}

                  {/* ── Always include: pin values of one feature — they stay in the
                        result even outside the Top-N cut, with their ACTUAL rank ── */}
                  {selDims.length > 0 && (() => {
                    var aDim = tn.alwaysDim && selDims.indexOf(tn.alwaysDim) >= 0 ? tn.alwaysDim : null;
                    var aVals = tn.alwaysVals || [];
                    var hasPin = !!(aDim && aVals.length > 0);
                    return (
                    <div style={{padding:"7px 8px",borderRadius:7,
                                 border:"1.5px solid "+(hasPin?"#f9a8d4":"#e2e8f0"),
                                 background:hasPin?"linear-gradient(180deg,#fdf2f8 0%,#fce7f3 100%)":"#fafafa"}}>
                      <label style={{fontSize:10,fontWeight:700,color:hasPin?"#be185d":"#64748b",
                                     display:"flex",alignItems:"center",gap:5,marginBottom:5}}>
                        <span style={{width:6,height:6,borderRadius:"50%",background:hasPin?"#be185d":"#cbd5e1"}} />
                        Always include
                        <span style={{fontSize:8.5,fontWeight:600,color:"#94a3b8"}}>— shown with actual rank</span>
                        {hasPin && (
                          <span style={{fontSize:9,fontWeight:700,color:"#be185d",marginLeft:"auto",
                                        background:"#fff",padding:"1px 6px",borderRadius:8,border:"1px solid #f9a8d4"}}>
                            {aVals.length} pinned
                          </span>
                        )}
                      </label>
                      <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:aDim?6:0}}>
                        {selDims.map(function(dk) {
                          var on = aDim === dk;
                          return (
                            <div key={dk}
                              onClick={function(){ setSel(function(p){ return {...p, topN:{...(p.topN||tn), alwaysDim:(on?null:dk), alwaysVals:[]}}; }); }}
                              style={{padding:"3px 10px",borderRadius:20,cursor:"pointer",fontSize:10,
                                      border:"1.5px solid "+(on?"#be185d":"#e2e8f0"),background:on?"#fdf2f8":"#fff",
                                      color:on?"#be185d":"#64748b",fontWeight:on?700:400,userSelect:"none"}}>
                              {FEAT_LBL[dk]||dk}
                            </div>
                          );
                        })}
                      </div>
                      {aDim && (
                        <MultiSelect label={"Pin " + (FEAT_LBL[aDim]||aDim) + " values"}
                          options={(dimValues && dimValues[aDim]) || []}
                          value={aVals}
                          onChange={function(v){ setSel(function(p){ return {...p, topN:{...(p.topN||tn), alwaysVals:v}}; }); }}
                          accent="#be185d" bg="#fdf2f8" />
                      )}
                      {aDim && !aVals.length && (
                        <div style={{fontSize:9,color:"#f87171",marginTop:4}}>
                          Pick one or more {(FEAT_LBL[aDim]||aDim)} values to pin
                        </div>
                      )}
                    </div>
                    );
                  })()}

                  <div style={{fontSize:9,padding:"4px 7px",background:"#fdf2f8",borderRadius:4,color:"#9d174d",fontWeight:600}}>
                    {(tn.mode||"global") === "global"
                      ? "Top N applied at every level of the tree"
                      : "Top N applied only to selected features"}
                    {tn.showOthers ? " · Remaining items grouped as \"Others\"" : ""}
                  </div>
                </div>
              )}
            </FilterSection>
          );
        })()}

        {/* ═══ SAVED QUERIES ═══ */}
        <FilterSection id="SavedQueries" isOpen={openSecs.SavedQueries} onToggle={togSec} color="#0d9488" title={"Saved Queries"} cnt={(savedQueries||[]).length}>
          <div style={{marginBottom:8}}>
            <div style={{display:"flex",gap:4,marginBottom:6}}>
              <input value={queryName} onChange={e => setQueryName(e.target.value)}
                placeholder="Query name…"
                onKeyDown={e => { if(e.key==="Enter" && queryName.trim()){ onSaveQuery(queryName.trim()); setQueryName(""); } }}
                style={{flex:1,padding:"5px 8px",border:"1.5px solid #e2e8f0",borderRadius:5,fontSize:10.5,
                        fontFamily:"inherit",outline:"none",color:"#0f172a",background:"#fff"}} />
              <button onClick={() => { if(queryName.trim()){ onSaveQuery(queryName.trim()); setQueryName(""); } }}
                disabled={!queryName.trim()}
                style={{padding:"4px 10px",border:"1.5px solid #0d9488",borderRadius:5,background:"#eff6ff",
                        color:"#0d9488",fontSize:10,fontWeight:700,cursor:queryName.trim()?"pointer":"not-allowed",
                        fontFamily:"inherit",opacity:queryName.trim()?1:0.5,whiteSpace:"nowrap"}}>
                💾 Save
              </button>
              {/* Refresh button — re-fetch the saved queries list. Useful to pick up
                  queries newly shared by other team members or content updates from
                  the original owner of a shared query. */}
              {onRefreshQueries && (
                <button onClick={function(){ onRefreshQueries(); }}
                  title="Refresh saved queries list"
                  style={{padding:"4px 8px",border:"1.5px solid #cbd5e1",borderRadius:5,background:"#fff",
                          color:"#475569",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",
                          whiteSpace:"nowrap"}}>
                  ↻
                </button>
              )}
            </div>
          </div>
          {/* ── Bulk-delete toolbar: Select all / Delete Selected / Delete All.
               Only shows for queries the user owns (delete is owner-gated). ── */}
          {(function(){
            /* Ownership: match by userId (authoritative) with legacy name fallback —
               matching on display name alone broke Edit/Share/Delete for owners
               whose name was edited by an admin after the query was saved. */
            var _ownsQ = function(sq){
              /* promoted admins own rows saved under either identity (portal id or AdminId) */
              if (user && sq.ownerUserId != null && (String(sq.ownerUserId) === String(user.userId) || (user.dataUserId != null && String(sq.ownerUserId) === String(user.dataUserId)))) return true;
              return !sq.owner || (user && sq.owner === user.name);
            };
            var ownedQs = (savedQueries||[]).filter(_ownsQ);
            if (!ownedQs.length || !onDeleteQueries) return null;
            var selQs = ownedQs.filter(function(sq){ return selQ[sq.id]; });
            var allSel = selQs.length>0 && selQs.length===ownedQs.length;
            return (
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6,padding:"4px 7px",background:"#f8fafc",borderRadius:6,border:"1px solid #e2e8f0"}}>
                <label style={{display:"flex",alignItems:"center",gap:4,cursor:"pointer",fontSize:9,fontWeight:700,color:"#475569"}}>
                  <input type="checkbox" checked={allSel} onChange={function(){
                      if (allSel) { setSelQ({}); }
                      else { var m={}; ownedQs.forEach(function(sq){ m[sq.id]=true; }); setSelQ(m); }
                    }} style={{cursor:"pointer"}} />
                  Select all
                </label>
                <div style={{flex:1}} />
                <button disabled={!selQs.length}
                  onClick={function(){
                    if (!selQs.length) return;
                    if (window.confirm("Delete " + selQs.length + " selected saved " + (selQs.length===1?"query":"queries") + "?\n\nThis cannot be undone.")) {
                      onDeleteQueries(selQs); setSelQ({});
                    }
                  }}
                  title="Delete the checked saved queries"
                  style={{padding:"3px 8px",border:"1px solid "+(selQs.length?"#fecaca":"#e2e8f0"),borderRadius:5,
                          background:selQs.length?"#fef2f2":"#f1f5f9",color:selQs.length?"#dc2626":"#94a3b8",
                          fontSize:9,fontWeight:800,cursor:selQs.length?"pointer":"not-allowed",fontFamily:"inherit",whiteSpace:"nowrap"}}>
                  ✕ Delete Selected{selQs.length?(" ("+selQs.length+")"):""}
                </button>
                <button
                  onClick={function(){
                    if (window.confirm("Delete ALL " + ownedQs.length + " of your saved " + (ownedQs.length===1?"query":"queries") + "?\n\nThis cannot be undone.")) {
                      onDeleteQueries(ownedQs); setSelQ({});
                    }
                  }}
                  title="Delete all of your saved queries"
                  style={{padding:"3px 8px",border:"1px solid #dc2626",borderRadius:5,background:"#dc2626",
                          color:"#fff",fontSize:9,fontWeight:800,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>
                  🗑 Delete All
                </button>
              </div>
            );
          })()}
          {(!savedQueries||!savedQueries.length) ? (
            <div style={{fontSize:10,color:"#94a3b8",textAlign:"center",padding:"8px 0",fontStyle:"italic"}}>No saved queries yet</div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:4}}>
              {savedQueries.map((sq,i) => {
                var ownsIt = (user && sq.ownerUserId != null && (String(sq.ownerUserId) === String(user.userId) || (user.dataUserId != null && String(sq.ownerUserId) === String(user.dataUserId)))) || !sq.owner || (user && sq.owner === user.name);
                var isEditing = editingQueryId === sq.id;
                var scope = sq.sharedScope; /* "team" | "users" | null/undefined */
                var scopeBadge = null;
                if (scope === "team")  scopeBadge = {label:"TEAM",  bg:"#0d9488"};
                else if (scope === "users") scopeBadge = {label:"USERS (" + ((sq.sharedUserIds||[]).length) + ")", bg:"#7c3aed"};
                else if (sq.shared)    scopeBadge = {label:"SHARED", bg:"#0d9488"}; /* legacy */
                return (
                <div key={sq.id||i} style={{display:"flex",alignItems:"center",gap:4,padding:"5px 8px",borderRadius:6,
                                            background: isEditing ? "#fef3c7" : (scopeBadge?"#eff6ff":"#f8fafc"),
                                            border:"1px solid "+ (isEditing ? "#f59e0b" : (scopeBadge?"#93c5fd":"#e2e8f0"))}}>
                  {!isEditing && ownsIt && onDeleteQueries && (
                    <input type="checkbox" checked={!!selQ[sq.id]} title="Select for bulk delete"
                      onChange={function(){ setSelQ(function(prev){ var n=Object.assign({},prev); if(n[sq.id]) delete n[sq.id]; else n[sq.id]=true; return n; }); }}
                      style={{cursor:"pointer",flexShrink:0,margin:0}} />
                  )}
                  <div style={{flex:1,minWidth:0}}>
                    {isEditing ? (
                      /* Editable name input — user can rename the query while editing its
                         filter selection. Save commits both name + selection in one call.
                         Cancel discards both (name reverts to original, selection state
                         stays in panel but isn't pushed back to the query). */
                      <div style={{display:"flex",alignItems:"center",gap:5}}>
                        <input value={editName} onChange={function(e){ setEditName(e.target.value); }}
                          placeholder="Query name…"
                          style={{flex:1,minWidth:0,padding:"3px 6px",fontSize:10.5,fontWeight:700,color:"#0f172a",
                                  background:"#fff",border:"1.5px solid #f59e0b",borderRadius:4,
                                  fontFamily:"inherit",outline:"none"}} />
                        <span style={{fontSize:7,background:"#f59e0b",color:"#fff",borderRadius:3,padding:"1px 4px",fontWeight:800,letterSpacing:".03em",flexShrink:0}}>EDITING</span>
                      </div>
                    ) : (
                      <div style={{fontSize:10.5,fontWeight:700,color:"#0f172a",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:5}}>
                        {sq.name}
                      </div>
                    )}
                    <div style={{fontSize:8,color:"#94a3b8",display:"flex",gap:4,alignItems:"center",marginTop:1,flexWrap:"wrap"}}>
                      <span>{sq.dims||0} dims · {sq.measures||0} measures</span>
                      {scopeBadge && <span style={{fontSize:7,background:scopeBadge.bg,color:"#fff",borderRadius:3,padding:"0px 4px",fontWeight:800}}>{scopeBadge.label}</span>}
                      {sq.owner && sq.owner !== (user&&user.name) && <span style={{fontSize:7,color:"#64748b"}}>by {sq.owner}</span>}
                    </div>
                  </div>

                  {/* ── Edit mode: only Save + Cancel visible. All other actions hidden to avoid
                       accidental clicks on Load/Delete/etc while user is mid-edit. ── */}
                  {isEditing && ownsIt && onUpdateLoadedQuery && (
                    <button onClick={function(){
                        /* Send the edited name (trimmed). If user cleared it, fall back to
                           original — empty names aren't valid and the server would reject. */
                        var nm = (editName || "").trim() || sq.name;
                        onUpdateLoadedQuery(Object.assign({}, sq, {name: nm}));
                      }}
                      title="Save current filter selection (and edited name) to this query"
                      style={{padding:"2px 8px",border:"1px solid #f59e0b",borderRadius:4,background:"#fef3c7",
                              color:"#92400e",fontSize:8.5,fontWeight:800,cursor:"pointer",fontFamily:"inherit",letterSpacing:".02em"}}>
                      💾 Save
                    </button>
                  )}
                  {isEditing && onCancelEdit && (
                    <button onClick={function(){ onCancelEdit(); }}
                      title="Exit edit mode without saving — your current filter state stays, name reverts"
                      style={{padding:"2px 8px",border:"1px solid #cbd5e1",borderRadius:4,background:"#fff",
                              color:"#475569",fontSize:8.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                      Cancel
                    </button>
                  )}

                  {/* ── Normal mode: Load (read-only), Edit (load + enter edit mode), Share, Delete ── */}
                  {!isEditing && (
                    <>
                      <button onClick={function(){ onLoadQuery(sq); }}
                        title="Load this query's filters into the panel (read-only — does not enter edit mode)"
                        style={{padding:"2px 6px",border:"1px solid #bfdbfe",borderRadius:4,background:"#eff6ff",
                                color:"#1d4ed8",fontSize:8.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Load</button>
                      {ownsIt && onStartEdit && (
                        <button onClick={function(){ onStartEdit(sq); }}
                          title="Load and edit this query — changes can be saved back via Save"
                          style={{padding:"2px 6px",border:"1px solid #fcd34d",borderRadius:4,background:"#fffbeb",
                                  color:"#b45309",fontSize:8.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                          ✏️ Edit
                        </button>
                      )}
                      {ownsIt && (
                        <>
                          <button onClick={function(){ onShareQuery(sq); }}
                            title="Manage sharing"
                            style={{padding:"2px 6px",border:"1px solid #bbf7d0",borderRadius:4,
                                    background:"#f0fdf4",
                                    color:"#15803d",fontSize:8.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                            Share
                          </button>
                          <button onClick={function(){
                              if (window.confirm("Delete saved query \"" + (sq.name || "(unnamed)") + "\"?\n\nThis cannot be undone.")) {
                                onDeleteQuery(sq);
                              }
                            }}
                            title="Delete this saved query"
                            style={{padding:"2px 6px",border:"1px solid #fecaca",borderRadius:4,background:"#fef2f2",
                                    color:"#dc2626",fontSize:8.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>✕</button>
                        </>
                      )}
                    </>
                  )}
                </div>
              );})}
            </div>
          )}
        </FilterSection>

      </div>

      {capHit && (
        <div style={{margin:"0 12px 6px", padding:"7px 11px", background:"#fffbeb", border:"1px solid #fcd34d", borderRadius:6, fontSize:11, color:"#92400e", fontWeight:600}}>
          {"Maximum " + MAX_FEATURES + " features per query — untick one to add another."}
        </div>
      )}
      {errors.length > 0 && (
        <div style={{margin:"0 12px 6px", padding:"7px 11px", background:"#fef2f2",
                     border:"1px solid #fecaca", borderRadius:6, fontSize:11, color:"#dc2626"}}>
          {errors.map((e, i) => <div key={i}>• {e}</div>)}
        </div>
      )}

      <div style={{padding:"8px 12px 10px", borderTop:"1px solid "+(__PT?__PT.shellBr:"#f1f5f9"), flexShrink:0}}>
        {(!sel.periodMode || !sel.periodKeys || sel.periodKeys.length === 0) && (
          <div style={{fontSize:9, color:"#dc2626", fontWeight:600, padding:"4px 6px", background:"#fef2f2", borderRadius:5, marginBottom:6, textAlign:"center"}}>⚠ Select at least one period to run query</div>
        )}
        <button onClick={onRun} disabled={loading || !sel.periodMode || !sel.periodKeys || sel.periodKeys.length === 0}
          style={{
            width:"100%", padding:"9px",
            background: (!sel.periodMode || !sel.periodKeys || sel.periodKeys.length === 0) ? (__PT?__PT.runOffBg:"#94a3b8") : (__PT?__PT.acc:"linear-gradient(135deg,#1e40af,#3b82f6)"),
            color:__PT?__PT.accTx:"#fff", border:"none", borderRadius:8, fontSize:13, fontWeight:700,
            cursor:(loading || !sel.periodMode || !sel.periodKeys || sel.periodKeys.length === 0)?"not-allowed":"pointer", fontFamily:"inherit",
            opacity:(loading || !sel.periodMode || !sel.periodKeys || sel.periodKeys.length === 0)?0.7:1, boxShadow:__PT?__PT.shadow:"0 3px 10px rgba(59,130,246,.3)",
          }}>
          {loading ? (__PT ? "Running…" : "Running…") : (__PT ? "Run query" : "▶  Run Query")}
        </button>
      </div>

      </React.Fragment>
      )}

    </aside>
  );
}
